import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Button,
  Box,
  ColumnLayout,
  Badge,
  StatusIndicator,
  Tabs,
  Flashbar,
  Table,
  Link,
  ProgressBar,
  Alert,
  Spinner,
  Modal,
  Icon,
  Popover,
  ExpandableSection
} from '@cloudscape-design/components';
import { s3Service, getCalculatorDetails, logVisit } from '../services/s3Service';
import PricingCalculatorTab from '../components/PricingCalculatorTab';
import ValidationSummaryBanner from '../components/ValidationSummaryBanner';
import PricingCalculatorWarnings from '../components/PricingCalculatorWarnings';
import LivePricingCheck from '../components/LivePricingCheck';
import ReviewerFeedbackPanel from '../components/ReviewerFeedbackPanel';
import AwsSignoffPanel from '../components/AwsSignoffPanel';
import AIArchetypePanel from '../components/AIArchetypePanel';
import BlindReviewForm, { blindDecisionLabel } from '../components/BlindReviewForm';
import IsfrReviewSummary from '../programs/isfr/ReviewSummary';
import { isfrTabs } from '../programs/isfr/tabs';
import {
  getProgramFeedbackConfig,
  getProgramManualChecks,
  getValidationsForSummaryCard,
  resolveProgram,
} from '../programs/registry';
import HistoryTab from '../components/HistoryTab';
import MarkdownRenderer from '../utils/markdownRenderer';
import { useFundRequestId } from '../utils/urlHelper';
import { formatNeedsReviewReason } from '../utils/needsReviewReason';
import { hasOutcomeGlyph } from '../utils/keyFinding';
import { describeValidationGroup } from '../utils/validationGroups';
import { buildDecisionClipboardText } from '../utils/decisionClipboard';
import { mdfClaimsTabStatus } from '../utils/mdfClaimsTabStatus';
import { projectTechnicalReviewAttachedFiles } from '../utils/technicalReviewAttachedFiles';
import { isAiArchetypeDisplayEligible } from '../utils/aiArchetypeDisplay';
import { buildMdfPerClaimBreakdown } from '../utils/mdfPerClaimBreakdown';
import { needsManualFxConversion, OEM_FX_CONVERTER_URL } from '../utils/mdfFxConversion';
import {
  getMdfPerCheck,
  countMdfChecks,
  formatMdfValidationSummary,
  formatMdfValidationBreakdown,
  getMdfRejectReason,
} from '../utils/mdfValidationSummary';
import {
  getPifccPerCheck,
  countPifccChecks,
  formatPifccValidationSummary,
  formatPifccValidationBreakdown,
} from '../utils/pifccValidationSummary';
import { parsePocResults, resolveNoCstState, pocTabIconStatus, resolvePocCstResult, resolveCstFieldChecks, resolveCstInfoFields, collectOtherCstFields } from '../utils/pocClaimsView';
import { useHistory } from 'react-router-dom';
import modPathways from '../data/modpathways.json';
import { MDF_ELIGIBLE_ACTIVITIES, MDF_INELIGIBLE_ACTIVITIES } from '../data/mdfActivities';

const STATUS_COLORS = {
  'RECOMMEND_APPROVAL': 'green',
  'RECOMMEND_REJECTION': 'red',
  'NEEDS_REVIEW': 'blue',
  // Backward compatibility
  'recommend approval': 'green',
  'recommend rejection': 'red',
  'needs review': 'blue',
  'approved': 'green',
  'conditional approval': 'blue',
  'rejected': 'red',
  'pending': 'grey',
  'processing': 'blue',
  'validating': 'blue',
  // WMP and IW decisions
  'approve': 'green',
  'reject': 'red',
  'escalate': 'blue',
  'flag for manual review': 'blue',
};

// Canonical workstream lists per phase, mirroring the MAP Partner Scope
// Checklist template (P469995696). Order is the display order; `required`
// drives the compliance flag in Section C. Names must match the canonical
// English names produced by the Python normalizer in
// ChronosSharedTools/configuration/shared/file_processing.py — the frontend
// joins backend workstream rows against this list by exact `name`.
const SCOPE_WORKSTREAMS = {
  assess: [
    { name: 'Initial Business Case', required: true },
    { name: 'Initial Discovery', required: false },
    { name: 'Strategy Analysis (7Rs)', required: false },
  ],
  mobilize: [
    { name: 'Detailed Business Case', required: false },
    { name: 'Detailed Discovery', required: true },
    { name: 'Planning & Governance', required: true },
    { name: 'Landing Zone', required: false },
    { name: 'Security & Compliance', required: false },
    { name: 'Operating Model', required: false },
    { name: 'People: skills, culture, change, and leadership', required: false },
    { name: 'Migration & Modernization Experience', required: true },
  ],
};

// Section D deliverables. Assess has just the two strategy rows; Mobilize
// adds the four governance/security/risk/documentation rows (P469995696 +
// scope_checklist.json ai_modernization block).
const AI_MODERNIZATION_ROWS = {
  assess: [
    { label: 'Model Selection Strategy', scopeField: 'model_selection_strategy_in_scope', commentField: 'model_selection_strategy_comment' },
    { label: 'Pricing Tier Selection Strategy', scopeField: 'pricing_tier_strategy_in_scope', commentField: 'pricing_tier_strategy_comment' },
  ],
  mobilize: [
    { label: 'Model Selection Justification', scopeField: 'model_selection_strategy_in_scope', commentField: 'model_selection_strategy_comment' },
    { label: 'Pricing Tier Justification', scopeField: 'pricing_tier_strategy_in_scope', commentField: 'pricing_tier_strategy_comment' },
    { label: 'AI-Ready Data Governance Framework', scopeField: 'ai_data_governance_in_scope', commentField: 'ai_data_governance_comment' },
    { label: 'AI Security & Compliance Controls', scopeField: 'ai_security_compliance_in_scope', commentField: 'ai_security_compliance_comment' },
    { label: 'AI Risk Management & Evaluation Framework', scopeField: 'ai_risk_management_in_scope', commentField: 'ai_risk_management_comment' },
    { label: 'Attached Additional Required Documentation', scopeField: 'additional_documentation_attached', commentField: null },
  ],
};

// Format Section B project cost. Backend `total_project_cost` may be a
// number, a string with a currency symbol already embedded, or null.
// currency_code is best-effort ISO; append it only when we can't tell it's
// already in the value.
const formatProjectCost = (value, currencyCode) => {
  if (value == null || value === '') return '-';
  if (typeof value === 'number') {
    const formatted = value.toLocaleString(undefined, { maximumFractionDigits: 2 });
    return currencyCode ? `${formatted} ${currencyCode}` : formatted;
  }
  const str = String(value);
  if (currencyCode && !str.toUpperCase().includes(currencyCode.toUpperCase())) {
    return `${str} ${currencyCode}`;
  }
  return str;
};

// Sum in-scope workstream effort days. Non-numeric entries are ignored.
const sumEffortDays = (workstreams) =>
  (workstreams || [])
    .filter((w) => String(w?.in_scope || '').toLowerCase() === 'yes')
    .reduce((acc, w) => acc + (Number.isFinite(Number(w?.effort_days)) ? Number(w.effort_days) : 0), 0);

// Read a phase field. The scope_checklist schema names phase_block properties
// without a prefix (total_project_cost, workstreams, ...), but
// file_processing.py persists them prefixed with the phase name
// (assess_cost, assess_workstreams, ...) when it flattens the extraction into
// detailed_validations. Try the prefixed key first, then the bare schema name,
// then any explicit aliases — so this panel renders against both the stored
// shape and the raw extractor envelope.
const readPhaseField = (phaseBlock, phaseKey, field, aliases = []) => {
  if (!phaseBlock) return undefined;
  const candidates = [`${phaseKey}_${field}`, field, ...aliases.flatMap((a) => [`${phaseKey}_${a}`, a])];
  for (const key of candidates) {
    if (phaseBlock[key] !== undefined && phaseBlock[key] !== null) return phaseBlock[key];
  }
  return undefined;
};

// Backend workstream names that don't reduce to their canonical name by
// normalization alone. Observed in prod on older templates (no
// template_version): Mobilize's "People: skills, culture, change, and
// leadership" row is extracted as "People & Culture". Keys and values are
// both passed through workstreamKey before comparison.
const WORKSTREAM_NAME_ALIASES = {
  assess: {},
  mobilize: {
    'People & Culture': 'People: skills, culture, change, and leadership',
  },
};

// Join key for workstream names. The template and the normalizer disagree on
// ampersands and spacing for some rows — the backend emits
// "Strategy & Analysis (7Rs)" where the template reads "Strategy Analysis
// (7Rs)". Normalize "&"/"and" and collapse whitespace/case so those join
// instead of rendering as an unmatched extra row. Punctuation beyond "&" is
// left alone to avoid collapsing genuinely distinct workstreams.
const workstreamKey = (name) =>
  String(name || '')
    .toLowerCase()
    .replace(/&/g, ' ')
    .replace(/\band\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

// Resolve a backend workstream name to the canonical key it should join on.
// Applied to backend rows only — the canonical list is already canonical.
const canonicalWorkstreamKey = (name, phaseKey) => {
  const normalized = workstreamKey(name);
  const aliases = WORKSTREAM_NAME_ALIASES[phaseKey] || {};
  for (const [backendName, canonicalName] of Object.entries(aliases)) {
    if (workstreamKey(backendName) === normalized) return workstreamKey(canonicalName);
  }
  // Unlisted names fall through unchanged: the row renders as a
  // non-canonical "extra" at the end of the table rather than being
  // fuzzy-matched into a canonical slot. Fuzzy matching here would risk
  // silently mapping a genuinely new workstream onto an existing row and
  // misattributing its effort days; an extra row is visibly wrong instead.
  return normalized;
};

// Format the "% of total effort" cell. The backend emits a 0-1 fraction
// ("0.5"), while the source Excel displays a percentage ("50%"). Values > 1
// are assumed to already be percentages so a future backend change to whole
// numbers doesn't render as 5000%.
const formatEffortPercentage = (value) => {
  if (value == null || value === '') return '-';
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  const pct = num <= 1 ? num * 100 : num;
  return `${Number(pct.toFixed(2))}%`;
};

// Identify cash-claim FRs using durable signals only.
//
// fundRequest.stage is captured at validation time and is NOT refreshed if
// the FR transitions in APFP after the agent runs. A cash-claim FR validated
// before transitioning to "completed" leaves the snapshot showing
// stage="completed" forever. Three durable signals are OR'd so the tab still
// renders for those records:
//
//   1. The literal "-CASH-" substring in the FR ID — embedded at FR
//      creation, never changes.
//   2. fundingType === "Cash" — set when the FR is created as a cash claim,
//      not lifecycle-dependent.
//   3. fundingDetails.claimPlans — populated whenever the FR has any
//      configured claim plans.
//
// Any single signal is sufficient. Belt-and-suspenders for sparse snapshots.
const isClaimFR = (request) => {
  const fundRequest = request?.validation_data?.complete_api_data?.fundRequest;
  const frId = fundRequest?.fundRequestId || request?.id || '';
  const fundingType = fundRequest?.fundingType || '';
  const claimPlans = fundRequest?.fundingDetails?.claimPlans || {};
  return (
    frId.includes('-CASH-') ||
    fundingType === 'Cash' ||
    Object.keys(claimPlans).length > 0
  );
};

// Programs whose approval_summary.decision holds the short INTERNAL code
// (approve/reject/flag_for_manual_review/escalate) rather than the display-ready
// recommend_* vocabulary. For these, both the Approval Summary tab icon and its
// in-tab "Final Decision" badge must read approval_summary.recommendation so the
// icon and badge agree (batpur@/AutoSDE CR-295319668). Single source of truth so
// the tab-icon site and the badge site can't drift apart. MAP/WMP/IW already
// store .decision in the recommend_* form and are intentionally excluded.
// MMP_VMP belongs here: only .recommendation is uniformly the recommend_* form
// across its stored rows. Its make_decision moved the SOP's own outcome word
// (clean/escalate/reject/not_this_program) to .rule_decision and now writes
// .decision uppercased, but rows written before that rename still hold a bare
// `reject` in .decision — a word getTabIcon does not recognize, so the icon
// would fall back to warning while the badge went red.
const _RECOMMENDATION_VOCAB_PROGRAMS = new Set(['RUBICON', 'MDF', 'POC', 'MMP_VMP', 'PIFCC']);
const _usesRecommendationVocab = (program) => _RECOMMENDATION_VOCAB_PROGRAMS.has(program);

// What the blind page shows for a kept check that has no entry in the
// detailed_validations the server sent: the check did not run, and the decision
// counts it as not_run.
const BLIND_NOT_RUN = { status: 'not_run', reason: 'This check did not run on this validation.' };

// The blind page's view contract (ChronosLambdas blind_review.VIEW_SCHEMA). The
// server decides which checks the page shows: a pending read's blind_review
// carries view_schema and shown_check_ids. The page renders exactly that list,
// and only under this schema with known ids; otherwise it shows no check tab. A
// check renders in the full page's tab for it, and specialized_workload,
// opportunity_amount and modernization_pathways inside the Partner Eligibility,
// Opportunity Details and Pricing Calculator tabs. It sends the schema with the
// label only when it rendered the list, so the label records what the reviewer
// saw. Showing a different set of checks takes a new schema in both packages.
const BLIND_VIEW_SCHEMA = 'blind-view-2';
const BLIND_CHECK_IDS = ['partner_eligibility', 'specialized_workload', 'opportunity_stage', 'opportunity_amount', 'mpe_program',
  'project_timeline', 'pricing_calculator', 'modernization_pathways', 'duplicate_funding_requests'];
// A full page tab whose answer stays withheld until the reviewer's decision: greyed,
// with this reason and no content.
const BLIND_PLACEHOLDER_REASON = 'Shown after your decision';
const blindContract = (blindReview) => (
  blindReview?.view_schema === BLIND_VIEW_SCHEMA
  && Array.isArray(blindReview.shown_check_ids)
  && blindReview.shown_check_ids.every((id) => BLIND_CHECK_IDS.includes(id))
);

// The MPE statuses that pass, as in ChronosLambdas blind_review.PASS_STATUSES.
// The blind page shows any other MPE status with its reason.
const MPE_PASS_STATUSES = ['validated', 'not_applicable'];

// The blind header's Total Funds Requested from AWS: APFP's own total, never a
// sum of the claim plans. The currency is the code on the amount, as in
// "8890315.00 INR", else totalFundingRequestedFromAwsLocalCurrency, else
// localCurrency, else none: the page never invents one. An amount that is
// absent, not a string or not a number shows '-'.
const blindRequestedAmount = (fundingDetails) => {
  const raw = fundingDetails?.totalFundingRequestedFromAwsLocalAmount;
  const [amountText, codeOnAmount] = typeof raw === 'string' ? raw.trim().split(/\s+/) : [];
  const amount = parseFloat(amountText);
  if (!Number.isFinite(amount)) return '-';
  const code = [codeOnAmount, fundingDetails.totalFundingRequestedFromAwsLocalCurrency, fundingDetails.localCurrency]
    .find((value) => typeof value === 'string' && value.trim() !== '');
  const formatted = new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(amount);
  return code ? `${code.trim().toUpperCase()} ${formatted}` : formatted;
};

const FundingRequestDetail = () => {
  // Get requestId from URL parameters instead of props
  const requestId = useFundRequestId();
  const history = useHistory();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [activeTab, setActiveTab] = useState('approval-summary');
  const [notifications, setNotifications] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [calculatorDetails, setCalculatorDetails] = useState(null);
  const [calculatorLoading, setCalculatorLoading] = useState(false);
  const [showComplianceInfoModal, setShowComplianceInfoModal] = useState(false);
  const [showValidationReportModal, setShowValidationReportModal] = useState(false);
  const [dismissedDuplicatesAlert, setDismissedDuplicatesAlert] = useState(false);
  // Blind review form state. It lives here, not in BlindReviewForm, because each
  // reload unmounts the form (see BlindReviewForm).
  const [blindDecision, setBlindDecision] = useState(null);
  const [blindSubmitting, setBlindSubmitting] = useState(false);
  // The blind page's own tab, apart from activeTab, so the page revealed after the
  // label still opens on the Approval Summary.
  const [blindActiveTab, setBlindActiveTab] = useState(null);

  const addNotification = (type, header, content) => {
    const id = Date.now().toString();
    setNotifications([{
      type,
      header,
      content,
      dismissible: true,
      dismissLabel: "Dismiss",
      onDismiss: () => setNotifications([]),
      id
    }]);
  };

  const loadRequestData = useCallback(async () => {
    // Check if requestId is valid
    if (!requestId) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setNotFound(false);
      // Fetch individual request directly from API
      console.log('Loading request data for ID:', requestId);
      const data = await s3Service.getFundingRequestById(requestId);
      console.log('Loaded request data:', data);
      setRequest(data);
      
      // Fetch calculator details for modernization pathways. Not for a blind
      // review: the blind page's Pricing Calculator tab, shown only when the
      // server lists that check, fetches its own.
      if (data?.blind_review?.required === true) {
        setCalculatorDetails(null);
      } else try {
        setCalculatorLoading(true);
        console.log('Loading calculator details for ID:', requestId);
        const calculatorData = await getCalculatorDetails(requestId);
        console.log('Loaded calculator details:', calculatorData);
        console.log('Modernization pathways in response:', calculatorData?.modernization_pathways);
        setCalculatorDetails(calculatorData);
      } catch (calcError) {
        console.log('Calculator details not available:', calcError.message);
        setCalculatorDetails(null);
      } finally {
        setCalculatorLoading(false);
      }
    } catch (error) {
      if (error.message.includes('not found') || error.message.includes('404')) {
        setNotFound(true);
      } else {
        // Show error to user
        addNotification('error', 'Failed to Load', error.message);
      }
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    loadRequestData();
    if (requestId) logVisit(requestId);
  }, [loadRequestData]);

  const handleValidate = async () => {
    try {
      setValidating(true);
      await s3Service.triggerValidation(requestId);
      
      console.log('Validation triggered, starting status polling...');
      
      // Start polling for status
      let attempts = 0;
      // 120 x 5000ms = 600s. The previous 30 attempts capped this at 150s,
      // below the p90 of a MAP validation (177s measured in prod over
      // 2026-08-13..17, n=301, p99 444s), so a healthy run reported as a
      // failure here for the same reason as the grid poll in
      // components/FundingRequestTable.js.
      const maxAttempts = 120;
      const pollInterval = 5000; // Poll every 5 seconds
      
      const checkStatus = async () => {
        try {
          const statusData = await s3Service.getReviewStatus(requestId);
          console.log('Status check response:', statusData);
          const reviewState = statusData.review_state || statusData.fundRequests?.[0]?.review_state;
          console.log(`Review state: ${reviewState}, Attempt: ${attempts + 1}/${maxAttempts}`);
          
          attempts++;
          
          if (reviewState === '3-Completed') {
            addNotification('success', 'Validation Completed', 'Validation completed successfully!');
            // Fetch immediately so validation results appear right away
            loadRequestData();
            // Second fetch after delay to pick up agent_narrative, which is written
            // by invoke() after the agent finishes generating the markdown summary
            setTimeout(() => loadRequestData(), 15000);
            setValidating(false);
            return;
          } else if (reviewState === '4-Failed') {
            addNotification('error', 'Validation Failed', 'Validation failed. Please check the details and try again.');
            loadRequestData();
            setValidating(false);
            return;
          } else if (attempts >= maxAttempts) {
            addNotification('warning', 'Status Check Timeout', 'Validation is taking longer than expected. Please refresh manually to see results.');
            setValidating(false);
            return;
          } else {
            // Still in progress, continue polling
            setTimeout(checkStatus, pollInterval);
          }
        } catch (error) {
          console.error('Error checking status:', error);
          if (attempts >= maxAttempts) {
            addNotification('error', 'Status Check Failed', 'Could not verify validation status. Please refresh manually.');
            setValidating(false);
          } else {
            setTimeout(checkStatus, pollInterval);
          }
        }
      };
      
      // Start checking status after a brief delay
      setTimeout(checkStatus, pollInterval);
      
    } catch (error) {
      addNotification('error', 'Validation Failed', error.message);
      setValidating(false);
    }
  };

  const handleBlindSubmit = async () => {
    try {
      setBlindSubmitting(true);
      await s3Service.submitBlindReview(requestId, {
        decision: blindDecision,
        processedDate: request.blind_review.processed_date,
        // Only when the page rendered the server's list (see blindContract).
        viewSchema: blindContract(request.blind_review) ? BLIND_VIEW_SCHEMA : undefined,
      });
      setBlindDecision(null);
      addNotification('success', 'Blind decision recorded', '');
      loadRequestData();
    } catch (error) {
      if (error.status === 409) {
        // No longer pending (already labelled, or blind review is off), or a
        // re-validation moved processed_date. Reload to show what the server
        // holds now; the form keeps what was entered.
        addNotification('info', 'Blind decision not recorded', 'This fund request changed since the page loaded, so it has been reloaded.');
        loadRequestData();
      } else {
        addNotification('error', 'Failed to submit blind decision', error.message);
      }
    } finally {
      setBlindSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const parseAmount = (value) => {
    if (!value) return 0;
    if (typeof value === 'number') return value;
    // Handle "150000.00 USD" format from API
    if (typeof value === 'string') {
      return parseFloat(value.replace(/[^0-9.-]+/g, '')) || 0;
    }
    return 0;
  };

  // Get environment - supports both build-time and runtime config
  const getEnvironment = () => {
    // Try runtime config first (for pipelines)
    if (window._env_?.ENVIRONMENT) {
      return window._env_.ENVIRONMENT;
    }
    
    // Try runtime config (for Pipelines deployment)
    if (window.APP_CONFIG?.ENVIRONMENT) {
      return window.APP_CONFIG.ENVIRONMENT;
    }
    
    // Fallback: detect from URL
    const hostname = window.location.hostname;
    if (hostname.includes('alpha')) return 'alpha';
    if (hostname.includes('gamma')) return 'gamma';
    if (hostname === 'chronos.aws.dev') return 'prod';
    
    return 'unknown';
  };

  const getFundingRequestUrl = (fundRequestId) => {
    const env = getEnvironment();
    const envPrefix = env === 'prod' ? '' : `${env}.`;
    return `https://${envPrefix}funding.apn.a2z.com/aws-approvals/${fundRequestId}/review`;
  };

  const getAceOpportunityUrl = (opportunityId) => {
    // ACE Opportunity ID uses APN Portal Salesforce (production)
    return `https://apn-portal.lightning.force.com/lightning/r/Opportunity/${opportunityId}/view`;
  };

  const getAwsOpportunityUrl = (opportunityId) => {
    // AWS Opportunity ID uses AWS CRM Salesforce
    return `https://aws-crm.lightning.force.com/lightning/r/Opportunity/${opportunityId}/view`;
  };

  if (loading) {
    return (
      <Box textAlign="center" padding="xxl">
        <StatusIndicator type="loading">Loading request details...</StatusIndicator>
      </Box>
    );
  }

  if (notFound) {
    return (
      <Container>
        <Box textAlign="center" padding="xxl">
          <Box variant="h2">Fund Request Not Found</Box>
          <Box variant="p" padding={{ bottom: 'm' }}>
            The fund request ID "{requestId}" could not be found.
          </Box>
          <SpaceBetween direction="horizontal" size="s">
            <Button onClick={() => history.push('/dashboard')}>
              Back to Dashboard
            </Button>

          </SpaceBetween>
        </Box>
      </Container>
    );
  }

  if (!request) {
    return (
      <Container>
        <Box textAlign="center" padding="xxl">
          <Box variant="h2">No Data Available</Box>
          <Box variant="p" padding={{ bottom: 'm' }}>
            Unable to load fund request data.
          </Box>
          <Button onClick={() => history.push('/dashboard')}>
            Back to Dashboard
          </Button>
        </Box>
      </Container>
    );
  }

  // Blind review (MAP): the server withholds this validation's answer until a
  // reviewer records a decision of their own. The blind page is returned below the
  // render helpers it reuses (see `if (blindPending)`).
  const blindPending = request.blind_review?.required === true;
  // The checks the server lists for the blind page. Without the contract, none.
  const blindShownIds = blindPending && blindContract(request.blind_review) ? request.blind_review.shown_check_ids : [];
  const blindListed = (id) => blindShownIds.includes(id);
  // On the blind page, a kept check missing from the detailed_validations the
  // server sent reads as not run. With none sent, renderers show the full page's defaults.
  const blindNotRun = blindPending && request.validation_data?.detailed_validations ? BLIND_NOT_RUN : {};

  // Effective *display* program for this request. Prefers the shared-
  // prefix challenger hint (routing_program_hint) over the authz-derived
  // incumbent (request.program). See registry.resolveProgram for the
  // precedence and rationale — batpur review on CR-289789467.
  //
  // Declared here (immediately after the request null-guard) rather than
  // adjacent to PROGRAM_TAB_ALLOWLIST because render helpers below close
  // over effectiveProgram and some are invoked eagerly to build the tabs
  // array — a later declaration triggers a TDZ ReferenceError at render.
  const effectiveProgram = resolveProgram(request);

  // Which programs read the CURATED APFP envelope rather than MAP's raw one.
  // Both render through renderRubiconOpportunityDetails/ProjectDetails, because
  // their agents project the same field shape: Rubicon via
  // curate_rubicon_apfp_for_ui and MMP/VMP via curate_mmp_vmp_apfp_for_ui
  // (ChronosAgentCoreAgents data_parser.py in each package). MAP's own render
  // functions read fundRequest.opportunities[key].record.* and
  // fundRequest.approvalDetails.*, which neither agent writes — pointing either
  // program at those is a grid of "-" and MAP's defaults, which is what kept
  // both tabs off the allowlist before the curation existed.
  //
  // A named list rather than a repeated ternary: this decides the renderer AND
  // the label in two places each, and the way that goes wrong is one of the
  // four disagreeing with the other three.
  const readsCuratedApfp = effectiveProgram === 'RUBICON' || effectiveProgram === 'MMP_VMP';

  const renderOpportunityDetails = () => {
    // Debug: Log the actual request data structure
    console.log('renderOpportunityDetails - Full request object:', request);
    console.log('renderOpportunityDetails - validation_data:', request.validation_data);
    console.log('renderOpportunityDetails - complete_api_data:', request.validation_data?.complete_api_data);
    console.log('renderOpportunityDetails - detailed_validations:', request.validation_data?.detailed_validations);
    
    // Use the ACTUAL DynamoDB structure - data is in fundRequest and opportunity_stage validation
    const fundRequest = request.validation_data?.complete_api_data?.fundRequest || {};
    const project = fundRequest.project || {};
    const partner = fundRequest.partner || {};
    const validationData = request.validation_data?.detailed_validations?.opportunity_stage || blindNotRun;
    
    // Get opportunity data from opportunities
    const opportunities = fundRequest.opportunities || {};
    const firstOppKey = Object.keys(opportunities)[0];
    const firstOpp = firstOppKey ? opportunities[firstOppKey] : {};
    const oppRecord = firstOpp.record || {};
    
    console.log('renderOpportunityDetails - fundRequest:', fundRequest);
    console.log('renderOpportunityDetails - project:', project);
    console.log('renderOpportunityDetails - oppRecord:', oppRecord);
    console.log('renderOpportunityDetails - validationData:', validationData);
    
    const getValidationStatusType = (status) => {
      if (status === 'validated' || status === 'eligible' || status === 'pass') return 'success';
      if (status === 'not_eligible' || status === 'not_enrolled' || status === 'fail') return 'error';
      return 'warning';
    };

    const validationStatusType = getValidationStatusType(validationData.status);

    // A number an approver cannot source is not verifiable, which is the point
    // of showing these at all. One row for all four values, because the stages
    // and both amounts come from the same SUDS snapshot.
    //
    // `absent` explains a missing amount off the SAME signal as the row, so the
    // two can never contradict each other. It only suggests re-validating where
    // that could actually change the outcome -- a transient SUDS outage, or a
    // record predating amount capture. Telling an approver to re-validate an
    // opportunity Salesforce has no amount for sends them to do futile work.
    const STAGE_AMOUNT_SOURCE = {
      suds_success: {
        type: 'success',
        text: 'Live from Salesforce',
        absent: 'Salesforce holds no amount for this opportunity.',
      },
      suds_error_fallback_apfp: {
        type: 'warning',
        text: 'APFP cached — SUDS unavailable',
        absent: 'Salesforce was unavailable on this run and APFP does not carry an opportunity amount — re-validate to populate.',
      },
      apfp_legacy: {
        type: 'warning',
        text: 'APFP cached — SUDS not enabled',
        absent: 'Live Salesforce reads are not enabled for this program, and APFP does not carry an opportunity amount.',
      },
      suds_no_record: {
        type: 'warning',
        text: 'Not found in Salesforce',
        absent: 'This opportunity was not found in Salesforce, so there is no amount to show.',
      },
      no_opp_id: {
        type: 'warning',
        text: 'No AWS opportunity ID',
        absent: 'This fund request carries no AWS opportunity ID, so no amount could be looked up.',
      },
    };
    // Records validated before amount capture carry a data_source -- it is written
    // before the MAP program branch -- but neither amount key. They must not
    // borrow the source-specific copy above: on a suds_success record that would
    // assert Salesforce holds no amount for an opportunity where it holds one.
    // Key-absent is a Chronos capture gap, and re-validating is what fixes it;
    // value-null is a fact about Salesforce, which re-validating cannot change.
    const AMOUNT_NOT_CAPTURED =
      'This validation run did not capture opportunity amounts — re-validate to populate.';
    const stageAmountSource = STAGE_AMOUNT_SOURCE[validationData.data_source] || {
      type: 'info',
      text: 'Not captured by this validation run',
      absent: AMOUNT_NOT_CAPTURED,
    };

    // Takes the key, not the value, so it can tell an absent key from a null one.
    // Explicit null check rather than truthiness: a genuine $0 opportunity against
    // a non-zero APFP MRR is a mismatch the approver must see, and '-' would hide
    // it. Presence is judged per field, since the two amounts are independent.
    const renderOpportunityAmount = (key) => {
      const value = validationData[key];
      if (value != null) return formatCurrency(parseAmount(value));
      return (
        <Popover
          dismissButton={false}
          position="top"
          size="small"
          content={key in validationData ? stageAmountSource.absent : AMOUNT_NOT_CAPTURED}
        >
          -
        </Popover>
      );
    };

    return (
      <SpaceBetween size="l">
        {/* Validation Decision */}
        <Container header={<Header variant="h2">Opportunity Details Summary</Header>}>
          <ValidationSummaryBanner 
            label="Opportunity Stage" 
            validationData={validationData} 
          />
          {/* The blind page only: MAP Lite's amount check, beside the amounts it
              compares. The full page has no view of it. */}
          {blindListed('opportunity_amount') && (
            <ValidationSummaryBanner
              label="Opportunity Amount"
              validationData={request.validation_data?.detailed_validations?.opportunity_amount}
            />
          )}
        </Container>

        <Container header={<Header variant="h2">Opportunity Information</Header>}>
          <ColumnLayout columns={3} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Opportunity ID</Box>
              <Link external href={getAceOpportunityUrl(oppRecord.aceOpportunityId || validationData.opportunity_id)}>
                {oppRecord.userFriendlyId || oppRecord.aceOpportunityId || validationData.opportunity_id || '-'}
              </Link>
            </div>
            <div>
              <Box variant="awsui-key-label">AWS opportunity ID</Box>
              <Link external href={getAwsOpportunityUrl(oppRecord.awsOpportunityId || validationData.aws_opportunity_id)}>
                {oppRecord.awsOpportunityId || validationData.aws_opportunity_id || '-'}
              </Link>
            </div>
            <div>
              <Box variant="awsui-key-label">ACE Opportunity Stage</Box>
              {/* Prefer the live SUDS value the decision was made on over the
                  APFP snapshot, which can be stale by the time an approver
                  looks. Falls through on '' (the resolver's empty default). */}
              <div>{validationData.ace_validation_stage || oppRecord.aceValidationStage || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">AWS Opportunity Stage</Box>
              {/* oppRecord.stage is absent on most APFP payloads, which rendered
                  '-' even on a successful SUDS read. */}
              <div>{validationData.aws_opportunity_stage || oppRecord.stage || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Partner Development Manager</Box>
              <div>{oppRecord.partnerDevelopmentManager || 'Not Assigned'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Opportunity Owner</Box>
              <div>{oppRecord.ownerName || 'Not Assigned'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">AWS Sales Rep</Box>
              <div>{oppRecord.awsSalesRepName || 'Not Assigned'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Partner Success Manager</Box>
              <div>{oppRecord.partnerSuccessManager || 'Not Assigned'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Creation Date</Box>
              <div>{fundRequest.approvalDetails?.submissionDate ? new Date(fundRequest.approvalDetails.submissionDate).toLocaleDateString() : '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Target Close Date</Box>
              <div>{oppRecord.targetCloseDate ? new Date(oppRecord.targetCloseDate).toLocaleDateString() : '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">System Close Date</Box>
              <div>{validationData.closed_stage_date ? new Date(validationData.closed_stage_date).toLocaleDateString(undefined, { timeZone: 'UTC' }) : '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Launch Window</Box>
              <div>{(() => {
                const d = validationData.delta_days;
                if (typeof d !== 'number') return '-';
                const rel = d < 0 ? 'after' : 'before';
                const win = d > 90 ? 'exceeds 90-day window' : 'within 90-day window';
                return `Launched ${Math.abs(d)} day(s) ${rel} submission — ${win}`;
              })()}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">AWS Total Opportunity</Box>
              <div>{renderOpportunityAmount('aws_total_opportunity')}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">ACE Opportunity Amount</Box>
              <div>{renderOpportunityAmount('ace_opportunity_amount')}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Stage &amp; Amount Source</Box>
              <div><StatusIndicator type={stageAmountSource.type}>{stageAmountSource.text}</StatusIndicator></div>
            </div>
            <div>
              <Box variant="awsui-key-label">Industry Vertical</Box>
              <div>{oppRecord.industryVertical || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Customer Type</Box>
              <div>{oppRecord.customerType || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Business Unit</Box>
              <div>{oppRecord.customerBizUnit || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">WWPS Partner Development Manager</Box>
              <div>{oppRecord.wwpsPartnerDevelopmentManager || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Territory</Box>
              <div>{oppRecord.territory || project.location?.country || '-'}</div>
            </div>
          </ColumnLayout>
        </Container>
      </SpaceBetween>
    );
  };

  // Rubicon-specific Opportunity Details: reads the curated
  // fundRequest.opportunity.opportunityDetail.* shape produced by
  // curate_rubicon_apfp_for_ui() (ChronosAgentCoreAgents data_parser.py),
  // NOT MAP's fundRequest.opportunities[key].record.* shape used above.
  // PII (opportunity owner name, AWS sales rep, PDM, PSM) is excluded by
  // that curator's allowlist, so those fields are intentionally absent
  // here — this is not feature parity with MAP, just the same tab shape.
  const renderRubiconOpportunityDetails = () => {
    const fundRequest = request.validation_data?.complete_api_data?.fundRequest || {};
    const opportunityDetail = fundRequest.opportunity?.opportunityDetail || {};
    // MMP/VMP's agent writes what SUDS returned for the opportunity
    // (approval_summary.opportunity_live), and only on a live read. The rules judged
    // that stage, and APFP leaves `stage` empty on a third of these requests, so the
    // live value goes first. Rubicon writes no such block and renders as before.
    const isMmpVmp = effectiveProgram === 'MMP_VMP';
    const live = isMmpVmp ? request.approval_summary?.opportunity_live || null : null;
    const salesforceStatus = (l) => {
      if (l.is_closed == null) return '-';
      if (!l.is_closed) return 'Open';
      return l.is_won ? 'Closed (won)' : 'Closed (lost)';
    };

    return (
      <SpaceBetween size="l">
        <Container header={<Header variant="h2">Opportunity Information</Header>}>
          <ColumnLayout columns={3} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Opportunity ID</Box>
              {/* Label is the user-facing opportunityId (O...); the link
                  target is the Salesforce record ID aceOpportunityId
                  (006...), which is what /lightning/r/Opportunity/{id}/view
                  resolves. Render plain text when the Salesforce ID is
                  unavailable rather than a link that cannot resolve. */}
              {opportunityDetail.opportunityId
                ? (opportunityDetail.aceOpportunityId
                  ? (
                    <Link external href={getAceOpportunityUrl(opportunityDetail.aceOpportunityId)}>
                      {opportunityDetail.opportunityId}
                    </Link>
                  )
                  : <div>{opportunityDetail.opportunityId}</div>)
                : <div>-</div>}
            </div>
            <div>
              <Box variant="awsui-key-label">AWS Opportunity ID</Box>
              {opportunityDetail.awsOpportunityId
                ? (
                  <Link external href={getAwsOpportunityUrl(opportunityDetail.awsOpportunityId)}>
                    {opportunityDetail.awsOpportunityId}
                  </Link>
                )
                : <div>-</div>}
            </div>
            <div>
              <Box variant="awsui-key-label">ACE Validation Stage</Box>
              <div>{live?.ace_validation_stage || opportunityDetail.aceValidationStage || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">{isMmpVmp ? 'AWS Opportunity Stage' : 'Stage'}</Box>
              <div>{live?.aws_opportunity_stage || opportunityDetail.stage || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Status</Box>
              <div>{opportunityDetail.status || '-'}</div>
            </div>
            {isMmpVmp && (
              <div>
                <Box variant="awsui-key-label">Stage Source</Box>
                <div>{live ? 'Live Salesforce' : 'APFP snapshot'}</div>
              </div>
            )}
            {/* No AWS Total Opportunity: no rule judges it, and beside the request's ARR
                it reads as a contradiction. */}
            {live && (
              <div>
                <Box variant="awsui-key-label">Salesforce Status</Box>
                <div>{salesforceStatus(live)}</div>
              </div>
            )}
            {/* Not on MMP/VMP: APFP keeps the close date from submission, SUDS does
                not return the current one, and a stale date misleads more than none. */}
            {!isMmpVmp && (
              <div>
                <Box variant="awsui-key-label">Target Close Date</Box>
                <div>{opportunityDetail.targetCloseDate ? new Date(opportunityDetail.targetCloseDate).toLocaleDateString(undefined, { timeZone: 'UTC' }) : '-'}</div>
              </div>
            )}
            <div>
              <Box variant="awsui-key-label">Opportunity Ownership</Box>
              <div>{opportunityDetail.opportunityOwnership || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Customer Business Unit</Box>
              <div>{opportunityDetail.customerBizUnit || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Industry Vertical</Box>
              <div>{opportunityDetail.industryVertical || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Territory</Box>
              <div>{opportunityDetail.territory || '-'}</div>
            </div>
          </ColumnLayout>
        </Container>
      </SpaceBetween>
    );
  };

  const renderMPEDetails = () => {
    const fundRequest = request.validation_data?.complete_api_data?.fundRequest || {};
    const mpeValidation = request.validation_data?.detailed_validations?.mpe_program || blindNotRun;
    
    // Get migration engagement from opportunities
    const opportunities = fundRequest.opportunities || {};
    const firstOppKey = Object.keys(opportunities)[0];
    const firstOpp = firstOppKey ? opportunities[firstOppKey] : {};
    const migrationEngagements = firstOpp.migrationEngagements || [];
    const primaryEngagement = migrationEngagements.find(eng => eng.engagementId === firstOpp.primaryMigrationEngagementId) || migrationEngagements[0] || {};
    
    const getValidationStatusType = (status) => {
      if (status === 'validated' || status === 'qualified' || status === 'qualified_plus' || status === 'pass') return 'success';
      if (status === 'not_qualified' || status === 'fail') return 'error';
      return 'warning';
    };

    // On the blind page, every MPE status that does not pass shows its status and
    // reason: a failure such as multiple_mpe_ids, a review status, or Not run. A
    // result without validation_checks could fill the engagement rows only with
    // the full page's defaults (UNKNOWN, REQUIRES REVIEW), so the blind page drops
    // them. The full page keeps its rows, and its banner only for not_run, which
    // the MPE tool never writes.
    const mpeBanner = blindPending
      ? typeof mpeValidation.status === 'string' && mpeValidation.status !== '' && !MPE_PASS_STATUSES.includes(mpeValidation.status)
      : mpeValidation.status === 'not_run';
    const mpeRowsShown = !(blindPending && mpeBanner && !mpeValidation.validation_checks);

    return (
      <SpaceBetween size="l">
        {/* Validation Decision */}
        <Container header={<Header variant="h2">MPE Details Summary</Header>}>
          <SpaceBetween size="s">
            {mpeBanner && <ValidationSummaryBanner label="MPE Program" validationData={mpeValidation} />}
            {mpeValidation.status === 'requires_mpsa_review' && (
              <Alert type="warning" header="MPSA review required">
                MPE ARR ${mpeValidation.validation_checks?.mpe_arr?.toLocaleString('en-US', { maximumFractionDigits: 0 })} exceeds $3M threshold. Assign to MPSA for review.
              </Alert>
            )}
            {/* Engagement Status Validation */}
            {mpeRowsShown && (
            <Box padding={{ vertical: 'xs' }}>
              <Box variant="awsui-key-label">Engagement Status</Box>
              <Box fontSize="heading-s" fontWeight="bold">
                <StatusIndicator type={
                  mpeValidation.validation_checks?.engagement_status_valid ? 'success' :
                  mpeValidation.status === 'not_applicable' ? 'stopped' :
                  mpeValidation.status === 'not_qualified' ? 'error' : 'warning'
                }>
                  {mpeValidation.validation_checks?.engagement_status_valid
                    ? `QUALIFIED — ${mpeValidation.validation_checks?.engagement_status || 'QUALIFIED_PLUS'}`
                    : mpeValidation.status === 'not_applicable'
                      ? 'NOT APPLICABLE'
                      : `${(mpeValidation.validation_checks?.engagement_status || 'UNKNOWN').toUpperCase()} — requires PMP Lead validation`
                  }
                </StatusIndicator>
              </Box>
            </Box>
            )}
            {/* ARR Comparison */}
            {mpeValidation.validation_checks?.mpe_arr > 0 && (
              <Box padding={{ vertical: 'xs' }}>
                <Box variant="awsui-key-label">ARR Comparison</Box>
                <Box fontSize="heading-s" fontWeight="bold">
                  <StatusIndicator type={mpeValidation.validation_checks?.arr_valid ? 'success' : 'error'}>
                    {mpeValidation.validation_checks?.arr_valid
                      ? `PASSED — Fund Request $${mpeValidation.validation_checks?.fund_request_arr?.toLocaleString()} vs MPE $${mpeValidation.validation_checks?.mpe_arr?.toLocaleString()} (${mpeValidation.validation_checks?.arr_variance_percent >= 0 ? '+' : ''}${mpeValidation.validation_checks?.arr_variance_percent?.toFixed(2)}%)`
                      : `FAILED — ${mpeValidation.validation_checks?.arr_reason || 'ARR mismatch'}`
                    }
                  </StatusIndicator>
                </Box>
              </Box>
            )}
          </SpaceBetween>
        </Container>

        <Container header={<Header variant="h2">MPE Information</Header>}>
          <ColumnLayout columns={2} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Program Engagement ID</Box>
              <div>{primaryEngagement.engagementId || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">MPE Record Status</Box>
              <div>{primaryEngagement.awsinvRequestStatus || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">MPE Engagement Status</Box>
              <div>{primaryEngagement.status || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Agreement Category</Box>
              <div>{primaryEngagement.migrationEngagementAgreementCategory || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Estimated Migration ARR</Box>
              <div>{(() => {
                if (primaryEngagement.estimatedMigrationArrUsdAmount) {
                  const numericValue = parseFloat(primaryEngagement.estimatedMigrationArrUsdAmount.replace(/[^0-9.-]+/g, ''));
                  return formatCurrency(numericValue);
                }
                return '-';
              })()}</div>
            </div>
          </ColumnLayout>
        </Container>

        {mpeRowsShown && (
        <Container header={<Header variant="h2">MPE Validation</Header>}>
          <SpaceBetween size="m">
            {/* ARR Comparison — only show when MPE ARR is available */}
            {mpeValidation.validation_checks?.mpe_arr > 0 && (
            <div>
              <Box variant="awsui-key-label">ARR Comparison</Box>
              <ColumnLayout columns={2} variant="text-grid">
                <div>
                  <Box variant="awsui-key-label">Validation Status</Box>
                  <StatusIndicator type={mpeValidation.validation_checks?.arr_valid ? 'success' : 'error'}>
                    {mpeValidation.validation_checks?.arr_valid ? 'PASSED' : 'FAILED'}
                  </StatusIndicator>
                </div>
                <div>
                  <Box variant="awsui-key-label">Fund Request ARR</Box>
                  <div>{mpeValidation.validation_checks?.fund_request_arr ? formatCurrency(mpeValidation.validation_checks.fund_request_arr) : '-'}</div>
                </div>
                <div>
                  <Box variant="awsui-key-label">Estimated Migration ARR</Box>
                  <div>{mpeValidation.validation_checks?.mpe_arr ? formatCurrency(mpeValidation.validation_checks.mpe_arr) : '-'}</div>
                </div>
                <div>
                  <Box variant="awsui-key-label">Variance</Box>
                  <div>{mpeValidation.validation_checks?.arr_variance_percent != null
                    ? `${mpeValidation.validation_checks.arr_variance_percent >= 0 ? '+' : ''}${mpeValidation.validation_checks.arr_variance_percent.toFixed(2)}%`
                    : '-'}</div>
                </div>
              </ColumnLayout>
            </div>
            )}
            {/* Engagement Status Validation */}
            <div>
              <Box variant="awsui-key-label">Engagement Status Validation</Box>
              <ColumnLayout columns={2} variant="text-grid">
                <div>
                  <Box variant="awsui-key-label">Validation Status</Box>
                  <StatusIndicator type={
                    mpeValidation.validation_checks?.engagement_status_valid ? 'success' :
                    mpeValidation.status === 'not_applicable' ? 'stopped' :
                    mpeValidation.status === 'not_qualified' ? 'error' : 'warning'
                  }>
                    {mpeValidation.validation_checks?.engagement_status_valid
                      ? 'PASSED'
                      : mpeValidation.status === 'not_applicable' ? 'NOT APPLICABLE'
                      : mpeValidation.status === 'not_qualified' ? 'FAILED' : 'REQUIRES REVIEW'}
                  </StatusIndicator>
                </div>
                <div>
                  <Box variant="awsui-key-label">MPE Engagement Status</Box>
                  <div>{mpeValidation.validation_checks?.engagement_status || '-'}</div>
                </div>
              </ColumnLayout>
            </div>
          </SpaceBetween>
        </Container>
        )}
      </SpaceBetween>
    );
  };





  const renderPartnerEligibility = () => {
    // Debug: Log the actual partner data structure
    console.log('renderPartnerEligibility - partner_eligibility validation:', request.validation_data?.detailed_validations?.partner_eligibility);
    console.log('renderPartnerEligibility - fundRequest partner:', request.validation_data?.complete_api_data?.fundRequest?.partner);
    
    // Use the ACTUAL DynamoDB structure
    const fundRequest = request.validation_data?.complete_api_data?.fundRequest || {};
    const partner = fundRequest.partner || {};
    const owner = fundRequest.owner || {};
    const validationData = request.validation_data?.detailed_validations?.partner_eligibility || {};
    const applications = validationData.applications || [];
    
    const getValidationStatusType = (status) => {
      if (status === 'eligible' || status === 'pass') return 'success';
      if (status === 'not_eligible' || status === 'fail') return 'error';
      return 'warning';
    };

    const validationStatusType = getValidationStatusType(validationData.status);
    // The blind page shows the calculator-derived specialized workload check, its
    // banner, Covered ARR % and the designations' order, only when the server lists it.
    const specializedWithheld = blindPending && !blindListed('specialized_workload');

    return (
      <SpaceBetween size="l">
        {/* Partner Eligibility Summary - Custom Display */}
        <Container header={<Header variant="h2">Partner Eligibility Summary</Header>}>
          <SpaceBetween size="s">
            {/* Partner Eligibility */}
            <Box padding={{ vertical: 'xs' }}>
              <Box fontSize="heading-s" fontWeight="bold">
                <StatusIndicator type={validationStatusType}>
                  {validationData.status?.toUpperCase() || 'UNKNOWN'}
                </StatusIndicator>
              </Box>
              {(() => {
                // Show eligibility factors if they exist
                if (validationData.eligibility_factors && validationData.eligibility_factors.length > 0) {
                  return validationData.eligibility_factors.map((factor, index) => {
                    // Fix competency name - replace MCP with MMC
                    let correctedFactor = factor.replace(/^MCP:/g, 'MMC:');
                    correctedFactor = correctedFactor.replace('Migration Competency Program', 'Migration and Modernization Services Competency');
                    return (
                      <Box key={index} fontSize="body-m" color="text-body-secondary" padding={{ top: 'xxs' }}>
                        {correctedFactor}
                      </Box>
                    );
                  });
                }
                // Fallback to reason if no eligibility factors
                return validationData.reason && (
                  <Box fontSize="body-m" color="text-body-secondary" padding={{ top: 'xxs' }}>
                    {validationData.reason}
                  </Box>
                );
              })()}
            </Box>
            
            {/* Specialized Workload - only show if it adds new information */}
            {(() => {
              const specializedData = request.validation_data?.detailed_validations?.specialized_workload;
              const alreadyEligible = validationData.status === 'eligible' || validationData.status === 'pass';
              // If partner is already eligible (MMC/MCR), skip — redundant
              if (specializedWithheld || alreadyEligible || !specializedData) return null;
              // If partner is not eligible and specialized workload also failed with same verdict, skip — duplicate
              const specResult = specializedData.result || '';
              const partnerResult = validationData.result || '';
              if (specResult === 'fail' && partnerResult === 'fail') return null;
              return (
                <ValidationSummaryBanner 
                  label="Specialized Workload" 
                  validationData={specializedData} 
                />
              );
            })()}
          </SpaceBetween>
        </Container>

        {/* Confirmed Applications */}
        {(() => {
          const confirmedApplications = applications.filter(app => app.status === 'Confirmed');
          
          // Sort applications: Competency-typed rows first (auto-eligible),
          // then AI Competency (75% union), then SDPs by Covered ARR %
          // descending, then unmatched. Rank by designation_type so the
          // list groups by eligibility path — reviewers see qualifying
          // Competencies at the top.
          // A withheld specialized workload check keeps the APFP order: the rank
          // and the percentages come from it.
          const sortedApplications = specializedWithheld ? confirmedApplications : [...confirmedApplications].sort((a, b) => {
            const specializedWorkloadData = request.validation_data?.detailed_validations?.specialized_workload;
            const specializedWorkloads = specializedWorkloadData?.specialized_workloads || [];

            const aDesignation = a.designationName || a.designation;
            const bDesignation = b.designationName || b.designation;

            // Match against label OR the row's designations[] array.
            // Backend rev 3+ splits multi-type rows so a single designation
            // may live inside a row with a joined label.
            const findWorkload = (des) => specializedWorkloads.find(w =>
              w['Competency or SDP'] === des ||
              (Array.isArray(w.designations) && w.designations.includes(des))
            );
            const aWorkload = findWorkload(aDesignation);
            const bWorkload = findWorkload(bDesignation);

            const rank = (w) => {
              if (!w) return 3;
              const t = w.designation_type || '';
              if (t === 'competency') return 0;
              if (t === 'ai_competency') return 1;
              return 2;
            };
            const rankDiff = rank(aWorkload) - rank(bWorkload);
            if (rankDiff !== 0) return rankDiff;

            const pct = (w) => {
              if (!w) return 0;
              return w.percentage ??
                (w.Percentage ? parseFloat(String(w.Percentage).replace('%', '')) : 0);
            };
            return pct(bWorkload) - pct(aWorkload);
          });
          
          return sortedApplications.length > 0 && (
            <Container header={<Header variant="h2" counter={`(${sortedApplications.length})`}>Existing Designations</Header>}>
              <Table
                columnDefinitions={[
                  {
                    id: 'name',
                    header: 'Application ID',
                    cell: item => item.name || '-'
                  },
                  {
                    id: 'status',
                    header: 'Status',
                    cell: item => (
                      <Badge color="green">
                        {item.status || 'Confirmed'}
                      </Badge>
                    )
                  },
                  {
                    id: 'designation',
                    header: 'Designation',
                    cell: item => {
                      return item.designationName || item.designation || '-';
                    }
                  },
                  {
                    id: 'recordType',
                    header: 'Record Type',
                    cell: item => {
                      const recordType = item.recordTypeLabel || item.recordType || 'Service Delivery';
                      return (
                        <Badge color={recordType === 'Competency' ? 'blue' : 'green'}>
                          {recordType}
                        </Badge>
                      );
                    }
                  },
                  {
                    id: 'arrPercentage',
                    header: 'Covered ARR %',
                    cell: item => {
                      const specializedWorkloadData = request.validation_data?.detailed_validations?.specialized_workload;
                      const specializedWorkloads = specializedWorkloadData?.specialized_workloads || [];
                      const unmatchedDesignations = specializedWorkloadData?.unmatched_designations || [];

                      const designation = item.designationName || item.designation;

                      // Match against label OR the row's designations[] array.
                      // Backend rev 3+ splits multi-type rows so a single
                      // designation may live inside a Competency row that
                      // carries several designations under one label.
                      const workload = specializedWorkloads.find(w => {
                        if (w['Competency or SDP'] === designation) return true;
                        if (Array.isArray(w.designations) && w.designations.includes(designation)) return true;
                        return false;
                      });
                      if (workload) {
                        const dtype = workload.designation_type || '';

                        // Competency rows: SOP §2.5(2)(c) auto-eligible.
                        // The percentage is meaningless here (backend
                        // populates it only as informational context),
                        // so render an "Auto-eligible" badge instead of
                        // a threshold-gated %.
                        if (dtype === 'competency') {
                          return (
                            <Badge color="blue">Auto-eligible (Competency)</Badge>
                          );
                        }

                        // AI Competency: 75% union across AI + Databases +
                        // Analytics services per SOP §2.5(4)(a)(i). Show
                        // the union % with context so reviewers know the
                        // number spans multiple service families.
                        if (dtype === 'ai_competency') {
                          const threshold = specializedWorkloadData?.threshold || 75.0;
                          const percentageDisplay = workload.percentage_display || workload.Percentage || '-';
                          const percentageValue = workload.percentage ??
                                                 (workload.Percentage ? parseFloat(workload.Percentage.replace('%', '')) : 0);
                          const meetsThreshold = percentageValue >= threshold;
                          return (
                            <SpaceBetween direction="horizontal" size="xs">
                              <Box
                                color={meetsThreshold ? "text-status-success" : "text-status-info"}
                                fontWeight={meetsThreshold ? "bold" : "normal"}
                              >
                                {percentageDisplay}
                              </Box>
                              <Box color="text-body-secondary" fontSize="body-s">
                                (AI + DB + Analytics union)
                              </Box>
                              {meetsThreshold && (
                                <Badge color="green">Qualifies</Badge>
                              )}
                            </SpaceBetween>
                          );
                        }

                        // SDP path: the actual 75% gate applies.
                        const threshold = specializedWorkloadData?.threshold || 75.0;
                        const percentageDisplay = workload.percentage_display || workload.Percentage || '-';
                        const percentageValue = workload.percentage ??
                                               (workload.Percentage ? parseFloat(workload.Percentage.replace('%', '')) : 0);
                        const meetsThreshold = percentageValue >= threshold;
                        return (
                          <SpaceBetween direction="horizontal" size="xs">
                            <Box
                              color={meetsThreshold ? "text-status-success" : "text-status-info"}
                              fontWeight={meetsThreshold ? "bold" : "normal"}
                            >
                              {percentageDisplay}
                            </Box>
                            {meetsThreshold && (
                              <Badge color="green">Qualifies</Badge>
                            )}
                          </SpaceBetween>
                        );
                      }

                      // Check if this designation is unmatched (not eligible for MAP)
                      const unmatched = unmatchedDesignations.find(u => u.designation === designation);
                      if (unmatched) {
                        return (
                          <StatusIndicator type="warning">
                            Not eligible for MAP
                          </StatusIndicator>
                        );
                      }

                      // No data available
                      return '-';
                    }
                  }
                // Covered ARR % is the specialized workload check's calculator data.
                ].filter((column) => !(specializedWithheld && column.id === 'arrPercentage'))}
                items={sortedApplications}
                loadingText="Loading confirmed applications"
                empty={
                  <Box textAlign="center" color="inherit">
                    <b>No confirmed applications</b>
                    <Box padding={{ bottom: 's' }} variant="p" color="inherit">
                      No confirmed partner applications found.
                    </Box>
                  </Box>
                }
              />
            </Container>
          );
        })()}
      </SpaceBetween>
    );
  };

  const renderApplications = () => {
    // Use the ACTUAL DynamoDB structure - applications are in the partner_eligibility validation
    const partnerData = request.validation_data?.detailed_validations?.partner_eligibility || {};
    const partner = request.validation_data?.complete_api_data?.fundRequest?.partner || {};
    
    const applications = partnerData.applications || [];
    
    const getValidationStatusType = (status) => {
      if (status === 'eligible' || status === 'pass') return 'success';
      if (status === 'not_eligible' || status === 'fail') return 'error';
      return 'warning';
    };

    const validationStatusType = getValidationStatusType(partnerData.status);

    return (
      <SpaceBetween size="l">
        <Container header={<Header variant="h2">Partner Information</Header>}>
          <ColumnLayout columns={2} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Partner Name</Box>
              <div>{partnerData.partner_name || partner.name || request.requestor || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">SPMS ID</Box>
              <div>{partnerData.spms_id || partner.spmsId || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">MCP Status</Box>
              <StatusIndicator type={partnerData.has_mcp ? 'success' : 'stopped'}>
                {partnerData.has_mcp ? 'Active' : 'Inactive'}
              </StatusIndicator>
            </div>
            <div>
              <Box variant="awsui-key-label">MCR Status</Box>
              <StatusIndicator type={partnerData.has_mcr ? 'success' : 'stopped'}>
                {partnerData.has_mcr ? 'Active' : 'Inactive'}
              </StatusIndicator>
            </div>
            <div>
              <Box variant="awsui-key-label">Partner Eligibility Status</Box>
              <StatusIndicator type={validationStatusType}>
                {partnerData.status?.toUpperCase() || 'UNKNOWN'}
              </StatusIndicator>
            </div>
          </ColumnLayout>
        </Container>

        {/* Eligibility Factors */}
        {partnerData.eligibility_factors && partnerData.eligibility_factors.filter(factor => 
          !factor.toLowerCase().includes('strategic collaboration') && 
          !factor.toLowerCase().includes('sca') &&
          !factor.toLowerCase().includes('re:stack')
        ).length > 0 && (
          <Container header={<Header variant="h2">Eligibility Factors</Header>}>
            <Box variant="p">
              <ul style={{ marginTop: '0', paddingLeft: '20px' }}>
                {partnerData.eligibility_factors
                  .filter(factor => 
                    !factor.toLowerCase().includes('strategic collaboration') && 
                    !factor.toLowerCase().includes('sca') &&
                    !factor.toLowerCase().includes('re:stack')
                  )
                  .map((factor, index) => {
                    // Fix competency name - replace MCP with MMC
                    let correctedFactor = factor.replace(/^MCP:/g, 'MMC:');
                    correctedFactor = correctedFactor.replace('Migration Competency Program', 'Migration and Modernization Services Competency');
                    return (
                      <li key={index} style={{ marginBottom: '4px' }}>
                        <Badge color="green">{correctedFactor}</Badge>
                      </li>
                    );
                  })}
              </ul>
            </Box>
          </Container>
        )}

        {/* Applications Table */}
        {applications.length > 0 ? (
          <Container header={<Header variant="h2" counter={`(${applications.length})`}>Confirmed Applications</Header>}>
            <Table
              columnDefinitions={[
                {
                  id: 'name',
                  header: 'Name',
                  cell: item => item.name || '-'
                },
                {
                  id: 'status',
                  header: 'Status',
                  cell: item => {
                    const status = item.status || '-';
                    let color = 'grey';
                    if (status === 'Confirmed') color = 'green';
                    else if (status === 'Submitted') color = 'blue';
                    return (
                      <Badge color={color}>
                        {status}
                      </Badge>
                    );
                  }
                },
                {
                  id: 'awardDate',
                  header: 'Award Date',
                  cell: item => {
                    const date = item.awardDate;
                    if (date) {
                      // Parse date string directly to avoid timezone conversion issues
                      const [year, month, day] = date.split('-');
                      return new Date(year, month - 1, day).toLocaleDateString();
                    }
                    return '-';
                  }
                },
                {
                  id: 'designation',
                  header: 'Designation',
                  cell: item => {
                    // Use the designation from the updated JSON data
                    return item.designationName || item.designation || '-';
                  }
                },
                {
                  id: 'recordType',
                  header: 'Record Type',
                  cell: item => {
                    // Use the recordTypeLabel from the API data
                    const recordType = item.recordTypeLabel || item.recordType || 'Service Delivery';
                    return (
                      <Badge color={recordType === 'Competency' ? 'blue' : 'green'}>
                        {recordType}
                      </Badge>
                    );
                  }
                },
                {
                  id: 'expirationDate',
                  header: 'Expiration Date',
                  cell: item => {
                    const date = item.expirationDate;
                    if (date) {
                      // Parse date string directly to avoid timezone conversion issues
                      const [year, month, day] = date.split('-');
                      return new Date(year, month - 1, day).toLocaleDateString();
                    }
                    return 'No Expiration';
                  }
                },
                {
                  id: 'partnerAccount',
                  header: 'Partner Account',
                  cell: item => item.accountName || item.partnerAccount || '-'
                }
              ]}
              items={applications}
              loadingText="Loading applications"
              empty={
                <Box textAlign="center" color="inherit">
                  <b>No applications</b>
                  <Box padding={{ bottom: 's' }} variant="p" color="inherit">
                    No confirmed applications found.
                  </Box>
                </Box>
              }
            />
          </Container>
        ) : null}
      </SpaceBetween>
    );
  };



  const renderTechnicalRequirements = () => {
    // Get Technical Requirements data from the SOP-organized structure
    const techReqData = request.technicalRequirements || {};
    const migrationDetails = techReqData.migrationDetails || {};
    const validationStatus = techReqData.validationStatus || 'unknown';
    const validationReason = techReqData.validationReason || '';
    const workstreamCompliance = migrationDetails.workstreamCompliance || {};
    
    // Get critical failures from migration details or top-level
    const criticalFailures = migrationDetails.criticalFailures || request.criticalFailures || [];
    
    // Determine validation status indicator type
    const getValidationStatusType = (status) => {
      if (status === 'pass' || status === 'validated') return 'success';
      if (status === 'fail' || status === 'workstream_issues') return 'error';
      return 'warning';
    };

    const validationStatusType = getValidationStatusType(validationStatus);

    return (
      <SpaceBetween size="l">
        {/* Critical Failures Alert - Display prominently at top if present */}
        {criticalFailures.length > 0 && (
          <Container
            header={
              <Header variant="h2">
                <span style={{ color: '#d13212' }}>⚠️ Critical Failures</span>
              </Header>
            }
          >
            <Box variant="p">
              <ul style={{ marginTop: '0', paddingLeft: '20px', color: '#d13212' }}>
                {criticalFailures.map((failure, index) => (
                  <li key={index} style={{ marginBottom: '8px', fontWeight: 'bold' }}>
                    <Badge color="red">CRITICAL</Badge> {failure}
                  </li>
                ))}
              </ul>
            </Box>
          </Container>
        )}

        {/* Validation Status Indicator */}
        <Container>
          <SpaceBetween size="m">
            <div>
              <Box variant="awsui-key-label">Section Validation Status</Box>
              <StatusIndicator type={validationStatusType}>
                {validationStatus.toUpperCase().replace('_', ' ')}
              </StatusIndicator>
            </div>
            {validationReason && (
              <div>
                <Box variant="awsui-key-label">Validation Details</Box>
                <Box variant="p">{validationReason}</Box>
              </div>
            )}
          </SpaceBetween>
        </Container>

        {/* Migration Details Section */}
        <Container header={<Header variant="h2">Migration Details</Header>}>
          <ColumnLayout columns={2} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Migration Phase</Box>
              <Badge color={migrationDetails.migrationPhase === 'Mobilize' ? 'blue' : 'green'}>
                {migrationDetails.migrationPhase || '-'}
              </Badge>
            </div>
            <div>
              <Box variant="awsui-key-label">Technical Requirements Status</Box>
              <StatusIndicator type={validationStatusType}>
                {migrationDetails.technicalRequirements || validationStatus.replace('_', ' ').toUpperCase()}
              </StatusIndicator>
            </div>
            <div>
              <Box variant="awsui-key-label">Scope Checklist Present</Box>
              <StatusIndicator type={migrationDetails.scopeChecklistPresent ? 'success' : 'error'}>
                {migrationDetails.scopeChecklistPresent ? 'Yes' : 'No'}
              </StatusIndicator>
            </div>
          </ColumnLayout>
        </Container>

        {/* Translation Notice - Conditional */}
        {(() => {
          const translationNote = migrationDetails.translationNote;
          
          return translationNote && (
            <Alert type="info" header="Translation Notice">
              {translationNote}
            </Alert>
          );
        })()}

        {/* Workstream Compliance Section */}
        {Object.keys(workstreamCompliance).length > 0 && (
          <Container header={<Header variant="h2">Workstream Compliance</Header>}>
            <ColumnLayout columns={3} variant="text-grid">
              {Object.entries(workstreamCompliance).map(([workstream, compliant]) => (
                <div key={workstream}>
                  <Box variant="awsui-key-label">{workstream}</Box>
                  <StatusIndicator type={compliant ? 'success' : 'error'}>
                    {compliant ? 'Compliant' : 'Not Compliant'}
                  </StatusIndicator>
                </div>
              ))}
            </ColumnLayout>
          </Container>
        )}


      </SpaceBetween>
    );
  };

  const renderProjectTimeline = () => {
    // Get Project Timeline data from the SOP-organized structure
    const projectTimelineData = request.projectTimeline || {};
    const timelineDetails = projectTimelineData.timelineDetails || {};
    const validationStatus = projectTimelineData.validationStatus || 'unknown';
    const validationReason = projectTimelineData.validationReason || '';

    // Determine validation status indicator type
    const getValidationStatusType = (status) => {
      if (status === 'pass' || status === 'validated') return 'success';
      if (status === 'fail' || status === 'invalid') return 'error';
      return 'warning';
    };

    const validationStatusType = getValidationStatusType(validationStatus);

    return (
      <SpaceBetween size="l">
        {/* Validation Status Indicator */}
        <Container>
          <SpaceBetween size="m">
            <div>
              <Box variant="awsui-key-label">Section Validation Status</Box>
              <StatusIndicator type={validationStatusType}>
                {validationStatus.toUpperCase()}
              </StatusIndicator>
            </div>
            {validationReason && (
              <div>
                <Box variant="awsui-key-label">Validation Details</Box>
                <Box variant="p">{validationReason}</Box>
              </div>
            )}
          </SpaceBetween>
        </Container>

        {/* Timeline Details Section */}
        <Container header={<Header variant="h2">Timeline Details</Header>}>
          <ColumnLayout columns={2} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Project Start Date</Box>
              <div>{timelineDetails.projectStartDate || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Project End Date</Box>
              <div>{timelineDetails.projectEndDate || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Submitted Date</Box>
              <div>{timelineDetails.submittedDate || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Approved Date</Box>
              <div>{timelineDetails.approvedDate || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">AWS Account ID</Box>
              <div>{timelineDetails.awsAccountId || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Project Timeline Status</Box>
              <StatusIndicator type={validationStatusType}>
                {timelineDetails.projectTimeline === 'validated' ? 'Validated' : timelineDetails.projectTimeline === 'invalid' ? 'Invalid' : timelineDetails.projectTimeline || 'Unknown'}
              </StatusIndicator>
            </div>
          </ColumnLayout>
        </Container>


      </SpaceBetween>
    );
  };

  const copyDecisionToClipboard = () => {
    const summary = request.approval_summary || {};

    const decisionText = buildDecisionClipboardText(
      summary,
      new Date().toLocaleDateString()
    );

    navigator.clipboard.writeText(decisionText).then(() => {
      addNotification('success', 'Decision Copied', 'The validation decision has been copied to your clipboard.');
    }).catch(() => {
      addNotification('error', 'Copy Failed', 'Failed to copy decision to clipboard.');
    });
  };

  const renderValidationSummary = () => {
    const summary = request.approval_summary || {};

    const getDecisionColor = (decision) => {
      const d = decision?.toLowerCase() || '';
      // New categorical decision system
      if (d === 'recommend_approval') return 'green';
      if (d === 'recommend_rejection') return 'red';
      if (d === 'needs_review') return 'blue';
      // Backward compatibility for old records
      if (d.includes('approval')) return 'green';
      if (d.includes('rejection')) return 'red';
      if (d.includes('review')) return 'blue';
      if (d.includes('approve') && !d.includes('reject')) return 'green';
      if (d.includes('reject')) return 'red';
      if (d.includes('conditional')) return 'blue';
      return 'grey';
    };

    return (
      <SpaceBetween size="xs">
        {/* Translation Notice - Conditional */}
        {(() => {
          const attachedFiles = request.validation_data?.detailed_validations?.attached_files || {};
          const scopeChecklist = attachedFiles.validations?.scope_checklist || {};
          const translationNote = scopeChecklist.translation_note;
          
          return translationNote && (
            <Alert type="info" header="Translation Notice">
              {translationNote}
            </Alert>
          );
        })()}
        
        {/* Row 1: Validation Decision */}
        <Container
          header={
            <Header
              actions={
                <Button variant="primary" iconName="copy" onClick={copyDecisionToClipboard}>
                  Copy Decision
                </Button>
              }
            >
              Validation Summary
            </Header>
          }
        >
          <ColumnLayout columns={(effectiveProgram === 'RUBICON' || effectiveProgram === 'MDF' || effectiveProgram === 'PIFCC') ? 3 : 4} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Final Decision</Box>
              {/* Rubicon, MDF, and POC store approval_summary.decision as the
                  short internal code ("approve"/"reject"/"flag_for_manual_review"
                  / "escalate"); .recommendation holds the persisted
                  recommend_approval/recommend_rejection/needs_review value
                  reviewers expect to see (and that getDecisionColor maps). Those
                  programs must read .recommendation so this in-tab badge agrees
                  with the tab icon, which is also driven by .recommendation
                  (batpur@/AutoSDE CR-295319668) — otherwise POC would show a raw
                  "flag_for_manual_review" here while the icon shows needs_review.
                  MAP/WMP/IW already store .decision in the recommend_* form. */}
              <Badge
                color={getDecisionColor(
                  _usesRecommendationVocab(effectiveProgram) ? summary.recommendation : summary.decision
                )}
                size="large"
              >
                {(_usesRecommendationVocab(effectiveProgram) ? summary.recommendation : summary.decision) || 'PENDING'}
              </Badge>
              {/* P474771832 (Option B): a MAP fund request carries two
                  independent decisions — Business Approval and Claims. The
                  overall badge above reflects whichever decision is being made
                  at the FR's current stage (decision_scope); the two labelled
                  recommendations below always show both so a claims finding is
                  never confused with a business-approval rejection. Rendered
                  only when the agent emitted the split (new MAP records). */}
              {summary.decision_scope && (
                <Box variant="small" color="text-body-secondary" margin={{ top: 'xxs' }}>
                  This decision reflects:{' '}
                  {summary.decision_scope === 'claim' ? 'Claim' : 'Business Approval'}
                </Box>
              )}
              {(summary.business_approval || summary.claims) && (
                <Box margin={{ top: 'xs' }}>
                  {summary.business_approval && summary.business_approval.recommendation && (
                    <Box margin={{ bottom: 'xxs' }}>
                      <Box variant="small" color="text-body-secondary" display="inline">
                        {summary.business_approval.label || 'Business Approval'}:{' '}
                      </Box>
                      <Badge color={getDecisionColor(summary.business_approval.recommendation)}>
                        {summary.business_approval.recommendation}
                      </Badge>
                    </Box>
                  )}
                  {summary.claims && (
                    <Box>
                      <Box variant="small" color="text-body-secondary" display="inline">
                        {summary.claims.label || 'Claims'}:{' '}
                      </Box>
                      {summary.claims.applicable === false ? (
                        <Badge color="grey">NOT APPLICABLE</Badge>
                      ) : summary.claims.recommendation ? (
                        <Badge color={getDecisionColor(summary.claims.recommendation)}>
                          {summary.claims.recommendation}
                        </Badge>
                      ) : null}
                    </Box>
                  )}
                </Box>
              )}
              {summary.needs_review_reason && (
                <Box variant="small" color="text-body-secondary" margin={{ top: 'xxs' }}>
                  Reason: {formatNeedsReviewReason(summary.needs_review_reason)}
                </Box>
              )}
              {summary.needs_review_detail && (
                <Box margin={{ top: 'xs' }}>
                  <Alert type="info" header="Why this needs your review">
                    {summary.needs_review_detail}
                  </Alert>
                </Box>
              )}
              {effectiveProgram === 'PIFCC' && summary.rule_note && (
                <Box margin={{ top: 'xs' }}>
                  <Alert type="info" header="Claim rule selected by workaround">
                    {summary.rule_note}
                  </Alert>
                </Box>
              )}
              {/* MDF reject reason. Reviewers found the bare reject codes
                  (MDF50 / MDFTravel / ...) confusing, so we LEAD with the
                  agent's plain-English reject_detail sentence and show the code
                  only as a small, muted secondary label for ops/audit. Prefer
                  the persisted approval_summary.reject_detail; fall back to the
                  blocking reject step derived from validation_results_json (for
                  rows validated before reject_detail was persisted). MDF only. */}
              {effectiveProgram === 'MDF' && (() => {
                const isRejection = String(summary.recommendation || '')
                  .toLowerCase() === 'recommend_rejection';
                if (!isRejection) return null;
                const reason = getMdfRejectReason(getMdfPerCheck(request));
                const detail = summary.reject_detail
                  || (reason && reason.reject_detail) || null;
                // Prefer the persisted approval_summary.rejection_code. The
                // concurrent-duplicate re-decision persists rejection_code +
                // reject_detail on the summary but does NOT add a rejecting entry
                // to per_check, so getMdfRejectReason() returns null there and the
                // code would otherwise be hidden despite being available (batpur).
                const code = summary.rejection_code
                  || (reason && reason.reject_code) || null;
                // Nothing to explain (no sentence and no code) — don't render an
                // empty "Why this was rejected" box (AutoSDE).
                if (!detail && !code) return null;
                return (
                  <Box margin={{ top: 'xs' }}>
                    <Alert type="error" header="Why this was rejected">
                      {detail || 'This claim did not meet an MDF funding requirement.'}
                      {code ? (
                        <Box variant="small" color="text-body-secondary" margin={{ top: 'xxs' }}>
                          Policy code: {code}
                        </Box>
                      ) : null}
                    </Alert>
                  </Box>
                );
              })()}
              {effectiveProgram === 'PIFCC' && (() => {
                const isRejection = String(summary.recommendation || '')
                  .toLowerCase() === 'recommend_rejection';
                if (!isRejection) return null;
                const detail = summary.reject_detail || null;
                const code = summary.rejection_code || null;
                if (!detail && !code) return null;
                return (
                  <Box margin={{ top: 'xs' }}>
                    <Alert type="error" header="Why this was rejected">
                      {detail || 'This claim did not meet a PIFCC funding requirement.'}
                      {code ? (
                        <Box variant="small" color="text-body-secondary" margin={{ top: 'xxs' }}>
                          Policy code: {code}
                        </Box>
                      ) : null}
                    </Alert>
                  </Box>
                );
              })()}
              {summary.line_items_translated_from && (
                <Box variant="small" color="text-body-secondary" margin={{ top: 'xxs' }}>
                  Line items machine-translated from {summary.line_items_translated_from} to English by Chronos — spot-check advised.
                </Box>
              )}
            </div>
            <div>
              <Box variant="awsui-key-label">
                Validations Performed{' '}
                <span 
                  onClick={() => setShowComplianceInfoModal(true)}
                  style={{ cursor: 'pointer', display: 'inline-block' }}
                  role="button"
                  tabIndex={0}
                  onKeyPress={(e) => e.key === 'Enter' && setShowComplianceInfoModal(true)}
                  aria-label="Show validation summary information"
                >
                  <Icon
                    name="status-info"
                    variant="link"
                  />
                </span>
              </Box>
              {/* MDF and PIFCC persist per-check results in
                  validation_results_json rather than validation_summary. Derive
                  each program's honest denominator from applicable results. */}
              {effectiveProgram === 'MDF' ? (() => {
                const counts = countMdfChecks(getMdfPerCheck(request));
                const breakdown = formatMdfValidationBreakdown(counts);
                return (
                  <>
                    <Box variant="h3">{formatMdfValidationSummary(counts)}</Box>
                    {breakdown && (
                      <Box variant="small" color="text-body-secondary">{breakdown}</Box>
                    )}
                  </>
                );
              })() : effectiveProgram === 'PIFCC' ? (() => {
                const counts = countPifccChecks(getPifccPerCheck(request));
                const breakdown = formatPifccValidationBreakdown(counts);
                return (
                  <>
                    <Box variant="h3">{formatPifccValidationSummary(counts)}</Box>
                    {breakdown && (
                      <Box variant="small" color="text-body-secondary">{breakdown}</Box>
                    )}
                  </>
                );
              })() : (
                <Box variant="h3">{summary.validation_summary || summary.compliance_score || '0/0 passed'}</Box>
              )}
              {/* "Core validations only" reads MAP's detailed_validations shape
                  (coreCount=7 is MAP's own checks). Excluded for RUBICON and
                  MMP_VMP (no core/optional split) and MDF (its own N/M + breakdown
                  lines above; MDF never persists validation_categories, so this
                  would always show the misleading "Core validations only"). */}
              {effectiveProgram !== 'RUBICON' && effectiveProgram !== 'MDF' && effectiveProgram !== 'MMP_VMP' && effectiveProgram !== 'PIFCC' && (
                <Box variant="small" color="text-body-secondary">
                  {(() => {
                    const validationData = request.validation_data?.detailed_validations?.final_decision || {};
                    const categories = validationData.validation_categories || {};
                    // Count validations excluding informational ones (duplicate_funding_requests)
                    const countedValidations = Object.keys(categories).filter(k => k !== 'duplicate_funding_requests').length;
                    // Core validations = 7 (partner_eligibility, mpe_program, opportunity_stage, pricing_calculator, technical_requirements, attached_files, project_timeline)
                    const coreCount = 7;
                    const optionalCount = countedValidations - coreCount;

                    if (optionalCount > 0) {
                      return `Includes ${optionalCount} optional validation(s)`;
                    }
                    return 'Core validations only';
                  })()}
                </Box>
              )}
            </div>
            {/* Validation Report does not apply to Rubicon, MDF, or PIFCC:
                their make_decision.py implementations do not persist agent_narrative. */}
            {effectiveProgram !== 'RUBICON' && effectiveProgram !== 'MDF' && effectiveProgram !== 'PIFCC' && (
              <div>
                <Box variant="awsui-key-label">
                  Validation Report{' '}
                  <span 
                    onClick={() => setShowValidationReportModal(true)}
                    style={{ cursor: 'pointer', display: 'inline-block' }}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => e.key === 'Enter' && setShowValidationReportModal(true)}
                    aria-label="Show validation report"
                  >
                    <Icon
                      name="status-info"
                      variant="link"
                    />
                  </span>
                </Box>
              </div>
            )}
          </ColumnLayout>
        </Container>

        {/* Row 2: Key Findings (+ Required Actions for non-Rubicon programs).
            Required Actions does not apply to Rubicon — make_decision.py
            never persists summary.required_actions, so the column was
            always empty for Rubicon rows. MAP/WMP/IW/POC keep the original
            2-column (Key Findings + Required Actions) layout unchanged. */}
        {((summary.key_findings && summary.key_findings.length > 0) || (summary.required_actions && summary.required_actions.length > 0)) && (
          <Container>
            <ColumnLayout columns={effectiveProgram === 'RUBICON' ? 1 : 2} variant="text-grid">
              {/* Key Findings Column */}
              <div>
                <Box variant="awsui-key-label">Key Findings</Box>
                {summary.key_findings && summary.key_findings.length > 0 ? (
                  <Box variant="p">
                    <ul style={{ marginTop: '0', paddingLeft: '20px', marginBottom: '0' }}>
                      {summary.key_findings
                        .map((finding, index) => {
                        // Add visual indicators if not already present. The
                        // test used to be a ✅/❌/⚠️ character class, which is
                        // the whole vocabulary MAP/WMP/IW emit but only half of
                        // MMP/VMP's: a finding prefixed ❓ (not_checked), ℹ️
                        // (nothing_to_judge) or ➖ (does_not_apply) fell through
                        // to the keyword guessing below, which prepended a
                        // SECOND glyph and, having no keyword to match, made
                        // every one of them ⚠️ — showing a warning where the
                        // agent had stated there was nothing to warn about.
                        let displayFinding = finding;
                        if (!hasOutcomeGlyph(finding)) {
                          // Determine appropriate icon based on content
                          if (finding.toLowerCase().includes('recommend approval') || 
                              finding.toLowerCase().includes('approved') || 
                              finding.toLowerCase().includes('pass') || 
                              finding.toLowerCase().includes('compliant') ||
                              finding.toLowerCase().includes('validated') ||
                              finding.toLowerCase().includes('confirmed') ||
                              finding.toLowerCase().includes('eligible') ||
                              (finding.toLowerCase().includes('requirement met') && !finding.toLowerCase().includes('requirement not met'))) {
                            displayFinding = `✅ ${finding}`;
                          } else if (finding.toLowerCase().includes('recommend rejection') ||
                                   finding.toLowerCase().includes('rejected') ||
                                   finding.toLowerCase().includes('fail') ||
                                   finding.toLowerCase().includes('critical') ||
                                   finding.toLowerCase().includes('missing') ||
                                   finding.toLowerCase().includes('not found') ||
                                   finding.toLowerCase().includes('exceeded')) {
                            displayFinding = `❌ ${finding}`;
                          } else if (finding.toLowerCase().includes('invalid_account_id_warning') ||
                                   finding.toLowerCase().includes('warning')) {
                            displayFinding = `⚠️ ${finding}`;
                          } else {
                            displayFinding = `⚠️ ${finding}`;
                          }
                        }
                        
                        return (
                          <li key={index} style={{ marginBottom: '4px' }}>
                            {displayFinding}
                          </li>
                        );
                      })}
                    </ul>
                  </Box>
                ) : (
                  <Box variant="p" color="text-body-secondary">No key findings available</Box>
                )}
              </div>

              {/* Required Actions Column — not rendered for Rubicon */}
              {effectiveProgram !== 'RUBICON' && (
                <div>
                  <Box variant="awsui-key-label">Required Actions</Box>
                  {summary.required_actions && summary.required_actions.length > 0 ? (
                    <Box variant="p">
                      <ul style={{ marginTop: '0', paddingLeft: '20px', marginBottom: '0' }}>
                        {summary.required_actions.map((action, index) => {
                          // Account ID warning on Assess phase — show as ⚠️ note, not ACTION REQUIRED
                          const actionLower = action.toLowerCase();
                          const isAccountIdWarning = actionLower.includes('account id') &&
                            actionLower.includes('flagged for review');
                          const isMpsaWarning = actionLower.includes('mpsa') &&
                            actionLower.includes('review');
                          return (
                            <li key={index} style={{ marginBottom: '4px' }}>
                              {isAccountIdWarning
                                ? <span>⚠️ {action}</span>
                                : <>
                                    {isMpsaWarning && (
                                      <><span role="img" aria-label="MPSA review warning">⚠️</span>{' '}</>
                                    )}
                                    <Badge color="blue">ACTION REQUIRED</Badge> {action}
                                  </>
                              }
                            </li>
                          );
                        })}
                      </ul>
                    </Box>
                  ) : (
                    <Box variant="p" color="text-body-secondary">No actions required</Box>
                  )}
                </div>
              )}
            </ColumnLayout>
          </Container>
        )}

        {/* Claim: what the agent found on the claim, and the partner's sign-off for the
            PSA to download. Only once a claim is submitted (the agent writes
            `aws_signoff` then). */}
        {effectiveProgram === 'MMP_VMP' && summary.aws_signoff && (
          <AwsSignoffPanel
            fundRequestId={request.apfp_fundrequestid || requestId}
            signoff={summary.aws_signoff}
            requested={summary.total_fund_requested}
            currency={summary.cash_currency}
          />
        )}

        {/* WMP-specific detail panel. Renders sub-program, tranche, credit
            approved, escalation codes, and the per-validation breakdown
            from validation_results_json. WMP has no Partner Eligibility /
            Opportunity / Pricing / Technical Review tabs (they don't apply
            to its 5-validation SOP), so this panel is the canonical place
            to surface the WMP-only fields. */}
        {effectiveProgram === 'WMP' && (
          <Container header={<Header variant="h2">WMP Validation Details</Header>}>
            <SpaceBetween size="m">
              <ColumnLayout columns={4} variant="text-grid">
                <div>
                  <Box variant="awsui-key-label">Sub-program</Box>
                  {/* Strip redundant WMP_ prefix at the display layer. The
                      agent's approval_summary.migration_phase already drops
                      it (e.g. "WMP MISC"); the top-level wmp_sub_program
                      keeps the raw "WMP_MISC". */}
                  <Box>{(request.wmp_sub_program || '').replace(/^WMP_/, '') || '-'}</Box>
                </div>
                <div>
                  <Box variant="awsui-key-label">Tranche</Box>
                  <Box>{request.tranche_number != null ? `#${request.tranche_number}` : '-'}</Box>
                </div>
                <div>
                  <Box variant="awsui-key-label">Credit Approved</Box>
                  <Box>{request.total_credit_approved != null
                    ? formatCurrency(parseFloat(request.total_credit_approved))
                    : '-'}</Box>
                </div>
                <div>
                  <Box variant="awsui-key-label">Escalation Codes</Box>
                  {Array.isArray(request.escalation_codes) && request.escalation_codes.length > 0 ? (
                    <SpaceBetween direction="horizontal" size="xs">
                      {request.escalation_codes.map((code, i) => (
                        <Badge key={i} color="blue">{code}</Badge>
                      ))}
                    </SpaceBetween>
                  ) : (
                    <Box>-</Box>
                  )}
                </div>
              </ColumnLayout>

              {(() => {
                let validations = [];
                try {
                  validations = typeof request.validation_results_json === 'string'
                    ? JSON.parse(request.validation_results_json)
                    : (request.validation_results_json || []);
                } catch (e) {
                  validations = [];
                }
                if (!Array.isArray(validations) || validations.length === 0) return null;
                const labelMap = {
                  // Misc WMP chain.
                  partner_eligibility: 'Partner Eligibility',
                  deal_tracker: 'Deal Tracker',
                  fr_fields: 'FR Fields',
                  proof_of_deal: 'Proof of Deal',
                  tranche_amounts: 'Tranche Amounts',
                  // Bulk PIF chain (validate_pif_fund_request writes these keys
                  // instead of deal_tracker / proof_of_deal / tranche_amounts).
                  pif_fields: 'FR Fields',
                  pif_spreadsheet: 'Bulk Spreadsheet',
                  pif_funding: 'Funding Reconciliation',
                  pif_opportunity_stage: 'Opportunity Stage',
                };
                // WMP emits: pass | reject | escalate.
                // 'fail', 'needs_review', 'warning' kept as defensive aliases
                // in case other programs reuse this helper or the SOP grows.
                const statusType = (s) => {
                  const v = (s || '').toLowerCase();
                  if (v === 'pass') return 'success';
                  if (v === 'reject' || v === 'fail') return 'error';
                  if (v === 'escalate' || v === 'needs_review' || v === 'warning') return 'warning';
                  return 'pending';
                };
                return (
                  <div>
                    <Box variant="awsui-key-label">Validations</Box>
                    <SpaceBetween size="xs">
                      {validations.map((v, i) => (
                        <Box key={i}>
                          <ColumnLayout columns={3} variant="text-grid">
                            <div>
                              <Box variant="strong">
                                {labelMap[v.validation] || v.validation}
                              </Box>
                            </div>
                            <div>
                              <StatusIndicator type={statusType(v.status)}>
                                {(v.status || '').toUpperCase()}
                              </StatusIndicator>
                            </div>
                            <div>
                              <Box variant="small" color="text-body-secondary">
                                {v.reason || '-'}
                              </Box>
                            </div>
                          </ColumnLayout>
                        </Box>
                      ))}
                    </SpaceBetween>
                  </div>
                );
              })()}
            </SpaceBetween>
          </Container>
        )}
      </SpaceBetween>
    );
  };

  const renderMigrationDetails = () => {
    const fundRequest = request.validation_data?.complete_api_data?.fundRequest || {};
    const techData = request.validation_data?.detailed_validations?.technical_requirements || {};
    const pricingData = request.validation_data?.detailed_validations?.pricing_calculator || {};
    const incentives = fundRequest.incentives || {};
    
    const getValidationStatusType = (status) => {
      if (status === 'validated' || status === 'eligible' || status === 'pass') return 'success';
      if (status === 'not_eligible' || status === 'fail') return 'error';
      return 'warning';
    };

    return (
      <SpaceBetween size="l">
        <Container header={<Header variant="h2">Migration Details</Header>}>
          <ColumnLayout columns={3} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Migration Phase</Box>
              <Badge color="blue">
                {fundRequest.migrationPhase || 'Mobilize'}
              </Badge>
            </div>
            <div>
              <Box variant="awsui-key-label">Annual Recurring Revenue (ARR) (USD)</Box>
              <div>{(() => {
                if (fundRequest.arrUsdAmount) {
                  const numericValue = parseFloat(fundRequest.arrUsdAmount.replace(/[^0-9.-]+/g, ''));
                  return formatCurrency(numericValue);
                }
                return pricingData.annual_recurring_revenue ? formatCurrency(pricingData.annual_recurring_revenue) : '-';
              })()}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Eligible ARR (USD)</Box>
              <div>{(() => {
                if (fundRequest.eligibleArrUsdAmount) {
                  const numericValue = parseFloat(fundRequest.eligibleArrUsdAmount.replace(/[^0-9.-]+/g, ''));
                  return formatCurrency(numericValue);
                }
                return pricingData.calculator_total_arr ? formatCurrency(pricingData.calculator_total_arr) : '-';
              })()}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Modernization ARR (USD)</Box>
              <div>{(() => {
                if (fundRequest.modernizationArrUsdAmount) {
                  const numericValue = parseFloat(fundRequest.modernizationArrUsdAmount.replace(/[^0-9.-]+/g, ''));
                  return formatCurrency(numericValue);
                }
                return '-';
              })()}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Modernization Incentive (USD)</Box>
              <div>{(() => {
                if (fundRequest.modernizationIncentiveUsdAmount) {
                  const numericValue = parseFloat(fundRequest.modernizationIncentiveUsdAmount.replace(/[^0-9.-]+/g, ''));
                  return formatCurrency(numericValue);
                }
                return '-';
              })()}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">VMware Acknowledgement</Box>
              <StatusIndicator type={incentives.vmware?.eligible ? 'success' : 'stopped'}>
                {incentives.vmware?.eligible ? 'Yes' : 'No'}
              </StatusIndicator>
            </div>
            <div>
              <Box variant="awsui-key-label">Funding Type</Box>
              <Badge color="blue">{fundRequest.fundingType || '-'}</Badge>
            </div>
            <div>
              <Box variant="awsui-key-label">Public Sector</Box>
              <StatusIndicator type={fundRequest.isPublicSector ? 'success' : 'stopped'}>
                {fundRequest.isPublicSector ? 'Yes' : 'No'}
              </StatusIndicator>
            </div>
            <div>
              <Box variant="awsui-key-label">Greenfield Eligible</Box>
              <StatusIndicator type={incentives.greenfield?.eligible ? 'success' : 'stopped'}>
                {incentives.greenfield?.eligible ? 'Yes' : 'No'}
              </StatusIndicator>
            </div>
            <div>
              <Box variant="awsui-key-label">VMware Incentive Eligible</Box>
              <StatusIndicator type={incentives.vmware?.eligible ? 'success' : 'stopped'}>
                {incentives.vmware?.eligible ? 'Yes' : 'No'}
              </StatusIndicator>
            </div>
          </ColumnLayout>
        </Container>
      </SpaceBetween>
    );
  };

  const renderProjectDetails = () => {
    const fundRequest = request.validation_data?.complete_api_data?.fundRequest || {};
    const project = fundRequest.project || {};
    const approvalDetails = fundRequest.approvalDetails || {};
    const timelineData = request.validation_data?.detailed_validations?.project_timeline || blindNotRun;

    // Manual-check reminders (V2323707533). Display only -- nothing here is
    // validated, acknowledged, or persisted. getProgramManualChecks returns []
    // for programs without a list, so a program that falls through to this
    // renderer without declaring one shows no box.
    const manualChecks = getProgramManualChecks(effectiveProgram);

    const getValidationStatusType = (status) => {
      if (status === 'validated' || status === 'eligible' || status === 'pass') return 'success';
      if (status === 'not_eligible' || status === 'fail') return 'error';
      return 'warning';
    };

    return (
      <SpaceBetween size="l">
        {/* Validation Decision */}
        <Container header={<Header variant="h2">Project Details Summary</Header>}>
          <ValidationSummaryBanner 
            label="Project Timeline" 
            validationData={timelineData} 
          />
        </Container>

        {/* Top of this tab, directly under the project-timeline validation
            banner. Display only -- nothing here is validated or persisted.
            Distinct from the format-level account-id warning further down,
            which reports a malformed id on this FR rather than a check
            nobody performed. Note the overall recommendation is the Final
            Decision badge on the Approval Summary tab, not this banner. */}
        {manualChecks.length > 0 && (
          <Alert
            type="info"
            header="Manual checks required before approval"
          >
            <SpaceBetween size="xs">
              <Box variant="p">
                Chronos does not validate the items below. Confirm each one
                before relying on the automated recommendation — "Recommend
                Approval" does not mean every required element on this fund
                request has been checked.
              </Box>
              <ul style={{ marginTop: '0', paddingLeft: '20px', marginBottom: '0' }}>
                {manualChecks.map(check => (
                  <li key={check.id} style={{ marginBottom: '4px' }}>{check.text}</li>
                ))}
              </ul>
            </SpaceBetween>
          </Alert>
        )}

        <Container header={<Header variant="h2">Project Information</Header>}>
          <ColumnLayout columns={3} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Activity Name</Box>
              <div>{project.activityName || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Budget Year</Box>
              <div>{project.budgetYear || request.budget_year || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Customer Considering</Box>
              <div>{project.customerConsidering || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Customer RFx</Box>
              <div>{project.customerRfx || 'None'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Third Party Funding</Box>
              <div>{project.thirdPartyFunding || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Project Country</Box>
              <div>{project.location?.country || '-'} {project.location?.states?.join(', ') || ''}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Project Location GEO</Box>
              <div>{project.location?.locationGeo || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Funding Expiration Date</Box>
              <div>{project.expirationDate ? new Date(project.expirationDate).toLocaleDateString() : '-'}</div>
            </div>
          </ColumnLayout>
        </Container>

        <Container header={<Header variant="h2">Project Timeline</Header>}>
          <ColumnLayout columns={2} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Project Start Date</Box>
              <div>{project.startDate ? new Date(project.startDate).toLocaleDateString() : (timelineData.project_start_date ? new Date(timelineData.project_start_date).toLocaleDateString() : '-')}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Project End Date</Box>
              <div>{project.endDate ? new Date(project.endDate).toLocaleDateString() : (timelineData.project_end_date ? new Date(timelineData.project_end_date).toLocaleDateString() : '-')}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Submission Date</Box>
              <div>{approvalDetails.submissionDate ? new Date(approvalDetails.submissionDate).toLocaleDateString() : '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Approval Date</Box>
              <div>{approvalDetails.approvalDate ? new Date(approvalDetails.approvalDate).toLocaleDateString() : '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Last Stage Transition</Box>
              <div>{fundRequest.lastStageTransitionDate ? new Date(fundRequest.lastStageTransitionDate).toLocaleDateString() : '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">AWS Account ID</Box>
              <div>{project.revisedAwsAccountId || project.awsAccountId || timelineData.aws_account_id || '-'}</div>
              {project.revisedAwsAccountId && (
                <Box variant="small" color="text-status-inactive">Revised (original: {project.awsAccountId || timelineData.aws_account_id || '-'})</Box>
              )}
              {(() => {
                const accountId = project.revisedAwsAccountId || project.awsAccountId || timelineData.aws_account_id || '';
                if (/^0+$/.test(accountId)) {
                  return (
                    <Box margin={{ top: 'xs' }}>
                      <Alert type="warning" statusIconAriaLabel="Warning">
                        Warning: The AWS Account ID provided ({accountId}) is not valid. While this requires attention before project completion, no immediate action is needed at the Assess phase.
                      </Alert>
                    </Box>
                  );
                }
                return null;
              })()}
            </div>
            <div>
              <Box variant="awsui-key-label">Timeline Status</Box>
              <StatusIndicator type={getValidationStatusType(timelineData.status)}>
                {timelineData.status?.toUpperCase() || 'UNKNOWN'}
              </StatusIndicator>
            </div>
          </ColumnLayout>
          {timelineData.status === 'duration_exceeded' && (
            <Box padding={{ top: 'm' }}>
              <Alert type="warning" header="Project Duration Exceeds Threshold">
                Project duration of {timelineData.validation_checks?.duration_months} months exceeds
                the {timelineData.validation_checks?.map_program?.replace('_', ' ')} limit
                of {timelineData.validation_checks?.max_duration_months} months. This fund request
                requires further review.
              </Alert>
            </Box>
          )}
        </Container>
      </SpaceBetween>
    );
  };

  // Rubicon-specific Project Details: reads the curated fundRequest.project.*
  // and fundRequest.approval.* shape from curate_rubicon_apfp_for_ui()
  // (ChronosAgentCoreAgents data_parser.py) — different field names than
  // MAP's fundRequest.project.{activityName,budgetYear,location,...}.
  // No ValidationSummaryBanner here: Rubicon has no per-project-timeline
  // check equivalent to MAP's detailed_validations.project_timeline, so
  // this tab is purely informational, not a pass/fail validation surface.
  const renderRubiconProjectDetails = () => {
    const fundRequest = request.validation_data?.complete_api_data?.fundRequest || {};
    const project = fundRequest.project || {};
    const approval = fundRequest.approval || {};

    return (
      <SpaceBetween size="l">
        <Container header={<Header variant="h2">Project Information</Header>}>
          <ColumnLayout columns={3} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Description</Box>
              <div>{project.description || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Executed Country</Box>
              <div>{project.executedCountry || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Location GEO</Box>
              <div>{project.locationGeo || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Location Sub-Region</Box>
              <div>{project.locationSubRegion || '-'}</div>
            </div>
          </ColumnLayout>
        </Container>

        <Container header={<Header variant="h2">Project Timeline</Header>}>
          <ColumnLayout columns={2} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Planned Start Date</Box>
              <div>{project.plannedStartDate ? new Date(project.plannedStartDate).toLocaleDateString(undefined, { timeZone: 'UTC' }) : '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Planned End Date</Box>
              <div>{project.plannedEndDate ? new Date(project.plannedEndDate).toLocaleDateString(undefined, { timeZone: 'UTC' }) : '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Submitted for Approval</Box>
              <div>{approval.dateSubmittedForApproval ? new Date(approval.dateSubmittedForApproval).toLocaleDateString(undefined, { timeZone: 'UTC' }) : '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Approved Date</Box>
              <div>{approval.dateApproved ? new Date(approval.dateApproved).toLocaleDateString(undefined, { timeZone: 'UTC' }) : '-'}</div>
            </div>
          </ColumnLayout>
        </Container>
      </SpaceBetween>
    );
  };



  // MMP/VMP Request Details: the fields a reviewer needs to verify the agent's
  // findings without opening the APFP portal. Reads from fundRequest.requestDetails
  // (curate_mmp_vmp_apfp_for_ui) plus the project/approval blocks the Rubicon
  // renderer already reads. No PII, no attachment data — all from the APFP JSON.
  const renderMmpVmpRequestDetails = () => {
    const fundRequest = request.validation_data?.complete_api_data?.fundRequest || {};
    const rd = fundRequest.requestDetails || {};
    const project = fundRequest.project || {};
    const approval = fundRequest.approval || {};

    const formatMoney = (money) => {
      if (!money || typeof money !== 'object') return '-';
      if (money.units == null && money.nanos == null) return '-';
      const units = Math.round(Number(money.units ?? 0) + Number(money.nanos ?? 0) / 1e9);
      const formatted = units.toLocaleString();
      return money.currency ? `${money.currency} ${formatted}` : formatted;
    };

    return (
      <SpaceBetween size="l">
        <Container header={<Header variant="h2">Funding</Header>}>
          <ColumnLayout columns={3} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Funding Type</Box>
              <div>{rd.fundingType || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Annual Run Rate (ARR)</Box>
              <div>{formatMoney(rd.annualRunRate)}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Budget Year</Box>
              <div>{rd.budgetYear || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Total Cost of Activity</Box>
              <div>{formatMoney(rd.totalCostOfActivity)}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Partner Contribution</Box>
              <div>{formatMoney(rd.partnerContribution)}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Currency</Box>
              <div>{rd.cashCurrency || '-'}</div>
            </div>
          </ColumnLayout>
        </Container>

        <Container header={<Header variant="h2">Workload and Migration</Header>}>
          <ColumnLayout columns={3} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">MRA Completed</Box>
              <div>{rd.mraCompleted != null ? String(rd.mraCompleted) : '-'}</div>
            </div>
            {rd.awsCalculatorUrl && (
              <div>
                <Box variant="awsui-key-label">Pricing Calculator</Box>
                <div><a href={rd.awsCalculatorUrl} target="_blank" rel="noopener noreferrer">Open Calculator</a></div>
              </div>
            )}
          </ColumnLayout>
        </Container>

        <Container header={<Header variant="h2">Location and Timeline</Header>}>
          <ColumnLayout columns={3} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Executed Country</Box>
              <div>{project.executedCountry || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Location GEO</Box>
              <div>{project.locationGeo || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Location Sub-Region</Box>
              <div>{project.locationSubRegion || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Planned Start Date</Box>
              <div>{project.plannedStartDate ? new Date(project.plannedStartDate).toLocaleDateString(undefined, { timeZone: 'UTC' }) : '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Planned End Date</Box>
              <div>{project.plannedEndDate ? new Date(project.plannedEndDate).toLocaleDateString(undefined, { timeZone: 'UTC' }) : '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Submitted for Approval</Box>
              <div>{approval.dateSubmittedForApproval ? new Date(approval.dateSubmittedForApproval).toLocaleDateString(undefined, { timeZone: 'UTC' }) : '-'}</div>
            </div>
          </ColumnLayout>
        </Container>
      </SpaceBetween>
    );
  };

  const getModernizationPathway = (serviceCode, serviceName) => {
    const pathway = modPathways.find(p => 
      p.service_code === serviceCode || 
      p.service_name === serviceName
    );
    return pathway ? pathway.Modernization_Pathway : 'Non Modern';
  };

  // Renders Sections A/B/C/D + notes for a single phase block from the
  // scope checklist (P469995696). Returns null when the phase block is
  // empty — callers should still create the Tabs tab for parity.
  const renderScopeChecklistPhase = (phaseKey, phaseBlock) => {
    if (!phaseBlock || typeof phaseBlock !== 'object') return null;
    const canonical = SCOPE_WORKSTREAMS[phaseKey] || [];

    // Section A rows come straight from backend general_information[].
    const generalInfo = Array.isArray(readPhaseField(phaseBlock, phaseKey, 'general_information'))
      ? readPhaseField(phaseBlock, phaseKey, 'general_information')
      : [];

    // Section C: join canonical list with backend rows on the normalized name
    // (see workstreamKey — the backend and template differ on "&"). Backend
    // rows that still don't match a canonical entry render at the end as
    // "extras" so we never silently drop data.
    const backendRows = Array.isArray(readPhaseField(phaseBlock, phaseKey, 'workstreams'))
      ? readPhaseField(phaseBlock, phaseKey, 'workstreams')
      : [];
    const backendByName = new Map(backendRows.map((w) => [canonicalWorkstreamKey(w?.name, phaseKey), w]));
    const canonicalNames = new Set(canonical.map((c) => workstreamKey(c.name)));
    const workstreamRows = [
      ...canonical.map((c) => {
        const row = backendByName.get(workstreamKey(c.name));
        // Spread the backend row FIRST so the canonical fields below always
        // win. A backend row carries its own `name` ("Strategy & Analysis
        // (7Rs)"), which would otherwise clobber the canonical display name
        // and defeat the point of joining against a canonical list.
        //
        // `matched` distinguishes "extraction produced no row for this
        // workstream" from "row present but In Scope was left blank". Only the
        // latter is a partner omission worth warning about.
        return { ...(row || {}), name: c.name, required: c.required, isCanonical: true, matched: Boolean(row) };
      }),
      ...backendRows
        .filter((w) => !canonicalNames.has(canonicalWorkstreamKey(w?.name, phaseKey)))
        .map((w) => ({ ...w, name: String(w?.name || '(unnamed)'), required: false, isCanonical: false, matched: true })),
    ];
    const effortDaysTotal = sumEffortDays(workstreamRows);

    // Section D: only render when ai_modernization is a real object.
    const aiModRaw = readPhaseField(phaseBlock, phaseKey, 'ai_modernization');
    const aiMod = aiModRaw && typeof aiModRaw === 'object' ? aiModRaw : null;
    const aiRows = aiMod
      ? (AI_MODERNIZATION_ROWS[phaseKey] || []).map((r) => ({
          label: r.label,
          in_scope: aiMod[r.scopeField],
          comment: r.commentField ? aiMod[r.commentField] : null,
        }))
      : [];

    const notes = readPhaseField(phaseBlock, phaseKey, 'notes_for_approvers');

    const yesNoBadge = (val) => {
      const s = String(val || '').toLowerCase();
      if (s === 'yes') return <Badge color="green">Yes</Badge>;
      if (s === 'no') return <Badge color="grey">No</Badge>;
      return <Box variant="small" color="text-body-secondary">-</Box>;
    };

    return (
      <SpaceBetween size="l">
        {/* Section A — General Information */}
        {generalInfo.length > 0 && (
          <div>
            <Box variant="h4" padding={{ bottom: 'xs' }}>A — General Information</Box>
            <Table
              variant="embedded"
              columnDefinitions={[
                { id: 'name', header: 'Field', cell: (item) => item.name || '-' },
                { id: 'in_scope', header: 'In Scope', cell: (item) => yesNoBadge(item.in_scope) },
                { id: 'comments', header: 'Comments', cell: (item) => item.comments || <Box variant="small" color="text-body-secondary">-</Box> },
              ]}
              items={generalInfo}
            />
          </div>
        )}

        {/* Section B — Effort Details */}
        <div>
          <Box variant="h4" padding={{ bottom: 'xs' }}>B — Effort Details</Box>
          <ColumnLayout columns={2} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Total Project Cost (local currency)</Box>
              <div>
                {formatProjectCost(
                  readPhaseField(phaseBlock, phaseKey, 'total_project_cost', ['cost']),
                  readPhaseField(phaseBlock, phaseKey, 'currency_code'),
                )}
              </div>
            </div>
            <div>
              <Box variant="awsui-key-label">Number of Resources</Box>
              <div>{readPhaseField(phaseBlock, phaseKey, 'num_resources', ['resources']) ?? '-'}</div>
            </div>
          </ColumnLayout>
        </div>

        {/* Section C — Workstreams */}
        <div>
          <Box variant="h4" padding={{ bottom: 'xs' }}>C — {phaseKey === 'assess' ? 'Assess' : 'Mobilize'} Workstreams</Box>
          <Table
            variant="embedded"
            columnDefinitions={[
              {
                id: 'workstream',
                header: 'Workstream',
                cell: (item) => item.name || '-',
              },
              {
                id: 'required',
                header: 'Required',
                cell: (item) => {
                  if (!item.isCanonical) return <Box variant="small" color="text-body-secondary">?</Box>;
                  if (item.required) return <Icon name="status-positive" variant="success" />;
                  return <Box variant="small" color="text-body-secondary">—</Box>;
                },
              },
              {
                id: 'in_scope',
                header: 'In Scope',
                cell: (item) => {
                  const s = String(item.in_scope || '').toLowerCase();
                  // No backend row for this canonical workstream: the
                  // extraction didn't cover it, which is not the same as the
                  // partner declining it. Warning here would contradict
                  // all_workstreams_compliant, so stay neutral.
                  if (item.isCanonical && !item.matched) {
                    return <Box variant="small" color="text-body-secondary">Not reported</Box>;
                  }
                  if (item.required && s !== 'yes') {
                    return (
                      <StatusIndicator type="warning">
                        {s === 'no' ? 'Required but not in scope' : 'Required — not answered'}
                      </StatusIndicator>
                    );
                  }
                  return yesNoBadge(item.in_scope);
                },
              },
              {
                id: 'effort_days',
                header: 'Effort (days)',
                cell: (item) => (item.effort_days == null ? '-' : item.effort_days),
              },
              {
                id: 'effort_percentage',
                header: '% of Total',
                cell: (item) => formatEffortPercentage(item.effort_percentage),
              },
            ]}
            items={workstreamRows}
          />
          <Box padding={{ top: 's' }}>
            <ColumnLayout columns={2} variant="text-grid">
              <div>
                <Box variant="awsui-key-label">Effort in days (in-scope total)</Box>
                <div>{effortDaysTotal}</div>
              </div>
              <div>
                <Box variant="awsui-key-label">Effort in weeks</Box>
                <div>{(effortDaysTotal / 5).toFixed(1)}</div>
              </div>
            </ColumnLayout>
          </Box>
        </div>

        {/* Section D — AI Modernization (only when the phase block declares it) */}
        {aiMod && aiRows.length > 0 && (
          <div>
            <Box variant="h4" padding={{ bottom: 'xs' }}>D — REQUIRED for AI Modernization (if applicable)</Box>
            <Table
              variant="embedded"
              columnDefinitions={[
                { id: 'label', header: 'Deliverable', cell: (item) => item.label },
                { id: 'in_scope', header: 'In Scope', cell: (item) => yesNoBadge(item.in_scope) },
                {
                  id: 'comment',
                  header: 'Comments',
                  cell: (item) => {
                    if (!item.comment) return <Box variant="small" color="text-body-secondary">-</Box>;
                    const text = String(item.comment);
                    if (text.length <= 120) return text;
                    return (
                      <Popover
                        dismissButton={false}
                        position="top"
                        size="large"
                        triggerType="text"
                        content={<div style={{ whiteSpace: 'pre-wrap' }}>{text}</div>}
                      >
                        {text.slice(0, 117) + '...'}
                      </Popover>
                    );
                  },
                },
              ]}
              items={aiRows}
            />
          </div>
        )}

        {/* Notes for Approvers */}
        {notes && (
          <div>
            <Box variant="awsui-key-label">Notes for Approvers</Box>
            <div style={{ whiteSpace: 'pre-wrap' }}>{notes}</div>
          </div>
        )}
      </SpaceBetween>
    );
  };

  const renderTechnicalReview = () => {
    // Real API data from validation results and calculator details
    const validationData = request.validation_data?.detailed_validations || {};
    const techReq = validationData.technical_requirements || {};
    const attachedFilesData = projectTechnicalReviewAttachedFiles(validationData.attached_files);
    const fundRequest = request.validation_data?.complete_api_data?.fundRequest || {};
    
    // Get real data from chronos_calc_review_history via calculator details API
    const calculatorData = calculatorDetails || {};
    const servicesData = calculatorData.services || [];
    
    // Calculate modernization metrics using the same logic as PricingCalculatorTab
    const totalServices = servicesData.length;
    const modernServices = servicesData.filter(service => 
      getModernizationPathway(service.service_code, service.service_name) !== 'Non Modern'
    );
    const modernizationIndex = totalServices > 0 ? Math.round((modernServices.length / totalServices) * 100) : 0;
    
    // Calculate total modern ARR from services using the same pathway logic
    const totalModernArr = modernServices.reduce((sum, service) => sum + (service.monthly_cost * 12 || 0), 0);
    
    // Calculate EC2 contribution for containerization potential
    const ec2Services = servicesData.filter(service => 
      service.service_name && service.service_name.toLowerCase().includes('ec2')
    );
    const ec2Contribution = ec2Services.reduce((sum, service) => sum + (service.monthly_cost * 12 * 0.3 || 0), 0);
    
    // Group services by modernization pathway using the same logic as PricingCalculatorTab
    const pathwayGroups = {};
    servicesData.forEach(service => {
      const pathway = getModernizationPathway(service.service_code, service.service_name);
      if (!pathwayGroups[pathway]) {
        pathwayGroups[pathway] = { count: 0, arr: 0 };
      }
      pathwayGroups[pathway].count += 1;
      pathwayGroups[pathway].arr += (service.monthly_cost * 12 || 0);
    });
    
    // Get the top modernization pathways (excluding Non Modern) - AFTER pathwayGroups is created
    const modernPathways = Object.entries(pathwayGroups)
      .filter(([pathway]) => pathway !== 'Non Modern')
      .sort(([,a], [,b]) => b.arr - a.arr);
    
    const technicalData = {
      // Real modernization metrics from calculator data
      modernizationIndex: modernizationIndex,
      totalModernArr: totalModernArr,
      pathwayGroups: pathwayGroups,
      modernPathways: modernPathways,
      ec2Contribution: ec2Contribution,
      totalServices: totalServices,
      modernServices: modernServices.length,
      
      // Real technical requirements data
      migrationPhase: techReq.migration_phase || fundRequest.migrationPhase || 'Unknown',
      validationStatus: techReq.status || 'unknown',
      validationReason: techReq.reason || 'No validation data available',
      complianceScore: techReq.result || techReq.compliance_score || 'unknown',
      
      // Real workstream compliance data
      workstreamCompliance: techReq.validation_checks?.workstream_compliance || {},
      allWorkstreamsCompliant: techReq.validation_checks?.all_workstreams_compliant || false,
      scopeChecklistPresent: techReq.scope_checklist_present || false,
      
      // VMware acknowledgement from fund request
      vmwareAcknowledgement: fundRequest.accurateWorkloadAcknowledgement || false,
      
      // Data availability flags
      hasCalculatorData: calculatorData && Object.keys(calculatorData).length > 0,
      calculatorLoading: calculatorLoading
    };

    return (
      <SpaceBetween size="l">
        {/* Translation Notice - Conditional */}
        {(() => {
          const scopeChecklist = attachedFilesData.validations?.scope_checklist || {};
          const translationNote = scopeChecklist.translation_note;
          
          return translationNote && (
            <Alert type="info" header="Translation Notice">
              {translationNote}
            </Alert>
          );
        })()}
        
        {/* Validation Decision */}
        <Container header={<Header variant="h2">Technical Review Summary</Header>}>
          <SpaceBetween size="s">
            <ValidationSummaryBanner
              label="Technical Requirements"
              validationData={techReq}
            />
            {Object.keys(attachedFilesData).length > 0 && (
              <ValidationSummaryBanner
                label="Attached Files"
                validationData={attachedFilesData}
              />
            )}
            {/*
              No AI Modernization banner here (P490545039). Section D results
              are shown in the dedicated Move to AI tab; duplicating the
              verdict here rendered a redundant "Section D compliant" line
              directly under the Technical Requirements banner, which already
              carried its own Section D message. The Technical Review tab icon
              still reflects ai_modernization via the group result.
            */}
          </SpaceBetween>
        </Container>

        {/* Supporting Documents - Moved from Supporting Documents tab */}
        {(() => {
          const validations = attachedFilesData.validations || {};
          
          const getValidationStatusType = (status) => {
            if (status === 'validated' || status === 'present' || status === 'pass') return 'success';
            if (status === 'not_eligible' || status === 'fail' || status === 'missing') return 'error';
            if (status === 'not_required') return 'info';
            return 'warning';
          };

          // Create documents list from validations
          const documentsList = Object.entries(validations)
            .map(([key, validation]) => ({
              id: key,
              name: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
              type: validation.file_name ? 'File' : 'Validation',
              fileName: validation.file_name || '-',
              documentId: validation.document_id || '-',
              status: validation.status || 'unknown',
              parsed: validation.parsed || false,
              rowsParsed: validation.rows_parsed || 0,
              applicable: validation.applicable !== false
            }))
            .sort((a, b) => {
              // Scope Checklist always first
              if (a.id === 'scope_checklist') return -1;
              if (b.id === 'scope_checklist') return 1;
              // Rest alphabetically by name
              return a.name.localeCompare(b.name);
            });

          return (
            <SpaceBetween size="l">
              {/* Document Summaries - Only show documents with status: "present" */}
              {(() => {
                const presentDocs = Object.entries(validations)
                  .filter(([key, validation]) => validation.status === 'present' && validation.summary)
                  .map(([key, validation]) => ({ key, ...validation }));

                if (presentDocs.length === 0) return null;

                return presentDocs.map(doc => {
                  const summary = doc.summary || {};
                  const docName = doc.key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                  
                  const getStatusColor = (status) => {
                    if (status === 'compliant') return 'success';
                    if (status === 'issues_found') return 'warning';
                    return 'info';
                  };

                  return (
                    <Container 
                      key={doc.key}
                      header={
                        <Header variant="h2">
                          {docName} Summary
                        </Header>
                      }
                    >
                      <SpaceBetween size="m">
                        {/* Status and Key Metrics */}
                        <ColumnLayout columns={4} variant="text-grid">
                          <div>
                            <Box variant="awsui-key-label">Status</Box>
                            <StatusIndicator type={getStatusColor(summary.status)}>
                              {summary.status?.replace(/_/g, ' ').toUpperCase() || 'UNKNOWN'}
                            </StatusIndicator>
                          </div>
                          {summary.phase && (
                            <div>
                              <Box variant="awsui-key-label">Phase</Box>
                              <Badge color="blue">{summary.phase}</Badge>
                            </div>
                          )}
                          {summary.compliance_summary && (
                            <div>
                              <Box variant="awsui-key-label">Compliance</Box>
                              <div>{summary.compliance_summary}</div>
                            </div>
                          )}
                          {summary.total_servers !== undefined && (
                            <div>
                              <Box variant="awsui-key-label">Total Servers</Box>
                              <div>{summary.total_servers}</div>
                            </div>
                          )}
                          {/* Validation failures and advisory notes are counted
                              separately (P494819904 reopen). A single "Critical
                              Issues" badge summed assessment failures together
                              with cost/performance advice, so a request with one
                              failing comparison plus zombie + over-utilized
                              servers showed "3" beside an Approval Summary that
                              listed one problem. validation_failures is the
                              count that reconciles with the Approval Summary;
                              advisory_notes never blocks.
                              Falls back to critical_issues for FRs persisted
                              before the split. */}
                          {/* Each cell is a direct <div> sibling, like the metric
                              cells above, rather than a Fragment wrapping the
                              pair. GridColumnLayout does flatten Fragments today
                              (react-keyed-flatten-children recurses into them and
                              sizes the grid off the flattened count), so a
                              Fragment renders correctly — but siblings keep the
                              cells independent of that behaviour. */}
                          {summary.validation_failures !== undefined && (
                            <div>
                              <Box variant="awsui-key-label">Validation Failures</Box>
                              <Badge color={summary.validation_failures > 0 ? 'red' : 'green'}>
                                {summary.validation_failures}
                              </Badge>
                            </div>
                          )}
                          {summary.validation_failures !== undefined && summary.advisory_notes > 0 && (
                            <div>
                              <Box variant="awsui-key-label">Advisory Notes</Box>
                              <Badge color="blue">{summary.advisory_notes}</Badge>
                            </div>
                          )}
                          {summary.validation_failures === undefined && summary.critical_issues !== undefined && (
                            <div>
                              <Box variant="awsui-key-label">Critical Issues</Box>
                              <Badge color={summary.critical_issues > 0 ? 'red' : 'green'}>
                                {summary.critical_issues}
                              </Badge>
                            </div>
                          )}
                          {summary.cost_optimization_potential && (
                            <div>
                              <Box variant="awsui-key-label">Cost Optimization Potential</Box>
                              <div>{summary.cost_optimization_potential}</div>
                            </div>
                          )}
                        </ColumnLayout>

                        {/* Key Findings */}
                        {summary.key_findings && summary.key_findings.length > 0 && (
                          <div>
                            <Box variant="awsui-key-label">Key Findings</Box>
                            <Box variant="p">
                              <ul style={{ marginTop: '0', paddingLeft: '20px', marginBottom: '0' }}>
                                {summary.key_findings.map((finding, index) => {
                                  const cleanFinding = finding.replace(/[\u2705\u274c\u26a0\ufe0f\u2139]/g, '').trim();
                                  const isCostMatch = cleanFinding.startsWith('Cost match:') || cleanFinding.startsWith('Cost mismatch:');
                                  const bestMatch = (techReq.cost_comparison || {}).best_match || {};
                                  const matchedLabel = bestMatch.apfp_source === 'approved_cash_amount' ? 'Approved Cash Amount' : 'Total Project Cost';
                                  const displayText = isCostMatch && bestMatch.apfp_usd != null && bestMatch.scope_interpretation === 'as_labeled'
                                    ? `${cleanFinding} (matched against ${matchedLabel})`
                                    : cleanFinding;
                                  return (
                                    <li key={index} style={{ marginBottom: '4px' }}>
                                      {displayText}
                                    </li>
                                  );
                                })}
                              </ul>
                            </Box>
                          </div>
                        )}

                        {/* Cost Comparison — scope-checklist section only.
                            techReq.cost_comparison is shared (not per-doc), so
                            rendering it inside this per-present-document loop
                            duplicated the identical block under both the VMware
                            and Scope Checklist summaries. The Total Project Cost
                            comparison belongs to the scope checklist validation
                            (P494819904); gate it to that doc so it shows once. */}
                        {doc.key === 'scope_checklist' && (() => {
                          const costCmp = techReq.cost_comparison || {};
                          if (!costCmp.check_applicable) return null;
                          const exceeded = costCmp.variance_usd > costCmp.threshold_usd;
                          const bestMatch = costCmp.best_match || {};
                          const matchedApproved = bestMatch.apfp_source === 'approved_cash_amount';
                          return (
                            <div>
                              <Box variant="awsui-key-label">Total Project Cost Comparison</Box>
                              <ColumnLayout columns={2} variant="text-grid">
                                <div>
                                  <Box variant="awsui-key-label">APFP Total Project Cost ({costCmp.local_currency})</Box>
                                  <div>
                                    {costCmp.apfp_total_project_cost_local?.toLocaleString()} {costCmp.local_currency}
                                    {costCmp.apfp_total_project_cost_usd != null && (
                                      <Box variant="small" color="text-body-secondary"> (≈${costCmp.apfp_total_project_cost_usd?.toLocaleString()} USD)</Box>
                                    )}
                                  </div>
                                </div>
                                {costCmp.apfp_approved_cash_local != null && costCmp.apfp_approved_cash_local > 0 && (
                                  <div>
                                    <Box variant="awsui-key-label">APFP Approved Cash Amount ({costCmp.local_currency}){matchedApproved && ' ✓'}</Box>
                                    <div>
                                      {costCmp.apfp_approved_cash_local?.toLocaleString()} {costCmp.local_currency}
                                      {costCmp.apfp_approved_cash_usd != null && (
                                        <Box variant="small" color="text-body-secondary"> (≈${costCmp.apfp_approved_cash_usd?.toLocaleString()} USD)</Box>
                                      )}
                                    </div>
                                  </div>
                                )}
                                <div>
                                  <Box variant="awsui-key-label">Scope Checklist</Box>
                                  <div>
                                    {(() => {
                                      const raw = costCmp.scope_checklist_cost_raw || '';
                                      if (!raw) return '-';
                                      const numMatch = raw.match(/([\d,.]+)/);
                                      const num = numMatch ? parseFloat(numMatch[1].replace(/,/g, '')) : null;
                                      const currency = costCmp.local_currency || raw.replace(/[\d,.\s]+/g, '').trim() || '';
                                      if (num != null && !isNaN(num)) {
                                        return `${num.toLocaleString()} ${currency}`;
                                      }
                                      return raw;
                                    })()}
                                    {costCmp.scope_checklist_cost_usd != null && (
                                      <Box variant="small" color="text-body-secondary"> (≈${costCmp.scope_checklist_cost_usd?.toLocaleString()} USD)</Box>
                                    )}
                                  </div>
                                </div>
                                <div>
                                  <Box variant="awsui-key-label">Variance (USD)</Box>
                                  <div>
                                    <StatusIndicator type={exceeded ? 'error' : 'success'}>
                                      ${costCmp.variance_usd?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                      {exceeded ? ` — exceeds $${costCmp.threshold_usd?.toLocaleString()} threshold` : ' — within threshold'}
                                    </StatusIndicator>
                                  </div>
                                  {costCmp.best_match?.scope_interpretation === 'as_labeled' && (
                                  <div style={{ marginTop: '4px' }}>
                                    <Box variant="small" color="text-body-secondary">
                                      Matched against: {matchedApproved ? 'Approved Cash Amount' : 'Total Project Cost'}
                                    </Box>
                                  </div>
                                  )}
                                </div>
                              </ColumnLayout>
                            </div>
                          );
                        })()}

                        {/* Recommendations */}
                        {summary.recommendations && summary.recommendations.length > 0 && (
                          <div>
                            <Box variant="awsui-key-label">Recommendations</Box>
                            <Box variant="p">
                              <ul style={{ marginTop: '0', paddingLeft: '20px', marginBottom: '0' }}>
                                {summary.recommendations.map((rec, index) => (
                                  <li key={index} style={{ marginBottom: '4px' }}>
                                    <Badge color="blue">ACTION</Badge> {rec}
                                  </li>
                                ))}
                              </ul>
                            </Box>
                          </div>
                        )}

                        {/* Issues */}
                        {summary.issues && summary.issues.length > 0 && (
                          <Alert type="warning" header="Issues Found">
                            <div style={{ marginTop: '0', paddingLeft: '8px', marginBottom: '0' }}>
                              {summary.issues.map((issue, index) => {
                                const isSubItem = issue.startsWith('•');
                                return isSubItem ? (
                                  <div key={index} style={{ marginBottom: '2px', paddingLeft: '2px' }}>
                                    {issue}
                                  </div>
                                ) : (
                                  <div key={index} style={{ marginBottom: '4px' }}>
                                    {issue}
                                  </div>
                                );
                              })}
                            </div>
                          </Alert>
                        )}

                        {/* File Name */}
                        <div>
                          <Box variant="awsui-key-label">File Name</Box>
                          <div>{doc.file_name || '-'}</div>
                        </div>
                      </SpaceBetween>
                    </Container>
                  );
                });
              })()}

              <Container header={<Header variant="h2">Document Validations</Header>}>
                <Table
                  columnDefinitions={[
                    {
                      id: 'name',
                      header: 'Document Type',
                      cell: item => item.name
                    },
                    {
                      id: 'status',
                      header: 'Status',
                      cell: item => (
                        <StatusIndicator type={getValidationStatusType(item.status)}>
                          {item.status?.toUpperCase() || 'UNKNOWN'}
                        </StatusIndicator>
                      )
                    },
                    {
                      id: 'fileName',
                      header: 'File Name',
                      cell: item => item.fileName || '-'
                    },
                    {
                      id: 'parsed',
                      header: 'Parsed',
                      cell: item => {
                        // Show "-" if document is not applicable
                        if (!item.applicable) {
                          return '-';
                        }
                        return (
                          <StatusIndicator type={item.parsed ? 'success' : 'error'}>
                            {item.parsed ? 'Yes' : 'No'}
                          </StatusIndicator>
                        );
                      }
                    },
                    {
                      id: 'applicable',
                      header: 'Applicable',
                      cell: item => (
                        <StatusIndicator type={item.applicable ? 'success' : 'info'}>
                          {item.applicable ? 'Yes' : 'No'}
                        </StatusIndicator>
                      )
                    }
                  ]}
                  items={documentsList}
                  loadingText="Loading document validations"
                  empty={
                    <Box textAlign="center" color="inherit">
                      <b>No document validations found</b>
                      <Box variant="p" color="inherit">
                        No document validation data is available for this fund request.
                      </Box>
                    </Box>
                  }
                />
              </Container>

              {validations.scope_checklist && validations.scope_checklist.status === 'present' && (
                <Container header={<Header variant="h2">Scope Checklist Details</Header>}>
                  <SpaceBetween size="m">
                    <Alert type="info">
                      The scope checklist information below is displayed for reference purposes only and does not influence any validation or approval outcomes.
                    </Alert>
                    <ColumnLayout columns={2} variant="text-grid">
                      <div>
                        <Box variant="awsui-key-label">File Name</Box>
                        {/* The scope_checklist validation block carries no
                            file_name; the filename is only recorded in
                            downloaded_files, keyed by file_type. */}
                        <div>
                          {validations.scope_checklist.file_name
                            || (request.validation_data?.detailed_validations?.downloaded_files?.download_results || [])
                              .find((f) => f?.file_type === 'scope_checklist')?.file_name
                            || '-'}
                        </div>
                      </div>
                      <div>
                        <Box variant="awsui-key-label">All Workstreams Compliant</Box>
                        <StatusIndicator type={validations.scope_checklist.all_workstreams_compliant ? 'success' : 'error'}>
                          {validations.scope_checklist.all_workstreams_compliant ? 'Yes' : 'No'}
                        </StatusIndicator>
                      </div>
                    </ColumnLayout>

                    {/* Per-phase details (P469995696): full Sections A–D mirror of the
                        source Excel, one tab per phase the backend returned. */}
                    {(() => {
                      const phases = validations.scope_checklist.phases || {};
                      const phaseKeys = ['assess', 'mobilize'].filter((k) => phases[k]);
                      if (phaseKeys.length === 0) return null;
                      return (
                        <Tabs
                          tabs={phaseKeys.map((k) => ({
                            id: `scope-${k}`,
                            label: k === 'assess' ? 'Assess' : 'Mobilize',
                            content: renderScopeChecklistPhase(k, phases[k]),
                          }))}
                        />
                      );
                    })()}
                  </SpaceBetween>
                </Container>
              )}

              {validations.genai_bedrock && validations.genai_bedrock.applicable && (
                <Container header={<Header variant="h2">GenAI Bedrock Validation</Header>}>
                  <ColumnLayout columns={2} variant="text-grid">
                    <div>
                      <Box variant="awsui-key-label">Status</Box>
                      <StatusIndicator type={getValidationStatusType(validations.genai_bedrock.status)}>
                        {validations.genai_bedrock.status?.toUpperCase() || 'UNKNOWN'}
                      </StatusIndicator>
                    </div>
                    <div>
                      <Box variant="awsui-key-label">Applicable</Box>
                      <StatusIndicator type={validations.genai_bedrock.applicable ? 'success' : 'info'}>
                        {validations.genai_bedrock.applicable ? 'Yes' : 'No'}
                      </StatusIndicator>
                    </div>
                  </ColumnLayout>
                </Container>
              )}

              {validations.connect_spreadsheet && validations.connect_spreadsheet.applicable && (
                <Container header={<Header variant="h2">Connect Spreadsheet Validation</Header>}>
                  <ColumnLayout columns={2} variant="text-grid">
                    <div>
                      <Box variant="awsui-key-label">Status</Box>
                      <StatusIndicator type={getValidationStatusType(validations.connect_spreadsheet.status)}>
                        {validations.connect_spreadsheet.status?.toUpperCase() || 'UNKNOWN'}
                      </StatusIndicator>
                    </div>
                    <div>
                      <Box variant="awsui-key-label">Applicable</Box>
                      <StatusIndicator type={validations.connect_spreadsheet.applicable ? 'success' : 'info'}>
                        {validations.connect_spreadsheet.applicable ? 'Yes' : 'No'}
                      </StatusIndicator>
                    </div>
                  </ColumnLayout>
                </Container>
              )}
            </SpaceBetween>
          );
        })()}




      </SpaceBetween>
    );
  };

  // POC-specific claims view. POC persists its per-check audit trail in
  // `validation_results_json` (a JSON string: { decision, reasons,
  // attached_files: { <doc_type>: {...} }, checks: { four_j_po, claim_amount }
  // }) written by poc_validation_agent/tools/make_decision.py — NOT in the
  // MAP-shaped `detailed_validations` envelope that renderClaims() reads. So
  // POC needs its own renderer (mirrors the Rubicon precedent of dedicated
  // render* functions) that reads POC's real fields and shows nothing MAP-
  // specific (no Modernization ARR / VMware / claim-milestones — none of which
  // apply to a POC cash claim). Reviewer-facing decision + key findings live on
  // the Approval Summary tab; this tab surfaces the CST document details and
  // the 4J / claim-amount check results.
  const renderPocClaims = () => {
    // s3Service surfaces validation_results_json as-persisted (string). Parse
    // defensively — an older row or a partial write must degrade to an
    // informational empty state, never throw.
    const results = parsePocResults(request.validation_results_json);
    const attached = results.attached_files;
    const checks = results.checks;
    const reasons = results.reasons;
    const cst = attached.customer_sign_off || null;
    const fourJ = checks.four_j_po || null;
    const claimAmount = checks.claim_amount || null;

    // Map an engine status/decision string to a Cloudscape StatusIndicator
    // type — used by the Claim Amount and 4J check cards below. POC entries use:
    // validated/ok/pass, reject/fail, and flag_for_manual_review/attention/warning.
    const indicatorType = (s) => {
      const v = (s || '').toString().toLowerCase();
      if (['validated', 'ok', 'pass', 'present'].includes(v)) return 'success';
      if (['reject', 'fail', 'failed', 'not_found', 'error'].includes(v)) return 'error';
      return 'warning';
    };

    // No customer_sign_off entry in attached_files. This is NOT always a
    // "missing document" — the backend also lands here when it COULDN'T inspect
    // the attachments (batpur@ CR-295319668). Disambiguate off the persisted
    // reasons before concluding the CST is missing:
    //   - AttachmentEngineUnavailable (flag_for_manual_review): the file-
    //     processing engine failed, so the CST could not be validated at all —
    //     an inability to inspect, not a definitive absence. Show manual-review.
    //   - Corrupt (reject): a CST-looking file was quarantined/unreadable —
    //     present but cannot be opened, not simply missing.
    //   - otherwise: genuinely no CST classified -> the missing-document reject.
    if (!cst) {
      const noCst = resolveNoCstState(reasons);
      return (
        <Container>
          <Alert type={noCst.alertType} header={noCst.header}>
            {noCst.message}
          </Alert>
        </Container>
      );
    }

    const cstFindings = cst.findings || [];
    // Validation RESULT (policy pass/fail) is derived from decision + findings,
    // NOT the processing status — status:"validated" only means extraction +
    // the validator completed, even on a reject-with-error-findings. Green only
    // when policy explicitly passed with no error finding (batpur@ rev 6).
    const cstResult = resolvePocCstResult(cst);

    // Grouped view (source of truth: poc_sop.yaml validation_taxonomy):
    //   - checkRows  : fields the SOP validates -> pass/fail/review/N-A, each
    //     labeled with its SOP section. Status is driven by the agent's
    //     findings, so it degrades correctly before/after the backend
    //     validation CRs deploy (no finding + value present -> Passed; no
    //     finding + blank -> neutral "—", never a false green).
    //   - infoRows   : fields shown for reviewer context; SOP defines no check.
    //   - otherRows  : extracted fields neither list accounts for -> quiet drift
    //     note so a new field is never silently dropped without a reviewer
    //     noticing it may need an SOP check.
    // Pass the row's persisted SOP version so a green "Passed"/"N/A" is only
    // shown when the check is proven to have run for this row; older/unversioned
    // rows render "Present, not validated" instead of a misleading green
    // (batpur@ CR-298110147).
    const checkRows = resolveCstFieldChecks(cst, results.sop_version);
    const infoRows = resolveCstInfoFields(cst);
    const otherRows = collectOtherCstFields(cst);

    // Map the per-field resolver type to a Cloudscape StatusIndicator type.
    // 'na'/'neutral'/'unproven' have no dedicated indicator colour -> render as
    // plain secondary text (unproven is not a pass and not a failure).
    const fieldIndicator = (row) => {
      if (row.type === 'success') return <StatusIndicator type="success">{row.statusLabel}</StatusIndicator>;
      if (row.type === 'error') return <StatusIndicator type="error">{row.statusLabel}</StatusIndicator>;
      if (row.type === 'warning') return <StatusIndicator type="warning">{row.statusLabel}</StatusIndicator>;
      // na / neutral / unproven
      return <Box color="text-body-secondary">{row.statusLabel}</Box>;
    };

    return (
      <SpaceBetween size="l">
        {/* CST document header — file + overall validation result. */}
        <Container header={<Header variant="h2">Customer Sign-Off (CST)</Header>}>
          <ColumnLayout columns={2} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">File Name</Box>
              <div>{cst.file_name || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Validation Result</Box>
              <StatusIndicator type={cstResult.type}>
                {cstResult.label}
              </StatusIndicator>
            </div>
            <div>
              <Box variant="awsui-key-label">Processing Status</Box>
              <div>{(cst.status || 'unknown').replace(/_/g, ' ')}</div>
            </div>
          </ColumnLayout>
        </Container>

        {/* Validation Checks — fields the SOP requires, each with its section
            reference and a pass/fail/review/N-A indicator. */}
        <Container header={
          <Header variant="h2" description="Fields validated per the POC SOP. Green = passed; red = failed; orange = manual review; “—” = not filled and not yet evaluated.">
            Validation Checks
          </Header>
        }>
          <ColumnLayout columns={1} variant="text-grid" borders="horizontal">
            {checkRows.map((row) => (
              <ColumnLayout key={row.key} columns={3} variant="text-grid">
                <div>
                  <Box variant="awsui-key-label">{row.label}</Box>
                  <Box variant="small" color="text-body-secondary">{row.sop}</Box>
                </div>
                <div>{fieldIndicator(row)}</div>
                <div>
                  <Box color={row.type === 'na' ? 'text-body-secondary' : undefined}>{row.value}</Box>
                  {row.message && (
                    <Box variant="small" color="text-body-secondary">{row.message}</Box>
                  )}
                </div>
              </ColumnLayout>
            ))}
          </ColumnLayout>
        </Container>

        {/* Information — shown for reviewer context; the SOP defines no
            pass/fail on these fields. */}
        <Container header={
          <Header variant="h2" description="Shown for context. The SOP defines no validation check on these fields.">
            Information (not required per SOP)
          </Header>
        }>
          <ColumnLayout columns={3} variant="text-grid">
            {infoRows.map((row) => (
              <div key={row.key}>
                <Box variant="awsui-key-label">{row.label}</Box>
                <div>{row.value}</div>
              </div>
            ))}
          </ColumnLayout>
        </Container>

        {/* Drift guard — extracted fields neither validated nor a known info
            field. Quiet gray note; only rendered when such fields exist. */}
        {otherRows.length > 0 && (
          <Container header={<Header variant="h2">Other extracted fields</Header>}>
            <Box variant="small" color="text-body-secondary" padding={{ bottom: 'xs' }}>
              Extracted from the CST but not covered by any SOP check. Confirm
              whether a validation is needed.
            </Box>
            <ColumnLayout columns={2} variant="text-grid">
              {otherRows.map((row) => (
                <div key={row.key}>
                  <Box variant="awsui-key-label" color="text-body-secondary">{row.key}</Box>
                  <Box color="text-body-secondary">{row.value}</Box>
                </div>
              ))}
            </ColumnLayout>
          </Container>
        )}

        {/* CST findings, when the validator raised any */}
        {cstFindings.length > 0 && (
          <Container header={<Header variant="h2">CST Findings</Header>}>
            <ul style={{ marginTop: '4px', paddingLeft: '20px', marginBottom: '0' }}>
              {cstFindings.map((f, i) => (
                <li key={i} style={{ marginBottom: '4px' }}>
                  <Badge color={f.severity === 'error' ? 'red' : 'grey'}>
                    {(f.severity || 'info').toUpperCase()}
                  </Badge>{' '}
                  {f.code ? `${f.code}: ` : ''}{f.message || ''}
                </li>
              ))}
            </ul>
          </Container>
        )}

        {/* Claim amount check */}
        {claimAmount && (
          <Container header={<Header variant="h2">Claim Amount</Header>}>
            <ColumnLayout columns={2} variant="text-grid">
              <div>
                <Box variant="awsui-key-label">Status</Box>
                <StatusIndicator type={indicatorType(claimAmount.status)}>
                  {(claimAmount.status || 'unknown').replace(/_/g, ' ')}
                </StatusIndicator>
              </div>
              <div>
                <Box variant="awsui-key-label">Detail</Box>
                <div>{claimAmount.message || claimAmount.code || '-'}</div>
              </div>
            </ColumnLayout>
          </Container>
        )}

        {/* 4J AWS India approval check (only when applicable to this PO) */}
        {fourJ && fourJ.applicable !== false && (
          <Container header={<Header variant="h2">4J — AWS India Approval</Header>}>
            <ColumnLayout columns={2} variant="text-grid">
              <div>
                <Box variant="awsui-key-label">Status</Box>
                <StatusIndicator type={indicatorType(fourJ.status)}>
                  {(fourJ.status || 'unknown').replace(/_/g, ' ')}
                </StatusIndicator>
              </div>
              <div>
                <Box variant="awsui-key-label">Detail</Box>
                <div>{fourJ.message || fourJ.code || '-'}</div>
              </div>
            </ColumnLayout>
          </Container>
        )}
      </SpaceBetween>
    );
  };

  const renderClaims = () => {
    const validations = request.validation_data?.detailed_validations?.attached_files?.validations || {};
    const claimsData = validations.customer_sign_off || {};
    const scopeChecklist = validations.scope_checklist || {};
    const fundRequest = request.validation_data?.complete_api_data?.fundRequest || {};
    const migrationPhase = fundRequest.migrationPhase || '';
    
    // Parse modernizationArrUsdAmount: NULL or missing → "No"; string
    // value like "440448.00 USD" → "Yes ($440,448)". The field comes from
    // APFP and may be null (not declared) or a "<amount> USD" string.
    const modArrRaw = fundRequest.modernizationArrUsdAmount;
    const modArrUsd = (() => {
      if (!modArrRaw || typeof modArrRaw !== 'string') return 0;
      const num = parseFloat(modArrRaw.replace(/[$,]/g, '').replace(/\s*USD\s*$/i, '').trim());
      return Number.isFinite(num) ? num : 0;
    })();
    const hasModArr = modArrUsd > 0;

    // VMware breakdown — reads from incentives.vmware. The block exists for
    // all FRs that ever had VMware data declared, but most are zero-count
    // (FR didn't declare VMware migration). Render only when there are
    // actual VMs, regardless of `eligible` flag — reviewers want to see
    // even ineligible-but-non-zero cases to investigate the discrepancy.
    const vmwareInc = fundRequest.incentives?.vmware || {};
    const vmCount = Number(vmwareInc.numberOfVmwareToMigrate || 0);
    const totalWorkloads = Number(vmwareInc.totalNumberOfWorkloadsInUse || 0);
    const hasVmware = vmCount > 0;
    const vmwareEligible = vmwareInc.eligible === true;

    // Parse "127018.10 USD" string → number, then format for display.
    const parseUsdString = (s) => {
      if (!s || typeof s !== 'string') return 0;
      const num = parseFloat(s.replace(/[$,]/g, '').replace(/\s*USD\s*$/i, '').trim());
      return Number.isFinite(num) ? num : 0;
    };
    const vmwareIncentiveUsd = parseUsdString(vmwareInc.usdAmount);
    const vmwareBonusUsd = parseUsdString(vmwareInc.bonusUsdAmount);

    // Multi-claim plans — fundingDetails.claimPlans is an object keyed by
    // claim_id. Sort by priority ascending so claim 1 (priority=0) renders
    // before claim 2 (priority=1). Most FRs have a single plan; a few
    // Mobilize FRs have 2-3 (cash + bonus, or milestone 1/2/3).
    const claimPlansMap = fundRequest.fundingDetails?.claimPlans || {};
    const claimPlansList = Object.values(claimPlansMap)
      .sort((a, b) => (a.priority ?? 999) - (b.priority ?? 999));
    const hasMultipleClaims = claimPlansList.length > 1;

    const getStatusType = (status) => {
      if (status === 'present') return 'success';
      if (status === 'not_found') return 'error';
      return 'warning';
    };

    // Handle not found case. CST is a required cash-claim document, so a
    // missing CST is a hard reject — not a soft warning. Match the red
    // ❌ that Technical Review's banner already shows for the same state,
    // and the file's own getStatusType('not_found') === 'error' helper
    // (which existed but wasn't wired up here).
    if (claimsData.status === 'not_found') {
      return (
        <Container>
          <Alert type="error" header="Customer Sign-Off Document Not Found">
            {claimsData.reason || 'Customer sign-off document not found or download failed'}
          </Alert>
        </Container>
      );
    }

    // Handle parsing failed case
    if (claimsData.status === 'present' && !claimsData.parsed) {
      return (
        <SpaceBetween size="l">
          <Container header={<Header variant="h2">Document Information</Header>}>
            <ColumnLayout columns={2} variant="text-grid">
              <div>
                <Box variant="awsui-key-label">File Name</Box>
                <div>{claimsData.file_name || '-'}</div>
              </div>
              <div>
                <Box variant="awsui-key-label">Status</Box>
                <StatusIndicator type="error">Parsing Failed</StatusIndicator>
              </div>
            </ColumnLayout>
          </Container>
          <Alert type="error" header="Document Parsing Failed">
            {claimsData.parse_error || claimsData.reason || 'Failed to parse customer sign-off document'}
          </Alert>
        </SpaceBetween>
      );
    }

    // Handle successful parsing
    const rawData = claimsData.data || {};
    const data = rawData.customer_sign_off_form_data || rawData;
    const crossValidation = rawData.cst_validation_with_scope_checklist || null;

    // Notes surfaced inline so reviewers don't open the files to read them
    // (P465854215 item 2). CST notes come off the parsed form (`data.notes`);
    // scope-checklist notes are the current phase's `notes_for_approvers`,
    // stored under scope_checklist.phases.<phase>. Both are best-effort —
    // absent/blank on most FRs, so each renders only when present.
    const cstNotes = (typeof data.notes === 'string' ? data.notes : '').trim();
    const scopeChecklistNotes = (() => {
      const phaseKey = migrationPhase.toLowerCase();
      const phaseBlock = scopeChecklist.phases?.[phaseKey] || {};
      const n = phaseBlock.notes_for_approvers;
      return (typeof n === 'string' ? n : '').trim();
    })();

    const claimsRec = claimsData.claims_recommendation;

    const getDecisionColor = (d) => {
      if (d === 'RECOMMEND_APPROVAL') return 'green';
      if (d === 'RECOMMEND_REJECTION') return 'red';
      return 'blue';
    };
    
    return (
      <SpaceBetween size="l">
        {/* Scope Checklist Warning */}
        {claimsData.scope_checklist_warning && (
          <Alert type="warning" header="Scope Checklist Issue">
            {claimsData.scope_checklist_warning}
          </Alert>
        )}

        {/* CST Format Warning */}
        {claimsData.cst_format_warning && (
          <Alert type="warning" header="Document Format Issue">
            {claimsData.cst_format_warning}
          </Alert>
        )}

        {/* Claims Approver Recommendation */}
        {claimsRec && (
          <Container header={<Header variant="h2">Claims Recommendation</Header>}>
            <SpaceBetween size="m">
              <ColumnLayout columns={2} variant="text-grid">
                <div>
                  <Box variant="awsui-key-label">Decision</Box>
                  <Badge color={getDecisionColor(claimsRec.decision)}>
                    {claimsRec.decision?.replace(/_/g, ' ') || 'PENDING'}
                  </Badge>
                </div>
                <div>
                  <Box variant="awsui-key-label">Reason</Box>
                  <div>{claimsRec.reason || '-'}</div>
                </div>
              </ColumnLayout>
              {claimsRec.key_findings?.length > 0 && (
                <div>
                  <Box variant="awsui-key-label">Key Findings</Box>
                  <ul style={{ marginTop: '4px', paddingLeft: '20px', marginBottom: '0' }}>
                    {claimsRec.key_findings.map((f, i) => (
                      <li key={i} style={{ marginBottom: '4px' }}>{f}</li>
                    ))}
                  </ul>
                </div>
              )}
              {claimsRec.required_actions?.length > 0 && (
                <div>
                  <Box variant="awsui-key-label">Required Actions</Box>
                  <ul style={{ marginTop: '4px', paddingLeft: '20px', marginBottom: '0' }}>
                    {claimsRec.required_actions.map((a, i) => (
                      <li key={i} style={{ marginBottom: '4px' }}>
                        <Badge color="blue">ACTION REQUIRED</Badge> {a}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </SpaceBetween>
          </Container>
        )}

        <Container header={<Header variant="h2">Claim Information</Header>}>
          <ColumnLayout columns={2} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">File Name</Box>
              <div>{claimsData.file_name || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Project Name</Box>
              <div>{data.project_name || '-'}</div>
            </div>
            {fundRequest.isPublicSector && (
            <div>
              <Box variant="awsui-key-label">Public Sector</Box>
              <Badge color="blue">YES</Badge>
            </div>
            )}
            <div>
              <Box variant="awsui-key-label">Modernization ARR</Box>
              {hasModArr ? (
                <Badge color="blue">{`YES — $${modArrUsd.toLocaleString('en-US', { maximumFractionDigits: 0 })}`}</Badge>
              ) : (
                <Badge color="grey">NO</Badge>
              )}
            </div>
            <div>
              <Box variant="awsui-key-label">Claim Fund Request ID</Box>
              <div>{data.claim_fund_request_id || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Start Date</Box>
              <div>{data.start_date || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">End Date</Box>
              <div>{data.end_date || '-'}</div>
            </div>
          </ColumnLayout>
        </Container>

        {hasVmware && (
          <Container header={<Header variant="h2">VMware Migration</Header>}>
            <SpaceBetween size="m">
              {!vmwareEligible && (
                <Alert type="warning" header="VMware ineligible">
                  APFP marked this FR's VMware incentive as ineligible despite{' '}
                  {vmCount.toLocaleString('en-US')} VM(s) declared. Investigate before
                  actioning.
                </Alert>
              )}
              <ColumnLayout columns={3} variant="text-grid">
                <div>
                  <Box variant="awsui-key-label">VMs to Migrate</Box>
                  <div>{vmCount.toLocaleString('en-US')}</div>
                </div>
                <div>
                  <Box variant="awsui-key-label">Total Workloads in Use</Box>
                  <div>{totalWorkloads.toLocaleString('en-US')}</div>
                </div>
                <div>
                  <Box variant="awsui-key-label">Eligible</Box>
                  <StatusIndicator type={vmwareEligible ? 'success' : 'stopped'}>
                    {vmwareEligible ? 'YES' : 'NO'}
                  </StatusIndicator>
                </div>
                <div>
                  <Box variant="awsui-key-label">VMware Incentive</Box>
                  <div>{vmwareIncentiveUsd > 0 ? `$${vmwareIncentiveUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '$0.00'}</div>
                </div>
                <div>
                  <Box variant="awsui-key-label">VMware Bonus ($200/VM)</Box>
                  <div>{vmwareBonusUsd > 0 ? `$${vmwareBonusUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '$0.00'}</div>
                </div>
              </ColumnLayout>
            </SpaceBetween>
          </Container>
        )}

        {hasMultipleClaims && (
          <Container header={
            <Header
              variant="h2"
              counter={`(${claimPlansList.length})`}
            >
              Claim Milestones
            </Header>
          }>
            <SpaceBetween size="m">
              <Box variant="p" color="text-body-secondary">
                This fund request has multiple claim plans. Review each milestone independently.
              </Box>
              {claimPlansList.map((planWrapper, idx) => {
                const plan = planWrapper;
                const claimType = plan.claimType || '?';
                const claimTypeLabel = claimType === 'BonusCashClaimPlan' ? 'Bonus' : 'Cash';
                const claimTypeColor = claimType === 'BonusCashClaimPlan' ? 'green' : 'blue';
                const claimId = plan.claimId || '?';
                const claimNumber = idx + 1;
                const totalClaims = claimPlansList.length;
                const isFinalClaim = claimNumber === totalClaims;
                const dueDate = plan.dueDate ? new Date(plan.dueDate).toLocaleDateString('en-US') : '-';
                const localAmount = plan.localAmount || '';
                const realizedRevenue = parseUsdString(plan.realizedRevenueUsdAmount);
                const bonusProps = plan.properties?.vmwareBonus || null;
                const bonusVmCount = bonusProps?.numberOfVmwareToMigrate || null;
                const bonusUsd = bonusProps ? parseUsdString(bonusProps.bonusUsdAmount) : 0;

                return (
                  <Container
                    key={claimId}
                    header={
                      <Header
                        variant="h3"
                        description={`Priority ${plan.priority ?? '-'} of ${totalClaims}`}
                        actions={
                          <SpaceBetween direction="horizontal" size="xs">
                            <Badge color={claimTypeColor}>{claimTypeLabel}</Badge>
                            {isFinalClaim && <Badge color="grey">FINAL</Badge>}
                          </SpaceBetween>
                        }
                      >
                        Claim {claimNumber} of {totalClaims}
                      </Header>
                    }
                  >
                    <ColumnLayout columns={3} variant="text-grid">
                      <div>
                        <Box variant="awsui-key-label">Claim ID</Box>
                        <div style={{ fontFamily: 'monospace', fontSize: '11px', wordBreak: 'break-all' }}>
                          {claimId}
                        </div>
                      </div>
                      <div>
                        <Box variant="awsui-key-label">Due Date</Box>
                        <div>{dueDate}</div>
                      </div>
                      <div>
                        <Box variant="awsui-key-label">Local Amount</Box>
                        <div>{localAmount || '-'}</div>
                      </div>
                      <div>
                        <Box variant="awsui-key-label">Realized Revenue (USD)</Box>
                        <div>
                          {realizedRevenue > 0
                            ? `$${realizedRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                            : '$0.00'}
                        </div>
                      </div>
                      {bonusVmCount !== null && (
                        <>
                          <div>
                            <Box variant="awsui-key-label">VMware VMs (Bonus)</Box>
                            <div>{Number(bonusVmCount).toLocaleString('en-US')}</div>
                          </div>
                          <div>
                            <Box variant="awsui-key-label">VMware Bonus (USD)</Box>
                            <div>
                              {bonusUsd > 0
                                ? `$${bonusUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                : '$0.00'}
                            </div>
                          </div>
                        </>
                      )}
                    </ColumnLayout>
                  </Container>
                );
              })}
            </SpaceBetween>
          </Container>
        )}

        {/* Cross-Validation: CST vs Scope Checklist. Replaces the standalone
            Assess/Mobilize Phase Workstreams section (removed) — the workstream
            YES/NO data it showed is the CST Sign-Off column here, so this table
            supersedes it. Phase label in the header tells the reviewer which
            phase's workstreams these are. Render every workstream (matched +
            mismatched), mismatches first, so a workstream a reviewer expects
            isn't silently filtered out. Falls back to mismatches-only when
            older records lack the `matched` array. */}
        {crossValidation && (
          <Container header={
            <Header
              variant="h2"
              description={migrationPhase ? `${migrationPhase} phase workstreams` : undefined}
            >
              CST vs Scope Checklist Cross-Validation
            </Header>
          }>
            {(() => {
              const mismatches = crossValidation.mismatches || [];
              const matched = crossValidation.matched || [];
              const allRows = [
                ...mismatches.map(m => ({ ...m, _isMatch: false })),
                ...matched.map(m => ({ ...m, _isMatch: true })),
              ];

              return (
                <SpaceBetween size="m">
                  {crossValidation.scope_checklist_validation_passed ? (
                    <StatusIndicator type="success">
                      All {crossValidation.matched_count} workstreams match between CST and scope checklist
                    </StatusIndicator>
                  ) : (
                    <Alert type="warning" header={`${crossValidation.mismatch_count} mismatch(es) found between CST and scope checklist`}>
                      Mismatched workstreams are shown first. Matched workstreams follow for reference.
                    </Alert>
                  )}
                  {allRows.length > 0 && (
                    <Table
                      columnDefinitions={[
                        { id: 'workstream', header: 'Workstream', cell: item => item.workstream },
                        { id: 'scopeChecklist', header: 'Scope Checklist', cell: item => (
                          <Badge color={item.scope_checklist === 'yes' ? 'green' : 'grey'}>
                            {item.scope_checklist === 'yes' ? 'In Scope' : 'Not In Scope'}
                          </Badge>
                        )},
                        { id: 'signOffForm', header: 'CST Sign-Off', cell: item => (
                          <Badge color={item.sign_off_form === 'YES' ? 'green' : 'grey'}>
                            {item.sign_off_form}
                          </Badge>
                        )},
                        { id: 'result', header: 'Result', cell: item => (
                          <StatusIndicator type={item._isMatch ? 'success' : 'warning'}>
                            {item._isMatch ? 'Match' : 'Mismatch'}
                          </StatusIndicator>
                        )},
                      ]}
                      items={allRows}
                      variant="embedded"
                    />
                  )}
                  {/* Notes from the source files (P465854215 item 2), so the
                      reviewer doesn't have to open the Scope Checklist / CST to
                      read them. Each renders only when the file carried a note. */}
                  {(scopeChecklistNotes || cstNotes) && (
                    <SpaceBetween size="s">
                      {scopeChecklistNotes && (
                        <div>
                          <Box variant="awsui-key-label">Scope Checklist Notes</Box>
                          <div style={{ whiteSpace: 'pre-wrap' }}>{scopeChecklistNotes}</div>
                        </div>
                      )}
                      {cstNotes && (
                        <div>
                          <Box variant="awsui-key-label">CST Notes</Box>
                          <div style={{ whiteSpace: 'pre-wrap' }}>{cstNotes}</div>
                        </div>
                      )}
                    </SpaceBetween>
                  )}
                </SpaceBetween>
              );
            })()}
          </Container>
        )}

        {/* Acknowledgments and Confirmations */}
        <Container header={<Header variant="h2">Acknowledgments and Confirmations</Header>}>
          <ColumnLayout columns={2} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Partner Acknowledgment Completed</Box>
              <StatusIndicator type={data.partner_acknowledgment_completed === 'YES' ? 'success' : 'stopped'}>
                {data.partner_acknowledgment_completed || '-'}
              </StatusIndicator>
            </div>
            <div>
              <Box variant="awsui-key-label">Customer Authorization Signed</Box>
              <StatusIndicator type={data.customer_authorization_signed?.includes('YES') ? 'success' : 'stopped'}>
                {data.customer_authorization_signed || '-'}
              </StatusIndicator>
            </div>
            <div>
              <Box variant="awsui-key-label">Customer Acknowledgement</Box>
              <StatusIndicator type={data.customer_authorization_checkbox_confirmed === 'YES' ? 'success' : 'stopped'}>
                {data.customer_authorization_checkbox_confirmed || '-'}
              </StatusIndicator>
            </div>
            {migrationPhase === 'Mobilize' && (
            <div>
              <Box variant="awsui-key-label">Migration to AWS Completion Checked</Box>
              <StatusIndicator type={data.migration_to_aws_completion_checked === 'YES' ? 'success' : 'stopped'}>
                {data.migration_to_aws_completion_checked || '-'}
              </StatusIndicator>
            </div>
            )}
            {migrationPhase === 'Mobilize' && (
            <div>
              <Box variant="awsui-key-label">Modernization Pathways Confirmation Checked</Box>
              <StatusIndicator type={data.modernization_pathways_confirmation_checked === 'YES' ? 'success' : 'stopped'}>
                {data.modernization_pathways_confirmation_checked || '-'}
              </StatusIndicator>
            </div>
            )}
          </ColumnLayout>
        </Container>
      </SpaceBetween>
    );
  };

  const renderPricingCalculator = () => {
    const pricingData = request.validation_data?.detailed_validations?.pricing_calculator || {};
    const modernizationData = request.validation_data?.detailed_validations?.modernization_pathways;
    
    return (
      <SpaceBetween size="l">
        {/* Validation Decision */}
        <Container header={<Header variant="h2">Pricing Calculator Summary</Header>}>
          <SpaceBetween size="s">
            {/* The blind page shows no pricing verdict: the reason states
                MAP's recommendation, for example "recommend rejection". */}
            {!blindPending && (
              <ValidationSummaryBanner
                label="Pricing Calculator"
                validationData={pricingData}
              />
            )}
            {/* The blind page shows it only when the server lists it. */}
            {modernizationData && modernizationData.status !== 'not_applicable' && (!blindPending || blindListed('modernization_pathways')) && (
              <ValidationSummaryBanner
                label="Modernization Pathways"
                validationData={modernizationData}
              />
            )}
            {/* Structured advisory warnings (ticket P472176838) — renders only
                when the backend emits a non-empty pricing_calculator.warnings.
                The blind page shows only the ARR reconciliation: each warning
                is a review flag (batpur, 2026-11-02). */}
            <PricingCalculatorWarnings
              pricingData={blindPending ? { arr_reconciliation: pricingData.arr_reconciliation } : pricingData}
            />
            {/* Live pricing-currency check (P411867476/P449272857) -
                renders only when the backend live_check saw a reprice or
                a reconfiguration need. The blind page leaves out its advice. */}
            <LivePricingCheck pricingData={pricingData} withholdVerdict={blindPending} />
          </SpaceBetween>
        </Container>
        
        {/* The tab reads approval_summary's key findings, which the blind page
            never shows. The blind page names the calculator MAP validated, which
            the tab opens and reads its currency from. */}
        <PricingCalculatorTab
          request={blindPending ? { ...request, approval_summary: undefined } : request}
          validatedCalculatorUrl={blindPending ? pricingData.calculator_url : undefined}
          withholdVerdict={blindPending}
        />
      </SpaceBetween>
    );
  };

  // ── MDF-specific detail tabs ──────────────────────────────────────────────
  // MDF persists its per-check results in `validation_results_json` (a JSON
  // string of { per_check_results: [...], decision, rejection_code }), NOT in
  // the MAP-shaped `detailed_validations` envelope. These helpers read that
  // shape and render MDF's own view (cost-share, entity linkage, line-item
  // eligibility) instead of MAP's tabs, which read a different envelope and
  // would show blank/misleading fields for an MDF FR.

  // Parse validation_results_json once into a { step_key: result } map. Mirrors
  // the WMP parse pattern already used in renderValidationSummary. Returns {}
  // on any parse failure so a renderer degrades to "-" rather than throwing.
  const getMdfChecks = () => {
    let parsed = request.validation_results_json;
    try {
      if (typeof parsed === 'string') parsed = JSON.parse(parsed);
    } catch (e) {
      return {};
    }
    const perCheck = Array.isArray(parsed)
      ? parsed
      : (parsed?.per_check_results || []);
    const byStep = {};
    perCheck.forEach((c) => { if (c && c.validation) byStep[c.validation] = c; });
    return byStep;
  };

  // MDF steps emit pass | reject | escalate; map to a Cloudscape StatusIndicator
  // type (getTabIcon understands pass/fail/needs_review, so the tab-label icon
  // uses those synonyms — see the tabs array).
  const mdfStatusType = (s) => {
    const v = (s || '').toLowerCase();
    if (v === 'pass') return 'success';
    if (v === 'reject') return 'error';
    if (v === 'escalate' || v === 'needs_review') return 'warning';
    return 'pending';
  };

  // Map an MDF verdict (pass|reject|escalate) to a token getTabIcon recognizes
  // (getTabIcon knows pass/fail/needs_review, not reject/escalate).
  const mdfStatusTypeToIcon = (s) => {
    const v = (s || '').toLowerCase();
    if (v === 'pass') return 'pass';
    if (v === 'reject') return 'fail';
    if (v === 'escalate') return 'needs_review';
    return 'needs_review';
  };

  // Money formatter that tolerates a missing amount/currency.
  const mdfMoney = (amount, currency) => {
    if (amount === null || amount === undefined || amount === '') return '-';
    const n = Number(amount);
    if (Number.isNaN(n)) return String(amount);
    const formatted = n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return currency ? `${formatted} ${currency}` : formatted;
  };

  // Display an FX rate to a readable precision — the persisted rate is a raw
  // float (e.g. 0.19430522021480312); show 6 significant-ish decimals, trimming
  // trailing zeros, rather than the full 17-digit value (mhamuzn).
  const mdfFxRate = (rate) => {
    const n = Number(rate);
    if (Number.isNaN(n)) return String(rate);
    return parseFloat(n.toFixed(6)).toString();
  };

  // Map a per-file processing outcome to a status indicator. A validated
  // invoice/4J is success; an FR-section non-invoice skip is neutral (info,
  // not an error — it's a benign skip, not a failure); a parse_error /
  // not_evaluated is a warning a reviewer should see.
  const mdfAttachmentStatus = (status) => {
    switch (status) {
      case 'validated':
        return { type: 'success', label: 'Validated' };
      case 'skipped_not_invoice':
        return { type: 'info', label: 'Skipped — not an invoice' };
      case 'parse_error':
        return { type: 'warning', label: 'Could not parse' };
      case 'not_evaluated':
        return { type: 'warning', label: 'Not evaluated' };
      default:
        return { type: 'pending', label: status || '-' };
    }
  };

  // Human label for a resolved doc_type.
  const mdfDocTypeLabel = (dt) => {
    switch (dt) {
      case 'third_party_invoice':
        return 'Third-party invoice';
      case 'four_j_approval_email':
        return '4J approval email';
      case 'unknown':
      case null:
      case undefined:
        return 'Unrecognized';
      default:
        return dt;
    }
  };

  // Reusable per-section attachment table.
  const renderMdfAttachmentSection = (title, files, emptyText) => (
    <Container header={<Header variant="h2">{title}</Header>}>
      {files && files.length > 0 ? (
        <Table
          variant="embedded"
          items={files}
          columnDefinitions={[
            {
              id: 'file_name',
              header: 'File',
              cell: (item) => item.file_name || '-',
            },
            {
              id: 'doc_type',
              header: 'Document type',
              cell: (item) => mdfDocTypeLabel(item.doc_type),
            },
            {
              id: 'status',
              header: 'Processing',
              cell: (item) => {
                const s = mdfAttachmentStatus(item.status);
                return <StatusIndicator type={s.type}>{s.label}</StatusIndicator>;
              },
            },
          ]}
          empty={emptyText}
        />
      ) : (
        <Box variant="small" color="text-body-secondary">{emptyText}</Box>
      )}
    </Container>
  );

  // Two-box attachment view for the MDF Claims tab: Cash Claim attachments and
  // Fund Request attachments render separately (partners sometimes file cash-claim
  // invoices in the FR section and vice versa). The agent reads both; FR-section
  // files that aren't invoices/4J approvals are skipped (shown as such). Reads the
  // per-section records the agent persists under
  // validation_data.detailed_validations.attached_files; falls back to the Step-3
  // aggregate counts for rows validated before that data was persisted.
  const renderMdfAttachmentsBySection = (step3) => {
    const attached = request.validation_data?.detailed_validations?.attached_files || {};
    const cashClaim = attached.cash_claim_attachments;
    const fundRequest = attached.fund_request_attachments;
    const hasSectionData = Array.isArray(cashClaim) || Array.isArray(fundRequest);

    if (!hasSectionData) {
      // Pre-section rows: no per-file section data persisted. Show the aggregate
      // Step-3 counts so the box isn't empty.
      return (
        <Container header={<Header variant="h2">Invoices Attached</Header>}>
          <ColumnLayout columns={2} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Third-Party Invoices</Box>
              <div>{step3.invoice_count ?? '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Total Attachments</Box>
              <div>{step3.attachment_count ?? '-'}</div>
            </div>
          </ColumnLayout>
        </Container>
      );
    }

    const skipped = attached.fr_section_skipped_count || 0;
    return (
      <SpaceBetween size="l">
        {renderMdfAttachmentSection(
          'Cash Claim Attachments',
          cashClaim || [],
          'No files were attached to the cash claim.',
        )}
        {renderMdfAttachmentSection(
          'Fund Request Attachments',
          fundRequest || [],
          'No files were attached to the fund request.',
        )}
        {skipped > 0 && (
          <Box variant="small" color="text-body-secondary">
            {skipped} fund-request attachment{skipped === 1 ? ' was' : 's were'} skipped
            (not a third-party invoice or 4J approval — not validated).
          </Box>
        )}
      </SpaceBetween>
    );
  };

  // Single MDF "Claims" tab: cost-share, entity linkage, invoices attached, and
  // line-item eligibility all under one tab (rather than two separate tabs). The
  // per-claim breakdown join lives in src/utils/mdfPerClaimBreakdown.js (pure +
  // unit-tested); buildMdfPerClaimBreakdown is imported at the top of this file.

  // Human label for a per-claim recommendation verdict (pass|escalate|reject).
  const mdfRecommendationLabel = (s) => {
    const v = (s || '').toLowerCase();
    if (v === 'pass') return 'Approve';
    if (v === 'reject') return 'Reject';
    if (v === 'escalate' || v === 'needs_review') return 'Needs review';
    return 'Pending';
  };

  // Compute the per-claim breakdown ONCE per render and share it between the
  // Claims tab (renderMdfClaims) and the summary-box "Sum of N claims" note,
  // instead of re-parsing validation_results_json + redoing the four-step join
  // in each place (mhamuzn). Only meaningful for MDF; cheap no-op ([]) otherwise.
  const mdfPerClaimBreakdown = effectiveProgram === 'MDF'
    ? buildMdfPerClaimBreakdown(getMdfChecks())
    : [];

  const renderMdfClaims = () => {
    const checks = getMdfChecks();
    const step2 = checks.step_2_po_4j_approval || {};
    const step3 = checks.step_3_invoice_present || {};
    const step4 = checks.step_4_entity_linkage || {};
    const step5 = checks.step_5_cost_share || {};
    const step6 = checks.step_6_line_item_eligibility || {};
    const step7 = checks.step_7_duplicate_invoice || {};
    const step8 = checks.step_8_sponsorship_eligibility || {};
    const fx = step5.fx_applied || null;
    const pct = step5.cost_share_pct;
    const maxPct = step5.max_percent ?? 50;
    // Cost-share badge follows the ENGINE's Step-5 verdict, not a raw
    // percentage comparison. A % at/under the cap is NOT always a pass, and a
    // foreign-currency claim with no approved rate escalates with no % at all
    // (the reviewer converts with the monthly AWS rate), so painting it green
    // would contradict the decision engine. mdfStatusType maps pass->success,
    // reject->error, escalate/needs_review->warning; the % itself is the value.
    const costShareType = mdfStatusType(step5.status);
    const unlinked = step4.unlinked_bill_to_names || [];
    const lineItemGroups = [
      { key: 'flagged_travel_items', label: 'Travel / Accommodation (MDFTravel)', color: 'red' },
      { key: 'flagged_ineligible_items', label: 'Other Ineligible (MDFIneligible)', color: 'red' },
      { key: 'venue_ambiguous_items', label: 'Hotel-as-Venue — needs review', color: 'grey' },
      { key: 'undetermined_items', label: 'Undetermined — needs review', color: 'grey' },
    ];
    const anyFlagged = lineItemGroups.some((g) => (step6[g.key] || []).length > 0);
    // Multi-claim FRs only: one card per submitted claim (recommendation +
    // amount + FX + cost-share % + that claim's invoices). Memoized so the Claims
    // tab and the summary-box note share ONE parse+join per render (mhamuzn).
    // Rendered only when >=2 claims (matches the summary note's `n > 1`); a
    // single submitted claim keeps the FR-level aggregate boxes.
    const perClaimBreakdown = mdfPerClaimBreakdown;

    return (
      <SpaceBetween size="l">
        {perClaimBreakdown.length > 1 && (
          <Container
            header={(
              <Header
                variant="h2"
                description="This fund request has multiple submitted claims. Each claim is validated on its OWN invoices — amount, cost-share, and recommendation are per claim, and the fund request takes the worst claim's outcome."
              >
                Per-Claim Breakdown
              </Header>
            )}
          >
            <SpaceBetween size="l">
              {perClaimBreakdown.map((claim) => {
                const conv = claim.invoice_total_converted != null;
                return (
                  <Container
                    key={claim.fund_claim_id}
                    header={(
                      <Header
                        variant="h3"
                        actions={(
                          <StatusIndicator type={mdfStatusType(claim.recommendation)}>
                            {mdfRecommendationLabel(claim.recommendation)}
                          </StatusIndicator>
                        )}
                      >
                        {`Claim ${claim.label.replace(/^claim-/i, '')}`}
                      </Header>
                    )}
                  >
                    <SpaceBetween size="m">
                      <ColumnLayout columns={3} variant="text-grid">
                        <div>
                          <Box variant="awsui-key-label">Claim Amount</Box>
                          <div>{mdfMoney(claim.claim_amount, claim.claim_currency)}</div>
                        </div>
                        <div>
                          <Box variant="awsui-key-label">Invoice Total{conv ? ' (converted)' : ''}</Box>
                          {conv ? (
                            <div>{mdfMoney(claim.invoice_total_converted, claim.claim_currency)}</div>
                          ) : (
                            <div>{mdfMoney(claim.invoice_total, claim.invoice_currency || claim.claim_currency)}</div>
                          )}
                          {claim.fx_applied && (
                            <Box variant="small" color="text-body-secondary">
                              {`${claim.fx_applied.from} → ${claim.fx_applied.to} @ ${mdfFxRate(claim.fx_applied.rate)}`}
                            </Box>
                          )}
                          {!claim.fx_applied && claim.invoice_currency
                            && claim.invoice_currency !== claim.claim_currency && (
                            <Box variant="small" color="text-body-secondary">
                              Invoice is in {claim.invoice_currency}; claim is in{' '}
                              {claim.claim_currency || '—'}. Convert manually (auto-FX unavailable).
                            </Box>
                          )}
                        </div>
                        <div>
                          <Box variant="awsui-key-label">Cost Share vs {claim.max_percent}% cap</Box>
                          <StatusIndicator type={mdfStatusType(claim.cost_share_status)}>
                            {(claim.cost_share_pct === null || claim.cost_share_pct === undefined)
                              ? '-' : `${claim.cost_share_pct}%`}
                          </StatusIndicator>
                        </div>
                      </ColumnLayout>
                      {/* Name WHICH check drove a reject/escalate so the reviewer
                          doesn't have to infer it from the FR-level (worst-of-all-
                          claims) boxes below — the recommendation badge is the
                          worst of all four per-claim steps, not just cost-share. */}
                      {claim.driving_step_label && (
                        <Box variant="small" color="text-body-secondary">
                          {`${mdfRecommendationLabel(claim.recommendation)} driven by: ${claim.driving_step_label}`}
                        </Box>
                      )}
                      <div>
                        <Box variant="awsui-key-label">Invoice Attachments</Box>
                        <Table
                          variant="embedded"
                          items={claim.invoice_breakdown}
                          columnDefinitions={[
                            {
                              id: 'file_name',
                              header: 'Invoice',
                              cell: (item) => item.file_name || '-',
                            },
                            {
                              id: 'amount',
                              header: 'Amount',
                              cell: (item) => mdfMoney(item.invoice_total, item.invoice_currency),
                            },
                          ]}
                          empty="No invoices attributed to this claim."
                        />
                      </div>
                    </SpaceBetween>
                  </Container>
                );
              })}
            </SpaceBetween>
          </Container>
        )}
        <Container header={<Header variant="h2">Cost-Share</Header>}>
          <ColumnLayout columns={3} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Claim Amount</Box>
              <div>{mdfMoney(step5.claim_amount, request.approval_summary?.claim_currency)}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Invoice Total{fx ? ' (converted)' : ''}</Box>
              {fx ? (
                <div>{mdfMoney(step5.invoice_total_converted, request.approval_summary?.claim_currency)}</div>
              ) : (
                // No FX conversion applied. Show the ORIGINAL invoice amount in
                // the invoice's OWN currency (step5.invoice_currency), so a
                // non-USD amount is copy/paste-able for a manual conversion while
                // auto-FX isn't available — rather than mislabeling it with the
                // claim currency. Falls back to the claim currency when the
                // invoice currency isn't recorded (same-currency claims).
                <div>{mdfMoney(step5.invoice_total, step5.invoice_currency || request.approval_summary?.claim_currency)}</div>
              )}
              {/* When the invoice currency differs from the claim currency and no
                  rate was applied, call it out so the reviewer knows to convert. */}
              {!fx && step5.invoice_currency
                && step5.invoice_currency !== request.approval_summary?.claim_currency && (
                <Box variant="small" color="text-body-secondary">
                  Invoice is in {step5.invoice_currency}; claim is in{' '}
                  {request.approval_summary?.claim_currency || '—'}. Convert the
                  amount above using the monthly AWS Finance rate.
                </Box>
              )}
              {/* FX escalation only: offer the monthly converter so the reviewer
                  can resolve the rate. Gated by needsManualFxConversion (escalate
                  + no auto-FX + differing currency), so it never appears on a
                  pass, a reject, or a same-currency / auto-converted claim. */}
              {needsManualFxConversion(step5, request.approval_summary?.claim_currency) && (
                <Box variant="small" padding={{ top: 'xxs' }}>
                  <Link external href={OEM_FX_CONVERTER_URL} target="_blank">
                    OEM FO Monthly Currency Converter
                  </Link>
                </Box>
              )}
            </div>
            <div>
              <Box variant="awsui-key-label">Cost Share vs {maxPct}% cap</Box>
              <StatusIndicator type={costShareType}>
                {(pct === null || pct === undefined) ? '-' : `${pct}%`}
              </StatusIndicator>
            </div>
          </ColumnLayout>
          {/* Per-invoice amounts. Shown whenever the agent recorded a breakdown,
              so a reviewer can copy each invoice's original amount + currency
              into an external converter — this is the only place the raw amounts
              appear when the aggregate Invoice Total above is blank because a
              multi-invoice claim couldn't be summed (mixed / unrecorded currency
              across receipts). A row whose amount the extractor couldn't read
              shows "-" rather than a misleading 0. */}
          {Array.isArray(step5.invoice_breakdown) && step5.invoice_breakdown.length > 0 && (
            <Box padding={{ top: 'm' }}>
              <Box variant="awsui-key-label">Invoice Amounts (per invoice)</Box>
              <Table
                variant="embedded"
                items={step5.invoice_breakdown}
                columnDefinitions={[
                  {
                    id: 'file_name',
                    header: 'Invoice',
                    cell: (item) => item.file_name || '-',
                  },
                  {
                    id: 'amount',
                    header: 'Amount',
                    cell: (item) => mdfMoney(item.invoice_total, item.invoice_currency),
                  },
                ]}
                empty="No invoice amounts recorded"
              />
              {/* Only show the "couldn't be totaled" note when there is genuinely
                  NO aggregate. A multi-invoice claim summed in a single currency
                  (no FX) has invoice_total populated but invoice_total_converted
                  falsy, and the "Invoice Total" above shows that real figure — so
                  guarding on invoice_total_converted alone would contradict it.
                  Gate on the actual absence of an aggregate (AutoSDE/batpur). */}
              {step5.invoice_breakdown.length > 1
                && !step5.invoice_total_converted
                && step5.invoice_total == null && (
                <Box variant="small" color="text-body-secondary" padding={{ top: 'xs' }}>
                  Invoices could not be automatically totaled (currencies differ or
                  were not all recorded). Convert each amount and confirm the claim
                  is within the {maxPct}% cost-share.
                </Box>
              )}
            </Box>
          )}
          {fx && (
            <Box padding={{ top: 'm' }}>
              <Box variant="awsui-key-label">FX Applied</Box>
              <div>{`${fx.from} → ${fx.to} @ ${fx.rate}`}</div>
            </Box>
          )}
          {step5.needs_review_detail && (
            <Box padding={{ top: 's' }}>
              <Box variant="small" color="text-body-secondary">{step5.needs_review_detail}</Box>
            </Box>
          )}
          {/* The per-claim cost-share breakdown now lives in the dedicated
              "Per-Claim Breakdown" section at the top of this tab (one card per
              claim: recommendation + amount + FX + cost-share % + that claim's
              invoices), so the inline per-claim table here is intentionally
              omitted to avoid duplicating it. This Cost-Share box shows the
              FR-level aggregate; single-claim FRs are unaffected. */}
        </Container>

        <Container header={<Header variant="h2">Invoice Entity Linkage</Header>}>
          <ColumnLayout columns={2} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">FR Invoice Entity</Box>
              <div>{step4.fr_entity_name || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Linkage</Box>
              <StatusIndicator type={mdfStatusType(step4.status)}>
                {step4.entity_link_clear === true
                  ? 'All bill-tos linked'
                  : (step4.status || '-').toUpperCase()}
              </StatusIndicator>
            </div>
            {step4.invoice_billed_to_aws && (
              <div>
                <Box variant="awsui-key-label">Billed to AWS</Box>
                <Badge color="blue">Yes</Badge>
              </div>
            )}
          </ColumnLayout>
          {unlinked.length > 0 && (
            <Box padding={{ top: 'm' }}>
              <Box variant="awsui-key-label">Bill-to names not linked to the partner</Box>
              <SpaceBetween size="xs">
                {unlinked.map((name, i) => (<Badge key={i} color="red">{name}</Badge>))}
              </SpaceBetween>
            </Box>
          )}
        </Container>

        {renderMdfAttachmentsBySection(step3)}

        {/* 4J Approver — only for 4J/AISPL POs (Step 2). A 4J PO requires the
            approval email to come from an India-resident AWS employee, resolved
            via PAPI. Shows what the agent persists: alias, email, and the
            India-residency verdict. (Approver name + India seat location aren't
            persisted yet — a possible PAPI-enrichment follow-up.) */}
        {step2.is_4j && (
          <Container header={<Header variant="h2">4J Approver</Header>}>
            <ColumnLayout columns={3} variant="text-grid">
              <div>
                <Box variant="awsui-key-label">Approver Alias</Box>
                <div>{step2.approver_alias || '-'}</div>
              </div>
              <div>
                <Box variant="awsui-key-label">Approver Email</Box>
                <div>{step2.approver_email || '-'}</div>
              </div>
              <div>
                <Box variant="awsui-key-label">India Residency</Box>
                <StatusIndicator
                  type={step2.approver_in_india === true
                    ? 'success'
                    : (step2.approver_in_india === false ? 'error' : 'warning')}
                >
                  {step2.approver_in_india === true
                    ? 'Confirmed in India'
                    : (step2.approver_in_india === false
                      ? 'Not in India'
                      : 'Undetermined')}
                </StatusIndicator>
              </div>
            </ColumnLayout>
            {step2.needs_review_detail && (
              <Box padding={{ top: 's' }}>
                <Box variant="small" color="text-body-secondary">{step2.needs_review_detail}</Box>
              </Box>
            )}
          </Container>
        )}

        <Container header={<Header variant="h2">Line-Item Eligibility</Header>}>
          <ColumnLayout columns={3} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Verdict</Box>
              <StatusIndicator type={mdfStatusType(step6.status)}>
                {(step6.status || '-').toUpperCase()}
              </StatusIndicator>
            </div>
            <div>
              <Box variant="awsui-key-label">Line Items Evaluated</Box>
              <div>{step6.line_items_evaluated ?? '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Invoice Language</Box>
              <div>{step6.invoice_language || '-'}</div>
            </div>
          </ColumnLayout>
          {step6.needs_review_detail && (
            <Box padding={{ top: 's' }}>
              <Box variant="small" color="text-body-secondary">{step6.needs_review_detail}</Box>
            </Box>
          )}
          {!anyFlagged ? (
            <Box padding={{ top: 'm' }}>
              {/* Follow the engine's Step-6 verdict for the indicator color, not
                  a pass-vs-everything-else split: a Step 6 that REJECTs without
                  enumerating specific line-item groups must stay red (error),
                  not render a yellow warning under the red REJECT verdict shown
                  in the same container (AutoSDE). */}
              <StatusIndicator type={mdfStatusType(step6.status)}>
                {step6.status === 'pass'
                  ? 'No ineligible line items found.'
                  : 'No specific line items were flagged.'}
              </StatusIndicator>
            </Box>
          ) : (
            <SpaceBetween size="m">
              {lineItemGroups.filter((g) => (step6[g.key] || []).length > 0).map((g) => (
                <div key={g.key}>
                  <Box variant="awsui-key-label">{`${g.label} (${step6[g.key].length})`}</Box>
                  <SpaceBetween size="xs">
                    {step6[g.key].map((desc, i) => (
                      <Box key={i}>
                        <Badge color={g.color}>{'•'}</Badge> {desc || '(no description)'}
                      </Box>
                    ))}
                  </SpaceBetween>
                </div>
              ))}
            </SpaceBetween>
          )}
        </Container>

        {/* Duplicate Invoice Check (Step 7). Surfaced so a Step-7 MDFDuplicate
            reject is visible in the tab body — not just the tab icon. Without
            this, a claim rejected solely on a duplicate invoice showed every
            box above green while the Approval Summary said reject (the reviewer-
            reported contradiction). Rendered whenever Step 7 produced a verdict. */}
        {step7.status && (
          <Container header={<Header variant="h2">Duplicate Invoice Check</Header>}>
            <ColumnLayout columns={2} variant="text-grid">
              <div>
                <Box variant="awsui-key-label">Verdict</Box>
                <StatusIndicator type={mdfStatusType(step7.status)}>
                  {(step7.status || '-').toUpperCase()}
                </StatusIndicator>
              </div>
              <div>
                <Box variant="awsui-key-label">Fingerprints Checked</Box>
                <div>{step7.fingerprints_checked ?? '-'}</div>
              </div>
            </ColumnLayout>
            {(step7.reject_detail || step7.needs_review_detail) && (
              <Box padding={{ top: 's' }}>
                <Box variant="small" color="text-body-secondary">
                  {step7.reject_detail || step7.needs_review_detail}
                </Box>
              </Box>
            )}
          </Container>
        )}

        {/* Sponsorship Eligibility (Step 8) — only applies to 3P-events / 4J
            sponsorship claims; a pass on a non-applicable step is not shown. As
            with Step 7, surfaced so a Step-8 reject/escalate is visible in the
            body, not only the tab icon. */}
        {step8.status && step8.status !== 'pass' && (
          <Container header={<Header variant="h2">Sponsorship Eligibility</Header>}>
            <ColumnLayout columns={2} variant="text-grid">
              <div>
                <Box variant="awsui-key-label">Verdict</Box>
                <StatusIndicator type={mdfStatusType(step8.status)}>
                  {(step8.status || '-').toUpperCase()}
                </StatusIndicator>
              </div>
              {step8.event_name && (
                <div>
                  <Box variant="awsui-key-label">Event</Box>
                  <div>{step8.event_name}</div>
                </div>
              )}
            </ColumnLayout>
            {(step8.reject_detail || step8.needs_review_detail) && (
              <Box padding={{ top: 's' }}>
                <Box variant="small" color="text-body-secondary">
                  {step8.reject_detail || step8.needs_review_detail}
                </Box>
              </Box>
            )}
          </Container>
        )}

        {/* Activity-eligibility reference from the MDF SOP / wiki. Read-only
            rubric so a reviewer can compare the invoice's line items against
            the eligible/ineligible activity types while judging the claim.
            Collapsed by default to keep the tab focused on the agent's
            findings above; this is the "why" behind those calls. */}
        <Container header={<Header variant="h2">Activity Eligibility Reference</Header>}>
          <Box variant="small" color="text-body-secondary" padding={{ bottom: 's' }}>
            MDF SOP activity types — compare each invoice line item against these
            lists. The agent flags travel and other ineligible items above; this
            is the full rubric behind those calls.
          </Box>
          <ColumnLayout columns={2}>
            <ExpandableSection
              headerText={`✅ Eligible activities (${MDF_ELIGIBLE_ACTIVITIES.length})`}
              defaultExpanded
            >
              <SpaceBetween size="xs">
                {MDF_ELIGIBLE_ACTIVITIES.map((a) => (
                  <div key={a.name}>
                    <Box variant="strong">{a.name}</Box>
                    {a.note && (
                      <Box variant="small" color="text-body-secondary">{a.note}</Box>
                    )}
                  </div>
                ))}
              </SpaceBetween>
            </ExpandableSection>
            <ExpandableSection
              headerText={`❌ Ineligible activities / expenses (${MDF_INELIGIBLE_ACTIVITIES.length})`}
              defaultExpanded
            >
              <SpaceBetween size="xs">
                {MDF_INELIGIBLE_ACTIVITIES.map((a) => (
                  <div key={a.name}>
                    <Box variant="strong">{a.name}</Box>
                    {a.note && (
                      <Box variant="small" color="text-body-secondary">{a.note}</Box>
                    )}
                  </div>
                ))}
              </SpaceBetween>
            </ExpandableSection>
          </ColumnLayout>
        </Container>
      </SpaceBetween>
    );
  };

  const getTabIcon = (validationStatus) => {
    const status = validationStatus?.toLowerCase();
    if (status === 'validated' || status === 'eligible' || status === 'approved' || status === 'qualified' || status === 'qualified_plus' || status === 'pass' || status === 'recommend_approval' || status === 'recommend approval') {
      return '✅ ';
    } else if (status === 'rejected' || status === 'not_eligible' || status === 'not_qualified' || status === 'fail' || status === 'failed' || status === 'critical' || status === 'recommend_rejection' || status === 'recommend rejection') {
      return '❌ ';
    } else if (status === 'warning' || status === 'conditional' || status === 'pending' || status === 'needs_review' || status === 'needs review' || status === 'needs_attention') {
      return '⚠️ ';
    }
    return '⚠️ '; // Default to warning if status is unknown
  };

  const getPricingCalculatorStatus = () => {
    // Use the agent's validation status which includes all checks:
    // - ARR variance (Request vs Calculator)
    // - Optimization threshold (Not Optimized ARR ≤ 10%)
    // - Excluded services (Data Transfer, Support, etc.)
    // - URL validation
    const pricingValidation = validationData.pricing_calculator || {};
    const agentStatus = pricingValidation.status;
    
    // If agent validation exists, use it
    if (agentStatus) {
      // The icon is the worse of status and result: the GPU >= 50%,
      // Bedrock > 50% and 3P Bedrock review warnings move only the result
      // to needs_attention and leave status validated.
      if (pricingValidation.result === 'fail') return 'fail';
      // Map agent statuses to tab icon statuses
      if (agentStatus === 'validated') {
        return pricingValidation.result === 'needs_attention' ? 'needs_attention' : 'validated';
      }
      if (agentStatus === 'failed_optimization' ||
          agentStatus === 'arr_variance_exceeded' ||
          agentStatus === 'critical_excluded_services' ||
          agentStatus === 'multiple_calculators' ||
          agentStatus === 'zero_arr' ||
          agentStatus === 'invalid_url' ||
          agentStatus === 'below_arr_threshold' ||
          agentStatus === 'inference_only_ineligible') return 'fail';
      return agentStatus; // pending, not_run, etc.
    }
    
    // Fallback: If no agent validation, return pending
    return 'pending';
  };

  const renderHistory = () => {
    return <HistoryTab fundRequestId={requestId} request={request} />;
  };

  const renderDuplicatesTab = () => {
    const duplicateData = request.validation_data?.detailed_validations?.duplicate_funding_requests || {};
    const duplicates = duplicateData.duplicates || [];
    const hasDuplicates = duplicates.length > 0;

    if (!hasDuplicates) {
      return (
        <Container>
          <Box textAlign="center" padding="xxl">
            <StatusIndicator type="success">
              <Box variant="h3">No Duplicates Found</Box>
              <Box variant="p" color="inherit" padding={{ top: 's' }}>
                This funding request has no duplicate entries in the system.
              </Box>
            </StatusIndicator>
          </Box>
        </Container>
      );
    }

    return (
      <SpaceBetween size="l">
        {/* Validation Decision */}
        <Container header={<Header variant="h2">Duplicates Summary</Header>}>
          <ValidationSummaryBanner 
            label="Duplicate Funding Requests" 
            validationData={duplicateData} 
          />
        </Container>

        <Alert type="warning" header={duplicateData.reason || `Found ${duplicates.length} duplicate FR(s)`}>
          The following funding requests may be duplicates based on matching criteria.
        </Alert>

        <Container>
          <Table
            columnDefinitions={[
              {
                id: 'fundRequestId',
                header: 'Potential Duplicate Fund Request Id',
                // Text on the blind page: the link opens the other fund request's
                // full page, whose answer is withheld only if it is sampled too.
                cell: item => (blindPending ? item.fundRequestId : (
                  <Link href={`/fund-requests/${item.fundRequestId}/details`}>
                    {item.fundRequestId}
                  </Link>
                )),
                sortingField: 'fundRequestId',
                width: 280
              },
              {
                id: 'opportunityId',
                header: 'Ace Opportunity Id',
                cell: item => item.opportunityId || '-',
                sortingField: 'opportunityId',
                width: 120
              },
              {
                id: 'programEngagementId',
                header: 'Program Engagement Id',
                cell: item => item.programEngagementId || '-',
                sortingField: 'programEngagementId',
                width: 150
              },
              {
                id: 'migrationPhase',
                header: 'Migration Phase',
                cell: item => item.migrationPhase || '-',
                sortingField: 'migrationPhase',
                width: 120
              },
              {
                id: 'activityName',
                header: 'Activity Name',
                cell: item => item.activityName || '-',
                sortingField: 'activityName',
                width: 200
              },
              {
                id: 'partnerName',
                header: 'Partner Name',
                cell: item => item.partnerName || '-',
                sortingField: 'partnerName',
                width: 150
              },
              {
                id: 'approvedAmount',
                header: 'Approved Amount',
                cell: item => item.approvedAmount ? formatCurrency(parseFloat(item.approvedAmount)) : '-',
                sortingField: 'approvedAmount',
                width: 130
              },
              {
                id: 'stage',
                header: 'Stage',
                cell: item => item.stage || '-',
                sortingField: 'stage',
                width: 120
              },
              {
                id: 'awsOpportunityId',
                header: 'AWS Opportunity Id',
                cell: item => item.awsOpportunityId || '-',
                sortingField: 'awsOpportunityId',
                width: 150
              },
              {
                id: 'businessDescription',
                header: 'Business Description',
                cell: item => (
                  <Box>
                    {item.businessDescription ? (
                      <Box variant="small">{item.businessDescription.substring(0, 100)}{item.businessDescription.length > 100 ? '...' : ''}</Box>
                    ) : '-'}
                  </Box>
                ),
                width: 250
              },
              {
                id: 'customerCompanyName',
                header: 'Ace Opportunity Customer Company Name',
                cell: item => item.customerCompanyName || '-',
                sortingField: 'customerCompanyName',
                width: 200
              }
            ]}
            items={duplicates}
            loadingText="Loading duplicates"
            sortingDisabled={false}
            empty={
              <Box textAlign="center" color="inherit">
                <b>No duplicates found</b>
              </Box>
            }
            header={
              <Header
                counter={`(${duplicates.length})`}
                description="Funding requests that may be duplicates based on matching identifiers"
              >
                Fund Request Duplicates
              </Header>
            }
          />
        </Container>
      </SpaceBetween>
    );
  };

  const validationData = request.validation_data?.detailed_validations || {};
  const technicalAttachedFilesData = projectTechnicalReviewAttachedFiles(
    validationData.attached_files,
  );

  // AI Modernization (P447717852): tab visibility + result surfacing.
  // Backend writes pathway_arrs onto state["pricing_calculator"] in
  // validate_pricing_calculator (both the Lambda and YAML propagation
  // paths). make_final_decision persists pricing_calculator to
  // DynamoDB verbatim, so this is the single source of truth the
  // agent decision was made against — reading anything else can
  // diverge from the actual validation state (e.g. show ARR=0
  // while the validator ran on non-zero data).
  const aiModResult = validationData.ai_modernization || {};
  const backendPathwayArrs = validationData.pricing_calculator?.pathway_arrs || {};
  let moveToAiArr = Number(backendPathwayArrs.move_to_ai) || 0;
  let usedClientSideFallback = false;
  if (!moveToAiArr && Array.isArray(calculatorDetails?.services)) {
    // Fallback for FRs that ran before pathway_arrs was persisted on
    // the result dict (pre-P447717852). Sums calculator services
    // whose name matches Move-to-AI keywords. Logged so we can spot
    // divergence between what the frontend shows and what the
    // backend validator saw.
    const AI_KEYWORDS = ['bedrock', 'sagemaker', 'q developer', 'comprehend', 'rekognition', 'textract', 'transcribe', 'translate', 'polly', 'lex', 'forecast', 'personalize'];
    moveToAiArr = calculatorDetails.services
      .filter(s => AI_KEYWORDS.some(k => (s.service_name || '').toLowerCase().includes(k)))
      .reduce((sum, s) => sum + ((s.monthly_cost || 0) * 12), 0);
    if (moveToAiArr > 0) {
      usedClientSideFallback = true;
      // eslint-disable-next-line no-console
      console.warn(
        `[Chronos] Move-to-AI ARR: backend pathway_arrs missing on pricing_calculator; using client-side fallback derivation (moveToAiArr=$${moveToAiArr.toLocaleString()}). Older FR — re-validate to refresh.`
      );
    }
  }
  // GPU >= 50% NEEDS REVIEW gate (P519717760). Read from the same persisted
  // pricing_calculator.warnings the Pricing Calculator tab renders. The gate
  // fires on EC2 GPU families independent of Move-to-AI ARR, so it alone is
  // enough to show the Move to AI tab.
  const gpuWarnings = (validationData.pricing_calculator?.warnings || []).filter(w => w && w.type === 'gpu_over_50');
  // MAP 3P Bedrock policy (P530558159, P447717852): the Move-to-AI split the
  // calculator Lambda computed, in the block the agent persisted, plus the
  // agent's GPU share and inference-only outcome. Rendered as-is.
  const bedrock3pBlock = validationData.pricing_calculator?.bedrock_3p_policy?.block;
  const moveToAiSplit = bedrock3pBlock?.status === 'ok' ? bedrock3pBlock.moveToAi : null;
  const gpuArrSummary = validationData.pricing_calculator?.gpu_arr_summary;
  const bedrock3pOutcome = validationData.pricing_calculator?.bedrock_3p_outcome;
  // bedrock_3p_outcome -> the inference-only indicator, which also drives the
  // Move to AI tab icon. An outcome not listed here reads as needs review.
  const INFERENCE_ONLY_INDICATORS = {
    pass: ['success', 'Below threshold'],
    not_applicable: ['success', 'Not applicable'],
    off: ['stopped', 'Off'],
    skipped_calculator_failed: ['warning', 'Not evaluated (calculator failed)'],
    review_known_breach: ['warning', 'Needs review: 3P Bedrock above threshold'],
    review_unknown_uncertain: ['warning', 'Needs review: unknown providers could exceed threshold'],
    review_contract_unavailable: ['warning', 'Needs review: share not evaluated'],
    reject_inference_only: ['error', 'Ineligible: inference-only'],
  };
  const [inferenceOnlyType, inferenceOnlyText] = bedrock3pOutcome
    ? INFERENCE_ONLY_INDICATORS[bedrock3pOutcome] || ['warning', `Needs review: ${bedrock3pOutcome}`]
    : [null, null];
  // An inference-only outcome that fails or needs review also shows the tab:
  // providers missing from the chronos_modpathway seed leave Move-to-AI ARR at
  // 0 while the gate still fires.
  const showExistingMoveToAi = moveToAiArr > 0 || gpuWarnings.length > 0
    || inferenceOnlyType === 'error' || inferenceOnlyType === 'warning';
  // AI archetype recommendation (P547836103). Only a record that passes the
  // stage's display gate (panel flag, visible mode, approved schema and
  // digests) shows the panel or makes the tab reachable on its own; a shadow,
  // old-schema or unapproved record changes nothing on this page.
  const aiArchetypeRecord = validationData.ai_archetype;
  const showAiArchetypePanel = isAiArchetypeDisplayEligible(aiArchetypeRecord);
  // A not_applicable result (no included Bedrock or AgentCore ARR) is shown
  // when the tab is already open but never opens it.
  const showMoveToAiTab = showExistingMoveToAi
    || (showAiArchetypePanel && aiArchetypeRecord.evaluation_status !== 'not_applicable');

  // The archetype panel sits above every existing Move to AI view, including
  // the zero-ARR one. When the archetype is the only Move-to-AI data, the tab
  // holds the panel alone.
  const renderMoveToAI = () => {
    const existing = showExistingMoveToAi ? renderExistingMoveToAI() : null;
    if (!showAiArchetypePanel) return existing;
    return (
      <SpaceBetween size="l">
        <AIArchetypePanel record={aiArchetypeRecord} fundRequestId={requestId} onReloadPage={loadRequestData} />
        {existing}
      </SpaceBetween>
    );
  };

  const renderExistingMoveToAI = () => {
    // AI Modernization tab (P447717852). Surfaces Section D data,
    // Move-to-AI ARR breakdown, and validator findings. The
    // breakdown splits Move-to-AI ARR by Bedrock policy class from
    // the persisted 3P Bedrock block (P530558159); FRs without the
    // block keep the placeholder.
    const splitArr = (value) => (
      value === null || value === undefined || !Number.isFinite(Number(value)) ? '—' : formatCurrency(Number(value))
    );
    const SPLIT_COLUMNS = [
      {
        id: 'label',
        header: 'Category',
        cell: item => (item.total ? <Box fontWeight="bold">{item.label}</Box> : item.label),
      },
      {
        id: 'arr',
        header: 'ARR',
        cell: item => (item.total ? <Box fontWeight="bold">{splitArr(item.arr)}</Box> : splitArr(item.arr)),
      },
      { id: 'basis', header: 'Basis', cell: item => item.basis },
      { id: 'inTotal', header: 'In Move-to-AI total', cell: item => item.inTotal },
    ];
    // gpu_arr_summary and bedrock_3p_outcome are persisted apart from the
    // block, so the GPU EC2 row and the inference-only indicator render on
    // every path below, including a GPU-only FR and a missing or non-ok block.
    const gpuEc2Row = gpuArrSummary ? {
      id: 'gpuEc2',
      label: `GPU EC2${Array.isArray(gpuArrSummary.families) && gpuArrSummary.families.length > 0 ? ` (${gpuArrSummary.families.join(', ')})` : ''}`,
      arr: gpuArrSummary.gpu_arr,
      basis: gpuArrSummary.basis === 'gross'
        ? `Gross (${gpuArrSummary.share_pct ?? '—'}% of gross ARR)`
        : gpuArrSummary.basis || '—',
      inTotal: 'No',
    } : null;
    const gpuEc2Table = gpuEc2Row && (
      <Table variant="embedded" trackBy="id" columnDefinitions={SPLIT_COLUMNS} items={[gpuEc2Row]} />
    );
    const inferenceOnlyIndicator = bedrock3pOutcome && (
      <div>
        <Box variant="awsui-key-label">Inference-only gate (3P Bedrock)</Box>
        <div><StatusIndicator type={inferenceOnlyType}>{inferenceOnlyText}</StatusIndicator></div>
      </div>
    );

    // GPU-only FR (tab shown by the GPU gate, no Move-to-AI ARR): Section D,
    // the Move-to-AI ARR fields and the not_applicable ai_modernization
    // verdict do not apply and would mislead, so show only the GPU message,
    // the inference-only indicator and the GPU EC2 row.
    if (moveToAiArr <= 0) {
      return (
        <Container header={<Header variant="h2">AI Modernization Summary</Header>}>
          <SpaceBetween size="s">
            <PricingCalculatorWarnings pricingData={{ warnings: gpuWarnings }} />
            {inferenceOnlyIndicator}
            {gpuEc2Table}
          </SpaceBetween>
        </Container>
      );
    }
    const scopeChecklist = validationData.attached_files?.validations?.scope_checklist || {};
    const phases = scopeChecklist.phases || {};
    const assessD = phases.assess?.assess_ai_modernization || null;
    const mobilizeD = phases.mobilize?.mobilize_ai_modernization || null;

    const iconFor = (val) => {
      const s = String(val || '').trim().toLowerCase();
      if (s === 'yes') return <StatusIndicator type="success">Yes</StatusIndicator>;
      if (s === 'no') return <StatusIndicator type="error">No</StatusIndicator>;
      return <StatusIndicator type="warning">Not answered</StatusIndicator>;
    };

    const ROWS_ASSESS = [
      { key: 'model_selection_strategy', label: 'Model Selection Strategy' },
      { key: 'pricing_tier_strategy', label: 'Pricing Tier Selection Strategy' },
    ];
    const ROWS_MOBILIZE = [
      { key: 'model_selection_strategy', label: 'Model Selection Justification', wantsComment: true },
      { key: 'pricing_tier_strategy', label: 'Pricing Tier Selection Justification', wantsComment: true },
      { key: 'ai_data_governance', label: 'AI-Ready Data Governance Framework' },
      { key: 'ai_security_compliance', label: 'AI Security & Compliance Controls' },
      { key: 'ai_risk_management', label: 'AI Risk Management & Evaluation Framework' },
    ];

    const MOVE_TO_AI_SPLIT_ROWS = [
      { key: 'bedrock3p', label: 'Bedrock, 3P model providers' },
      { key: 'bedrock1p', label: 'Bedrock, 1P (Amazon) models' },
      { key: 'bedrockUnknown', label: 'Bedrock, model provider unknown' },
      { key: 'agentCore', label: 'Bedrock AgentCore' },
      { key: 'other', label: 'Other Move-to-AI services' },
    ];

    const renderSectionDGrid = (phaseName, phaseD, rows) => (
      <Container key={phaseName} header={<Header variant="h3">{phaseName} — Section D</Header>}>
        {phaseD ? (
          <Table
            columnDefinitions={[
              { id: 'label', header: 'Deliverable', cell: item => item.label, width: 320 },
              { id: 'in_scope', header: 'In Scope', cell: item => iconFor(phaseD[`${item.key}_in_scope`]), width: 140 },
              {
                id: 'comment',
                header: 'Comment',
                cell: item => {
                  const c = phaseD[`${item.key}_comment`];
                  if (!c) return item.wantsComment ? <StatusIndicator type="warning">Missing (required)</StatusIndicator> : '-';
                  return <Box variant="small">{c.length > 220 ? `${c.slice(0, 220)}…` : c}</Box>;
                }
              }
            ]}
            items={rows}
            variant="embedded"
          />
        ) : (
          <Alert type="warning" header={`${phaseName} Section D absent from scope checklist`}>
            The partner did not fill Section D for this phase — validator will surface as needs_attention or reject depending on migration phase.
          </Alert>
        )}
      </Container>
    );

    return (
      <SpaceBetween size="l">
        <Container header={<Header variant="h2">AI Modernization Summary</Header>}>
          <SpaceBetween size="s">
            <ValidationSummaryBanner
              label="AI Modernization"
              validationData={aiModResult}
            />
            <PricingCalculatorWarnings pricingData={{ warnings: gpuWarnings }} />
            <ColumnLayout columns={bedrock3pOutcome ? 3 : 2} variant="text-grid">
              <div>
                <Box variant="awsui-key-label">Move-to-AI ARR</Box>
                <div>{typeof formatCurrency === 'function' ? formatCurrency(moveToAiArr) : `$${moveToAiArr.toLocaleString()}`}</div>
              </div>
              <div>
                <Box variant="awsui-key-label">Ratio of Total ARR</Box>
                <div>{(() => {
                  const total = Number(validationData.pricing_calculator?.calculator_total_arr) || 0;
                  if (!total) return '—';
                  return `${((moveToAiArr / total) * 100).toFixed(1)}%`;
                })()}</div>
              </div>
              {inferenceOnlyIndicator}
            </ColumnLayout>
          </SpaceBetween>
        </Container>

        {renderSectionDGrid('Assess', assessD, ROWS_ASSESS)}
        {renderSectionDGrid('Mobilize', mobilizeD, ROWS_MOBILIZE)}

        {(aiModResult.key_findings?.length > 0 || aiModResult.required_actions?.length > 0) && (
          <Container header={<Header variant="h2">Findings & Required Actions</Header>}>
            <SpaceBetween size="m">
              {aiModResult.key_findings?.length > 0 && (
                <div>
                  <Box variant="awsui-key-label">Key Findings</Box>
                  <ul style={{ marginTop: 0, paddingLeft: 20 }}>
                    {aiModResult.key_findings.map((f, i) => <li key={i}>{f}</li>)}
                  </ul>
                </div>
              )}
              {aiModResult.required_actions?.length > 0 && (
                <div>
                  <Box variant="awsui-key-label">Required Actions</Box>
                  <ul style={{ marginTop: 0, paddingLeft: 20 }}>
                    {aiModResult.required_actions.map((a, i) => <li key={i}>{a}</li>)}
                  </ul>
                </div>
              )}
            </SpaceBetween>
          </Container>
        )}

        {/*
          Move-to-AI ARR by Bedrock policy class, on the modernization
          basis, as the Lambda computed it: the five classes sum to the
          total. Bedrock outside the pathway and the GPU EC2 row (the
          agent's gross-basis GPU share) are shown beside it, never in it.
        */}
        <Container header={<Header variant="h2">Move-to-AI ARR Breakdown</Header>}>
          {moveToAiSplit ? (
            <Table
              variant="embedded"
              trackBy="id"
              columnDefinitions={SPLIT_COLUMNS}
              items={[
                ...MOVE_TO_AI_SPLIT_ROWS.map(row => ({
                  id: row.key, label: row.label, arr: moveToAiSplit[row.key], basis: 'Modernization', inTotal: 'Yes',
                })),
                {
                  id: 'moveToAiArr', label: 'Move-to-AI ARR total', arr: moveToAiSplit.moveToAiArr, basis: 'Modernization', inTotal: '—', total: true,
                },
                {
                  id: 'bedrockOutside', label: 'Bedrock outside the Move-to-AI pathway', arr: moveToAiSplit.bedrockOutside, basis: 'Modernization', inTotal: 'No',
                },
                ...(gpuEc2Row ? [gpuEc2Row] : []),
              ]}
            />
          ) : (
            <SpaceBetween size="s">
              <Alert type="info" header="Coming soon">
                AWS-native vs 3P vs GPU ARR filtering will surface here once eligibility policy is finalized. See ticket P447717852 for tracking.
              </Alert>
              {gpuEc2Table}
            </SpaceBetween>
          )}
        </Container>
      </SpaceBetween>
    );
  };

  // The blind page. The server sends the fund request's own data and Chronos's
  // deterministic checks, and withholds the document and LLM checks, the agent
  // narrative, the approval summary and the final recommendation (batpur,
  // 2026-10-21). Since blind-view-2 it also sends the pricing calculator and the
  // checks that read it when MAP validated a calculator APFP linked (batpur,
  // 2026-10-30). The page is built from the kept keys alone, never by filtering
  // the full page: the header, the form, and the full page's tabs in its order,
  // where a listed check's tab renders it and every other tab is a placeholder.
  // There is no status badge, Validate, reveal, feedback panel or modal, and no
  // page-level pricing fetch (loadRequestData). It returns here, after the Move to
  // AI condition it reads and before the full page builds its tabs.
  if (blindPending) {
    const blindFundRequestId = request.apfp_fundrequestid || request.id;
    const fundRequest = request.validation_data?.complete_api_data?.fundRequest || {};
    const requestSummary = request.validation_data?.request_data_summary || {};
    const checks = request.validation_data?.detailed_validations || {};
    const opportunities = fundRequest.opportunities || {};
    const oppRecord = opportunities[Object.keys(opportunities)[0]]?.record || {};
    const duplicateCount = (checks.duplicate_funding_requests?.duplicates || []).length;
    // The full page's icon is the worse of the partner and specialized workload
    // checks. The specialized one counts only when listed.
    const specialized = blindListed('specialized_workload') ? checks.specialized_workload : undefined;
    // Likewise Opportunity Details, which shows opportunity_amount only when listed.
    // The icon is the worse of the tab's own status and that check.
    const amount = blindListed('opportunity_amount') ? checks.opportunity_amount : undefined;
    const blindWorse = (own, other) => {
      if (own === 'fail' || other?.result === 'fail') return 'fail';
      if (own === 'needs_attention' || other?.result === 'needs_attention') return 'needs_attention';
      return own;
    };
    // A tab the full page shows with no listed check behind it: its plain label,
    // greyed, and no renderer called. Cloudscape never renders a disabled tab's content.
    const blindPlaceholder = (id, label) => ({
      id,
      label,
      disabled: true,
      disabledReason: BLIND_PLACEHOLDER_REASON,
      content: <Box>{BLIND_PLACEHOLDER_REASON}</Box>,
    });
    // MAP's tabs in the full page's order. A check's tab is built only when the
    // server lists it, so only the listed checks' renderers run. A tab the full page
    // shows on a condition is a placeholder only where the condition reads kept
    // data; MPE Details and Duplicates show only when listed.
    const blindTabs = [
      blindPlaceholder('approval-summary', 'Approval Summary'),
      blindPlaceholder('migration-details', 'Migration Details'),
      // Listed on the MMC or MCR path.
      blindListed('partner_eligibility') ? {
        id: 'partner-eligibility',
        label: `${getTabIcon(
          specialized?.result === 'fail' || checks.partner_eligibility?.result === 'fail'
            ? 'fail'
            : specialized?.result === 'needs_attention' || checks.partner_eligibility?.result === 'needs_attention'
              ? 'needs_attention'
              : checks.partner_eligibility?.result
        )}Partner Eligibility`,
        content: renderPartnerEligibility(),
      } : blindPlaceholder('partner-eligibility', 'Partner Eligibility'),
      blindListed('opportunity_stage') ? {
        id: 'opportunity-details',
        label: `${getTabIcon(blindWorse(checks.opportunity_stage?.result, amount))}Opportunity Details`,
        content: renderOpportunityDetails(),
      } : blindPlaceholder('opportunity-details', 'Opportunity Details'),
      // Listed for MAP_LARGE from request_data_summary.map_program, and on any
      // tier for an MPE result other than not_applicable. The full page gates on
      // final_decision.map_program, which is withheld.
      ...(blindListed('mpe_program') ? [{
        id: 'mpe-details',
        label: `${getTabIcon(checks.mpe_program?.result)}MPE Details`,
        content: renderMPEDetails(),
      }] : []),
      blindListed('project_timeline') ? {
        id: 'project-details',
        label: `${getTabIcon(checks.project_timeline?.status === 'invalid_account_id_warning' ? 'warning' : checks.project_timeline?.result)}Project Details`,
        content: renderProjectDetails(),
      } : blindPlaceholder('project-details', 'Project Details'),
      // Listed when the calculator MAP validated is one APFP linked. No icon: the
      // pricing check's own verdict stays withheld.
      blindListed('pricing_calculator') ? {
        id: 'pricing-calculator',
        label: 'Pricing Calculator',
        content: renderPricingCalculator(),
      } : blindPlaceholder('pricing-calculator', 'Pricing Calculator'),
      blindPlaceholder('technical-review', 'Technical Review'),
      // No Move to AI placeholder: every input of the full page's condition is
      // withheld from a blind reviewer (pathway_arrs, the GPU review warning and
      // the inference-only outcome), so its presence alone would hint at them.
      blindPlaceholder('history', 'History'),
      // The full page's condition, on APFP's own fields.
      ...(isClaimFR(request) ? [blindPlaceholder('claims', 'Claims')] : []),
      ...(blindListed('duplicate_funding_requests') ? [{
        id: 'duplicates',
        label: `⚠️ Duplicates (${duplicateCount})`,
        content: renderDuplicatesTab(),
      }] : []),
    ];
    // A placeholder never opens: the chosen tab if it is enabled, else the first
    // enabled one. Without a listed check's tab there is no tab at all.
    const blindEnabledIds = blindTabs.filter((tab) => !tab.disabled).map((tab) => tab.id);
    const blindOpenTab = blindEnabledIds.includes(blindActiveTab) ? blindActiveTab : blindEnabledIds[0];
    return (
      <SpaceBetween size="s">
        <Flashbar items={notifications} />
        {blindShownIds.includes('duplicate_funding_requests') && !dismissedDuplicatesAlert && (
          <Alert
            type="warning"
            dismissible
            onDismiss={() => setDismissedDuplicatesAlert(true)}
            header="Possible Duplicates Detected"
          >
            We identified possible duplicates with this fund request. Refer to the <Link onFollow={() => setBlindActiveTab('duplicates')}>Fund Request Duplicates Table</Link> for additional details.
          </Alert>
        )}
        <Container
          header={
            <Header
              variant="h1"
              actions={<Button onClick={() => history.push('/dashboard')}>Back to List</Button>}
            >
              {blindFundRequestId}
            </Header>
          }
        >
          <SpaceBetween size="m">
            {request.processed_date && (
              <Box textAlign="right">
                <Box variant="small" color="text-body-secondary">
                  Last Processed: {new Date(request.processed_date).toLocaleString()}
                </Box>
              </Box>
            )}
            <ColumnLayout columns={3} variant="text-grid">
              <div>
                <Box variant="awsui-key-label">Request ID</Box>
                <Link external href={getFundingRequestUrl(blindFundRequestId)}>
                  {blindFundRequestId}
                </Link>
                <Box variant="awsui-key-label" padding={{ top: 's' }}>Benefit Application ID</Box>
                <div>{fundRequest.benefitApplicationId || '-'}</div>
                <Box variant="awsui-key-label" padding={{ top: 's' }}>Annual Run Rate (ARR)</Box>
                <Box fontSize="heading-s" fontWeight="bold" color="text-status-success">
                  {formatCurrency(parseAmount(fundRequest.arrUsdAmount || fundRequest.eligibleArrUsdAmount || 0))}
                </Box>
              </div>
              <div>
                <Box variant="awsui-key-label">Partner</Box>
                <div>{fundRequest.partner?.name || request.partner || '-'}</div>
                <Box variant="awsui-key-label" padding={{ top: 's' }}>SPMS ID</Box>
                <div>{checks.partner_eligibility?.spms_id || fundRequest.partner?.spmsId || fundRequest.owner?.partnerSpmsId || 'Not Available'}</div>
                <Box variant="awsui-key-label" padding={{ top: 's' }}>Total Funds Requested from AWS</Box>
                <Box fontSize="heading-s" fontWeight="bold">
                  {blindRequestedAmount(fundRequest.fundingDetails)}
                </Box>
              </div>
              <div>
                <Box variant="awsui-key-label">Customer</Box>
                <div>{oppRecord.customerCompanyName || checks.opportunity_stage?.customer_company_name || '-'}</div>
                <Box variant="awsui-key-label" padding={{ top: 's' }}>Migration Phase</Box>
                <Badge color="blue">{fundRequest.migrationPhase || requestSummary.migration_phase || '-'}</Badge>
                <Box variant="awsui-key-label" padding={{ top: 's' }}>Program Type</Box>
                <Badge color="blue">{requestSummary.map_program?.replace('_', ' ') || '-'}</Badge>
                {fundRequest.isPublicSector && (
                  <Box padding={{ top: 'xs' }}>
                    <Badge color="blue">PUBLIC SECTOR</Badge>
                  </Box>
                )}
              </div>
            </ColumnLayout>
            <ColumnLayout columns={1} variant="text-grid">
              <div>
                <Box variant="awsui-key-label">Project Description</Box>
                <div>{fundRequest.project?.description || request.description || '-'}</div>
              </div>
            </ColumnLayout>
          </SpaceBetween>
        </Container>
        {/* Where the full page has the feedback panel. */}
        <BlindReviewForm
          decision={blindDecision}
          submitting={blindSubmitting}
          onDecisionChange={setBlindDecision}
          onSubmit={handleBlindSubmit}
        />
        {/* No contract or an empty list: no tab to open. */}
        {blindEnabledIds.length > 0 && (
          <Tabs
            activeTabId={blindOpenTab}
            // Space on a focused placeholder asks for it too.
            onChange={({ detail }) => blindEnabledIds.includes(detail.activeTabId) && setBlindActiveTab(detail.activeTabId)}
            tabs={blindTabs}
          />
        )}
      </SpaceBetween>
    );
  }

  // ISFR's tabs after its Approval Summary: the Reviewer Breakdown, Request
  // Details, one tab per review category (built from the record's category
  // rollup) and the flags, all from src/programs/isfr/tabs.js.
  // Computed once: the tab list and ISFR's PROGRAM_TAB_ALLOWLIST entry below
  // both read it.
  const isfrTabList = effectiveProgram === 'ISFR' ? isfrTabs(request, setActiveTab) : [];

  const tabs = [
    {
      id: 'approval-summary',
      // Rubicon/MDF/POC store approval_summary.decision as the short internal
      // code ("approve"/"reject"/"flag_for_manual_review", from make_decision.py's
      // decision aggregation) -- getTabIcon doesn't recognize those strings and
      // falls through to its ⚠️ default regardless of outcome. approval_summary.
      // recommendation holds the persisted recommend_approval/recommend_rejection/
      // needs_review value, which getTabIcon DOES recognize, so those programs
      // read .recommendation (single source of truth: _usesRecommendationVocab,
      // shared with the in-tab Final Decision badge in renderValidationSummary).
      // (MAP/WMP/IW persist .decision already in the recommend_* vocabulary.)
      label: _usesRecommendationVocab(effectiveProgram)
        ? `${getTabIcon(request.approval_summary?.recommendation)}Approval Summary`
        : `${getTabIcon(request.approval_summary?.decision)}Approval Summary`,
      // ISFR renders its own summary from src/programs/isfr/: its record
      // carries a category rollup, an executive summary and separate reviewer
      // and partner lanes, none of which the shared summary reads.
      content: effectiveProgram === 'ISFR'
        ? <IsfrReviewSummary request={request} onOpenTab={setActiveTab} />
        : renderValidationSummary()
    },
    ...isfrTabList,
    // MDF-specific "Claims" tab — one tab holding cost-share, entity linkage,
    // invoices attached, and line-item eligibility. Validation-bearing, so the
    // label carries the worst of the cost-share (Step 5) and line-item (Step 6)
    // verdicts as a pass/reject/escalate icon. Uses a distinct id (mdf-claims,
    // not the MAP 'claims' id) so it doesn't collide with the MAP claims tab
    // that isClaimFR also generates for FR-MDF-CASH-* rows; that MAP tab stays
    // filtered out by the MDF allowlist below.
    ...(effectiveProgram === 'MDF' ? (() => {
      // The Claims tab renders EVERY displayed step (2 PO/4J, 3 invoice present,
      // 4 entity linkage, 5 cost-share, 6 line-item eligibility), so the tab
      // icon must reflect the worst of ALL of them — a reject/escalate in Steps
      // 2-4 must not leave the tab green just because Steps 5-6 passed (batpur R4).
      // Computed by the pure helper (unit-tested in mdfClaimsTabStatus.test.js).
      const worst = mdfClaimsTabStatus(getMdfChecks());
      return [
        {
          id: 'mdf-claims',
          label: `${getTabIcon(mdfStatusTypeToIcon(worst))}Claims`,
          content: renderMdfClaims()
        },
      ];
    })() : []),
    {
      id: 'migration-details',
      label: 'Migration Details',
      content: renderMigrationDetails()
    },
    {
      id: 'partner-eligibility',
      label: `${getTabIcon(
        // Partner Eligibility group = worst of partner_eligibility + specialized_workload
        validationData.specialized_workload?.result === 'fail' || validationData.partner_eligibility?.result === 'fail'
          ? 'fail'
          : validationData.specialized_workload?.result === 'needs_attention' || validationData.partner_eligibility?.result === 'needs_attention'
            ? 'needs_attention'
            : validationData.partner_eligibility?.result
      )}Partner Eligibility`,
      content: renderPartnerEligibility()
    },
    {
      id: 'opportunity-details',
      // The curated-envelope programs' Opportunity Details tab is purely
      // informational (no per-tab pass/fail/warning icon), same as Project
      // Details below -- it just surfaces curated APFP opportunity fields, with
      // no validation check gating what's shown. MMP/VMP has an opportunity
      // rule set of its own (OS-001..OS-005) but no `opportunity_stage` entry
      // under detailed_validations to read an icon off, so it takes the
      // unadorned label with Rubicon rather than a permanently grey icon.
      label: readsCuratedApfp
        ? 'Opportunity Details'
        : `${getTabIcon(validationData.opportunity_stage?.result)}Opportunity Details`,
      content: readsCuratedApfp
        ? renderRubiconOpportunityDetails()
        : renderOpportunityDetails()
    },
    // Conditionally show MPE Details tab only for MAP_LARGE
    ...(request.validation_data?.detailed_validations?.final_decision?.map_program === 'MAP_LARGE' ? [{
      id: 'mpe-details',
      label: `${getTabIcon(validationData.mpe_program?.result)}MPE Details`,
      content: renderMPEDetails()
    }] : []),
    {
      id: 'project-details',
      // Neither curated-envelope program has a project_timeline-equivalent
      // check, so the label carries no pass/fail/warning icon prefix
      // (renderRubiconProjectDetails is purely informational — see comment on
      // that function).
      label: readsCuratedApfp
        ? 'Project Details'
        : `${getTabIcon(validationData.project_timeline?.status === 'invalid_account_id_warning' ? 'warning' : validationData.project_timeline?.result)}Project Details`,
      content: readsCuratedApfp
        ? renderRubiconProjectDetails()
        : renderProjectDetails()
    },
    // MMP/VMP "Request Details": funding shape, calculator link, location,
    // timeline — everything a reviewer needs from the APFP JSON to verify
    // the agent's findings without opening the portal. Gated on MMP_VMP so
    // MAP reviewers do not see an empty tab (MAP does not populate
    // requestDetails). Separate from Project Details because 56/89 of
    // MMP/VMP FRs are assessments (MVA), not projects.
    ...(effectiveProgram === 'MMP_VMP' ? [{
      id: 'request-details',
      label: 'Request Details',
      content: renderMmpVmpRequestDetails()
    }] : []),
    {
      id: 'pricing-calculator',
      label: `${getTabIcon(getPricingCalculatorStatus())}Pricing Calculator`,
      content: renderPricingCalculator()
    },
    {
      id: 'technical-review',
      label: `${getTabIcon(
        // Technical Review uses the claims-free attached-files projection
        // so CST outcomes affect Claims without changing this tab. Section D
        // remains under the Technical Review umbrella.
        technicalAttachedFilesData.result === 'fail' || validationData.technical_requirements?.result === 'fail' || aiModResult.result === 'fail'
          ? 'fail'
          : technicalAttachedFilesData.result === 'needs_attention' || validationData.technical_requirements?.result === 'needs_attention' || aiModResult.result === 'needs_attention'
            ? 'needs_attention'
            : validationData.technical_requirements?.result
      )}Technical Review`,
      content: renderTechnicalReview()
    },
    // Conditionally show Move to AI tab whenever the calculator has
    // Move-to-AI ARR (P447717852). Uses the backend pathway_arrs
    // when present with a client-side calculatorDetails fallback so
    // older FRs that ran before pathway_arrs propagation still
    // render if their calculator services warrant it. A GPU >= 50%
    // warning also shows the tab and holds its icon at warning unless
    // ai_modernization failed (the not_applicable verdict is 'pass').
    // The inference-only indicator moves the icon too: error to fail,
    // warning to needs attention.
    // An archetype-only tab carries no icon: the recommendation is advisory,
    // not a validation result.
    ...(showMoveToAiTab ? [{
      id: 'move-to-ai',
      label: `${showExistingMoveToAi ? getTabIcon(
        aiModResult.result === 'fail' || inferenceOnlyType === 'error'
          ? 'fail'
          : gpuWarnings.length > 0 || inferenceOnlyType === 'warning'
            ? 'needs_attention'
            : aiModResult.result
      ) : ''}Move to AI`,
      content: renderMoveToAI()
    }] : []),
    {
      id: 'history',
      label: 'History',
      content: renderHistory()
    },
    // Show the Claims tab whenever the FR is a cash-claim FR — OR always for
    // POC. POC is intrinsically a cash-claim program (every POC FR is a cash
    // claim), and the entire POC detail view lives in renderPocClaims(), so it
    // must not hinge on isClaimFR's heuristics (FR-ID '-CASH-', fundingType,
    // claimPlans). If a POC row ever lacked those signals, the whole POC claims
    // view would silently vanish, leaving only approval-summary + history
    // (AutoSDE CR-295319668 rev 7). Gate on the program directly, like MDF does.
    // CST parse state is informational and rendered inside the tab — not a
    // precondition for the tab itself.
    ...((effectiveProgram === 'POC' || isClaimFR(request)) ? [{
      id: 'claims',
      // POC persists no MAP-shaped claims_recommendation; drive its tab icon
      // off the agent's overall outcome. Use approval_summary.recommendation
      // (recommend_approval/recommend_rejection/needs_review — the vocabulary
      // getTabIcon recognizes), NOT .decision, which POC stores as the internal
      // approve/reject/flag_for_manual_review code getTabIcon can't map (batpur@).
      label: effectiveProgram === 'POC'
        ? `${getTabIcon(pocTabIconStatus(request.approval_summary))}Claims`
        // When CST is not_found, claims_recommendation is empty so decision
        // is undefined — getTabIcon would default to ⚠️ and the tab would
        // look like a soft warning. Force ❌ to match the red Alert
        // inside the Claims tab.
        : `${getTabIcon(
            validationData.attached_files?.validations?.customer_sign_off?.status === 'not_found'
              ? 'fail'
              : validationData.attached_files?.validations?.customer_sign_off?.claims_recommendation?.decision
          )}Claims`,
      content: effectiveProgram === 'POC' ? renderPocClaims() : renderClaims()
    }] : []),
    // Conditionally show Duplicates tab only when duplicates exist
    ...(() => {
      const duplicateData = request.validation_data?.detailed_validations?.duplicate_funding_requests || {};
      const duplicates = duplicateData.duplicates || [];
      const count = duplicates.length;
      
      return count > 0 ? [{
        id: 'duplicates',
        label: `⚠️ Duplicates (${count})`,
        content: renderDuplicatesTab()
      }] : [];
    })()
  ];

  const PROGRAM_TAB_ALLOWLIST = {
    WMP: ['approval-summary', 'history'],
    IW: ['approval-summary', 'history'],
    // ISFR persists the shared common-core envelope (curate_common_apfp_for_ui:
    // ids, dates, account id, opportunity id + stage, requested credit), not
    // MAP's. The data tabs' renderers read MAP-shaped fields ISFR never
    // writes, so without this entry an ISFR row falls through to the full tab
    // set and shows blanks and misleading defaults (an absent modernization ARR
    // reads as "No" on Claims). ISFR's own Request Details tab reads that
    // envelope directly, and its other tabs (the category tabs' ids come from
    // the record's rollup) read its review fields; ISFR's tab ids are their
    // own, so MMP/VMP's 'request-details' renderer is never reached by an ISFR
    // row. Any other data tab is added only once a renderer reads the fields
    // ISFR actually persists.
    ISFR: [
      'approval-summary',
      ...isfrTabList.map((tab) => tab.id),
      'history',
    ],
    // Opportunity Details and Project Details now read the curated
    // fundRequest.opportunity.opportunityDetail.* / fundRequest.project.* /
    // fundRequest.approval.* fields that curate_rubicon_apfp_for_ui()
    // projects (ChronosAgentCoreAgents data_parser.py) via the dedicated
    // renderRubiconOpportunityDetails()/renderRubiconProjectDetails()
    // functions above — not MAP's render functions, which read a
    // different (incompatible) field shape and were the reason this
    // allowlist originally excluded both tabs (batpur review comment on
    // CR-289789467 rev 1). Claims stays excluded: Rubicon has no
    // equivalent to MAP's attached_files/customer_sign_off/
    // claims_recommendation validation, so there is no non-misleading
    // data to render there yet.
    RUBICON: ['approval-summary', 'history', 'opportunity-details', 'project-details'],
    // MDF renders its own single "Claims" tab (renderMdfClaims reads MDF's
    // validation_results_json per-check shape: cost-share, entity linkage,
    // invoices attached, line-item eligibility) instead of MAP's Migration/
    // Pricing/MPE/Technical tabs, which read a MAP envelope MDF doesn't populate
    // and would show blank/misleading fields. Uses id 'mdf-claims' (not the MAP
    // 'claims' id) so the MAP claims renderer stays filtered out for MDF rows.
    MDF: ['approval-summary', 'mdf-claims', 'history'],
    // POC shows the two program-agnostic tabs plus Claims, which renders via
    // the POC-specific renderPocClaims() (reads validation_results_json, not
    // MAP's detailed_validations). Opportunity/Project/Technical/etc. are
    // excluded — their renderers read MAP-shaped fields POC doesn't persist and
    // would show misleading blanks/defaults.
    POC: ['approval-summary', 'claims', 'history'],
    // PIFCC persists its decision summary and ordered per-check results, but no
    // MAP-shaped detail domains or agent narrative. Keep the first activation
    // surface to the shared summary and history tabs; program-specific claim
    // rendering can be added only from fields PIFCC actually persists.
    PIFCC: ['approval-summary', 'history'],
    // MMP/VMP tabs. "Request Details" replaces "Project Details" because
    // 56/89 of MMP/VMP FRs are assessments (MVA), not projects, and the tab
    // shows funding, workload, calculator link, and timeline from the APFP
    // JSON — fields a reviewer needs to verify the agent's findings.
    // Opportunity Details still uses the Rubicon renderer (same curated shape).
    //
    // This entry is required, not optional: without it the lookup below falls
    // through to the unfiltered `tabs`, i.e. MMP/VMP would inherit MAP's
    // whole tab set.
    MMP_VMP: ['approval-summary', 'opportunity-details', 'request-details', 'history'],
  };
  const programAllowlist = PROGRAM_TAB_ALLOWLIST[effectiveProgram];
  const visibleTabs = programAllowlist
    ? tabs.filter(tab => programAllowlist.includes(tab.id))
    : tabs;
  const safeActiveTab = visibleTabs.some(t => t.id === activeTab)
    ? activeTab
    : visibleTabs[0]?.id;

  return (
    <SpaceBetween size="s">
      <Flashbar items={notifications} />
      
      {/* Duplicates Warning Banner */}
      {(() => {
        const duplicateData = request.validation_data?.detailed_validations?.duplicate_funding_requests || {};
        const duplicates = duplicateData.duplicates || [];
        if (duplicates.length > 0 && !dismissedDuplicatesAlert) {
          return (
            <Alert
              type="warning"
              dismissible
              onDismiss={() => setDismissedDuplicatesAlert(true)}
              header="Possible Duplicates Detected"
            >
              We identified possible duplicates with this fund request. Refer to the <Link onFollow={() => setActiveTab('duplicates')}>Fund Request Duplicates Table</Link> for additional details.
            </Alert>
          );
        }
        return null;
      })()}
      
      {/* Validation Summary Info Modal */}
      <Modal
        visible={showComplianceInfoModal}
        onDismiss={() => setShowComplianceInfoModal(false)}
        header="Validation Summary Explanation"
        size="large"
        footer={
          <Box float="right">
            <Button variant="primary" onClick={() => setShowComplianceInfoModal(false)}>
              Close
            </Button>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <Box variant="p">
            <Box variant="strong">The validation summary shows how many SOP checks passed, failed, or need attention for a {effectiveProgram} funding request.</Box>
          </Box>

          <Box variant="h3">Validations Performed</Box>
          <Box variant="p" color="text-body-secondary">
            {effectiveProgram === 'WMP'
              ? 'The system performs 5 WMP validations on every fund request.'
              : effectiveProgram === 'IW'
                ? 'The system performs up to 9 IW validations. Some run only when applicable (e.g. blocked-partner check, anomaly flags).'
              : effectiveProgram === 'RUBICON'
                ? 'The system performs 7 Rubicon validations covering partner eligibility, opportunity/ARR, fund-request date, duplication, funding letter, direct matches, and key fields.'
              : effectiveProgram === 'MMP_VMP'
                ? 'The system performs 32 MMP/VMP rule checks and rolls them up into the 8 SOP domains listed above. The SOP declares two further domains, tagging and duplicates, whose evidence lives in other systems (CUR, Payee Central) — the agent reports on them but does not judge them, so they are not listed.'
                : 'The system performs up to 10 validations. Some validations are optional and only run when applicable to the fund request.'}
          </Box>
          <SpaceBetween size="s">
            {(() => {
              const keyFindings = request.approval_summary?.key_findings || [];

              // Helper function to find matching key finding for a validation
              const findKeyFinding = (keywords) => {
                return keyFindings.find(finding =>
                  keywords.some(keyword => finding.toLowerCase().includes(keyword.toLowerCase()))
                );
              };

              // Per-program validation list. WMP and IW have different SOPs
              // than MAP — the i-icon modal has to reflect the program that
              // actually ran, otherwise approvers see MAP's tools next to
              // findings that came from a different agent. WMP also branches by
              // sub-program: Bulk PIF (WMP_PIF) runs a different check chain
              // than Misc, so pass wmp_sub_program to pick the right list.
              const validations = getValidationsForSummaryCard(
                resolveProgram(request),
                request.wmp_sub_program,
              );

              // MMP/VMP persists approval_summary.validation_groups, keyed on
              // the same SOP domains as `validation.key`, so its per-domain
              // result is read rather than guessed by keyword. A domain absent
              // from the rollup is stated as not reported instead of shown
              // bare, which would read as a silent pass.
              //
              // Gated on the program: MAP writes a validation_groups of its own
              // with a different shape (it has a primary_status, ours has flat
              // peers) under different keys, so sharing this branch would show
              // MAP reviewers an empty result line.
              const groups = request.approval_summary?.validation_groups;
              if (resolveProgram(request) === 'MMP_VMP' && groups && Object.keys(groups).length > 0) {
                return validations.map((validation, index) => {
                  const described = describeValidationGroup(groups[validation.key]);
                  return (
                    <Box key={index}>
                      {described ? (
                        <StatusIndicator type={described.status}>{validation.label}</StatusIndicator>
                      ) : (
                        <StatusIndicator type="pending">{validation.label}</StatusIndicator>
                      )}
                      <Box variant="small" color="text-body-secondary" padding={{ left: 'l', top: 'xxs' }}>
                        {described
                          ? `${described.resultLabel} — ${described.tally.map(t => `${t.count} ${t.label.toLowerCase()}`).join(', ')}`
                          : 'Not reported by this run.'}
                      </Box>
                    </Box>
                  );
                });
              }

              return validations.map((validation, index) => {
                const keyFinding = findKeyFinding(validation.keywords);
                return (
                  <Box key={index}>
                    <Box>{validation.label}</Box>
                    {keyFinding && (
                      <Box variant="small" color="text-body-secondary" padding={{ left: 'l', top: 'xxs' }}>
                        {keyFinding}
                      </Box>
                    )}
                  </Box>
                );
              });
            })()}
          </SpaceBetween>
          
          <Alert type="info" header="Recommendation">
            <SpaceBetween size="xs">
              <Box>
                <Box variant="strong">Decision:</Box> {request.approval_summary?.decision || '-'}
              </Box>
              <Box>
                <Box variant="strong">Recommendation:</Box> {request.approval_summary?.recommendation || '-'}
              </Box>
              <Box>
                <Box variant="strong">Validation Summary:</Box> {request.approval_summary?.validation_summary || '-'}
              </Box>
            </SpaceBetween>
          </Alert>

          {request.approval_summary?.notes_for_approvers && (
            <Alert type="warning" header="Note for Approvers">
              {request.approval_summary.notes_for_approvers}
            </Alert>
          )}

          <Box variant="h3">How It Works</Box>
          {/* MMP/VMP separates "we judged this and there is nothing to flag"
              from "we could not judge this", because only the second one is a
              reason to send a request to a human. Showing it MAP's three-result
              legend would collapse that distinction and imply a warning where
              the agent stated a conclusion. */}
          {effectiveProgram === 'MMP_VMP' ? (
            <>
              <Box variant="p">
                Each rule reports one of these results:
              </Box>
              <SpaceBetween size="s">
                <Box>
                  <Box variant="strong">✅ Pass</Box> — Rule fully met
                </Box>
                <Box>
                  <Box variant="strong">❌ Fail</Box> — Rule not met
                </Box>
                <Box>
                  <Box variant="strong">⚠️ Needs Attention / Out of Scope / Not Checked</Box> — Partially met, outside what this programme funds, or the evidence could not be read; human review recommended
                </Box>
                <Box>
                  <Box variant="strong">ℹ️ Nothing To Judge</Box> — Judged, and there was nothing to flag
                </Box>
                <Box>
                  <Box variant="strong">➖ Does Not Apply</Box> — The rule does not apply to this request
                </Box>
              </SpaceBetween>
            </>
          ) : (
            <>
              <Box variant="p">
                Each validation is categorized as one of three results:
              </Box>
              <SpaceBetween size="s">
                <Box>
                  <Box variant="strong">✅ Pass</Box> — Validation fully meets SOP requirements
                </Box>
                <Box>
                  <Box variant="strong">⚠️ Needs Attention</Box> — Partially meets requirements, human review recommended
                </Box>
                <Box>
                  <Box variant="strong">❌ Fail</Box> — Does not meet SOP requirements
                </Box>
              </SpaceBetween>
            </>
          )}

          <Box variant="h3">Decision Outcomes</Box>
          <SpaceBetween size="s">
            <Box>
              <Badge color="green">RECOMMEND APPROVAL</Badge>
              {effectiveProgram === 'MMP_VMP'
                ? ' — Every rule passed and every rule had the evidence it needed. A human still approves; the agent only narrows and evidences'
                : ' — All validations pass, no critical findings'}
            </Box>
            <Box>
              <Badge color="red">RECOMMEND REJECTION</Badge> — Critical findings detected (see below)
            </Box>
            <Box>
              <Badge color="yellow">NEEDS REVIEW</Badge>
              {effectiveProgram === 'MMP_VMP'
                ? ' — Either a rule needs attention, or the run was otherwise clean but a rule was left unchecked. Human review required.'
                : ' — No critical findings, but some validations failed or need attention. Human review required.'}
            </Box>
          </SpaceBetween>
          
        </SpaceBetween>
      </Modal>

      {/* Validation Report Modal */}
      <Modal
        visible={showValidationReportModal}
        onDismiss={() => setShowValidationReportModal(false)}
        header="Validation Report"
        size="large"
        footer={
          <Box float="right">
            <Button variant="primary" onClick={() => setShowValidationReportModal(false)}>
              Close
            </Button>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <Alert type="info">
            This is the narrative generated by Chronos as it validated this fund request. It reflects the agent's reasoning and findings at the time of processing.
          </Alert>
          
          {(() => {
            // Agent narrative: top-level field (new records) or fallback to nested path (existing records)
            let agentNarrative = request.agent_narrative
              || request.validation_data?.detailed_validations?.final_decision?.validation_report?._final_summary
              || '';
            
            // Handle raw Strands response objects: {'role':'assistant','content':[{'text':'...'}]}
            if (agentNarrative && typeof agentNarrative === 'object') {
              const c = agentNarrative.content || agentNarrative;
              agentNarrative = Array.isArray(c) && c[0]?.text ? c[0].text : JSON.stringify(c);
            }
            // Handle stringified Python dicts (existing records saved before fix)
            // Can't use JSON.parse — content contains apostrophes that break quote replacement.
            // Extract text by slicing off the known wrapper structure.
            if (typeof agentNarrative === 'string' && agentNarrative.startsWith("{'role'")) {
              const marker = "'text': '";
              const startIdx = agentNarrative.indexOf(marker);
              if (startIdx !== -1) {
                // Text starts after marker, ends before '}]}' (or trailing variants)
                let extracted = agentNarrative.slice(startIdx + marker.length);
                // Remove trailing wrapper: '}]}" or '}]}' or similar
                extracted = extracted.replace(/'\}[\]\}]*\s*$/, '');
                agentNarrative = extracted.replace(/\\n/g, '\n').replace(/\\'/g, "'");
              }
            }
            
            if (!agentNarrative) {
              // WMP narrative persistence shipped after some records were
              // already validated. Tell the reviewer what to do instead of
              // a dead-end "no reports available".
              if (effectiveProgram === 'WMP') {
                return (
                  <Alert type="info">
                    Validation report not available — this WMP fund request
                    was processed before narrative persistence was enabled.
                    Re-run validation to populate.
                  </Alert>
                );
              }
              return (
                <Box textAlign="center" padding="l">
                  <Box variant="p" color="text-body-secondary">
                    No validation reports available for this fund request.
                  </Box>
                </Box>
              );
            }
            
            return (
              <>
                {/* Agent narrative — the full markdown summary */}
                {agentNarrative && (
                  <Container header={<Header variant="h3">Agent Summary</Header>}>
                    <MarkdownRenderer content={agentNarrative} />
                  </Container>
                )}
                

              </>
            );
          })()}
        </SpaceBetween>
      </Modal>

      {/* Header Section */}
      <Container
        header={
          <Header
            variant="h1"
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button onClick={() => history.push('/dashboard')}>Back to List</Button>
                <Button 
                  variant="primary" 
                  onClick={handleValidate}
                  loading={validating}
                  disabled={validating}
                >
                  {validating ? 'Validating...' : 'Validate'}
                </Button>
              </SpaceBetween>
            }
          >
            {request.apfp_fundrequestid || request.id}
          </Header>
        }
      >
        <SpaceBetween size="m">
          {/* Processed Date - Prominent Display */}
          {request.processed_date && (
            <Box textAlign="right">
              <Box variant="small" color="text-body-secondary">
                Last Processed: {new Date(request.processed_date).toLocaleString()}
              </Box>
            </Box>
          )}
          
          <div>
            <Badge color={STATUS_COLORS[request.status] || STATUS_COLORS[request.status?.toUpperCase()?.replace(/ /g, '_')] || 'grey'}>
              {request.status?.toUpperCase()?.replace(/ /g, '_') || request.status}
            </Badge>
          </div>
          {/* Blind review reveal: the decision recorded before the result was shown. */}
          {request.blind_review?.required === false && request.blind_review.decision && (
            <Alert type="info">
              {`${request.blind_review.yours ? 'Your blind decision' : 'Blind decision'}: ${blindDecisionLabel(request.blind_review.decision)}`}
            </Alert>
          )}
          {/* Header grid is program-aware. MAP reads its rich validation_data
              shape (complete_api_data.fundRequest.*). WMP, IW, RUBICON, MDF, and
              POC persist a flat envelope (program, approval_summary.*) and do not
              write MAP's shape, so MAP-specific fields (ARR, Migration Phase,
              MAP program tier "MAP LITE") don't apply and would otherwise
              render as "MAP LITE" / "Mobilize" / "$0". MDF is a marketing-fund
              cash/credit claim — no migration phase, ARR, or MAP tier — so it
              uses this slim header (Total Funds shows its claim amount below).
              POC is a cash claim with the same flat envelope: it was falling
              through to the MAP header and showing MAP's Migration Phase /
              Program Type / ARR, which do not apply to a POC claim. MMP/VMP was
              falling through the same way and every one of those fields was
              wrong for it: Migration Phase showed the 'Mobilize' default rather
              than the phase PH-001 resolved into approval_summary.migration_phase,
              Program Type showed 'MAP LITE' off an ARR the MAP header reads at a
              path a PIF payload has no field for, Total Funds showed '-' for the
              same reason, and Project Description showed the invented default
              'Migration and Modernization Project'. ISFR is a credit request
              with the same flat envelope. On the MAP header it would show ARR
              '$0', Migration Phase 'Mobilize', Program Type 'MAP LITE', Total
              Funds '-' (its amount is approval_summary.total_fund_requested,
              already formatted), and the invented project description. */}
          {effectiveProgram === 'WMP' || effectiveProgram === 'IW' || effectiveProgram === 'RUBICON' || effectiveProgram === 'MDF' || effectiveProgram === 'POC' || effectiveProgram === 'MMP_VMP' || effectiveProgram === 'PIFCC' || effectiveProgram === 'ISFR' ? (
            <>
              <ColumnLayout columns={3} variant="text-grid">
                <div>
                  <Box variant="awsui-key-label">Request ID</Box>
                  <Link
                    external
                    href={getFundingRequestUrl(request.apfp_fundrequestid || request.id)}
                  >
                    {request.apfp_fundrequestid || request.id}
                  </Link>
                  <Box variant="awsui-key-label" padding={{ top: 's' }}>Program</Box>
                  <Badge color="blue">{effectiveProgram}</Badge>
                  {/* Activity/Project Name — Rubicon only. Sourced from APFP
                      details.activityName via curate_rubicon_apfp_for_ui;
                      shown verbatim, no manipulation. Not gated behind WMP/IW
                      since neither persists this field into complete_api_data
                      today. */}
                  {effectiveProgram === 'RUBICON' && request.validation_data?.complete_api_data?.fundRequest?.activityName && (
                    <>
                      <Box variant="awsui-key-label" padding={{ top: 's' }}>Activity/Project Name</Box>
                      <div>{request.validation_data.complete_api_data.fundRequest.activityName}</div>
                    </>
                  )}
                  {effectiveProgram === 'WMP' && request.wmp_sub_program && (
                    <>
                      <Box variant="awsui-key-label" padding={{ top: 's' }}>Sub-program</Box>
                      <Badge color="blue">{request.wmp_sub_program.replace(/^WMP_/, '')}</Badge>
                    </>
                  )}
                  {/* Benefit Application ID — only persisted for WMP records
                      validated after ChronosAgentCoreAgents CR-279120285.
                      Older rows don't have it; the row stays hidden then. */}
                  {request.validation_data?.complete_api_data?.fundRequest?.benefitApplicationId && (
                    <>
                      <Box variant="awsui-key-label" padding={{ top: 's' }}>Benefit Application ID</Box>
                      <div>{request.validation_data.complete_api_data.fundRequest.benefitApplicationId}</div>
                    </>
                  )}
                </div>
                <div>
                  <Box variant="awsui-key-label">Partner</Box>
                  <div>{request.approval_summary?.partner || request.partner || '-'}</div>
                  {/* Rubicon: SPMS ID before Customer (swapped per request).
                      WMP/IW keep the original Customer-then-SPMS-ID order
                      unchanged below, since this row order was not part of
                      the request and WMP also relies on this same field
                      (see "Hidden for older WMP rows" note there). */}
                  {effectiveProgram === 'RUBICON' ? (
                    <>
                      {request.validation_data?.complete_api_data?.fundRequest?.partner?.spmsId && (
                        <>
                          <Box variant="awsui-key-label" padding={{ top: 's' }}>SPMS ID</Box>
                          <div>{request.validation_data.complete_api_data.fundRequest.partner.spmsId}</div>
                        </>
                      )}
                      <Box variant="awsui-key-label" padding={{ top: 's' }}>Customer</Box>
                      <div>{request.approval_summary?.customer || request.customer || '-'}</div>
                    </>
                  ) : (
                    <>
                      {/* Customer is not applicable to MDF — it's a
                          partner-cost reimbursement program with no end-customer
                          on the claim, so the field only ever rendered "Unknown
                          Customer". Suppressed for MDF; WMP/IW/POC keep it. */}
                      {effectiveProgram !== 'MDF' && (
                        <>
                          <Box variant="awsui-key-label" padding={{ top: 's' }}>Customer</Box>
                          <div>{request.approval_summary?.customer || request.customer || '-'}</div>
                        </>
                      )}
                      {/* SPMS ID — populated by curate_api_data_for_ui in
                          ChronosAgentCoreAgents from apnPartner.partnerSpmsId.
                          Hidden for older WMP rows or empty payloads. */}
                      {request.validation_data?.complete_api_data?.fundRequest?.partner?.spmsId && (
                        <>
                          <Box variant="awsui-key-label" padding={{ top: 's' }}>SPMS ID</Box>
                          <div>{request.validation_data.complete_api_data.fundRequest.partner.spmsId}</div>
                        </>
                      )}
                    </>
                  )}
                </div>
                <div>
                  <Box variant="awsui-key-label">
                    {effectiveProgram === 'MDF' || effectiveProgram === 'PIFCC' ? 'Claim Amount' : 'Total Funds Requested from AWS'}
                  </Box>
                  <Box fontSize="heading-s" fontWeight="bold">
                    {/* Rubicon: total_fund_requested is the NATIVE request
                        currency amount (make_decision.py persists
                        cash_funding_amount, not the USD-converted figure),
                        shown with its own currency code -- e.g.
                        "JPY 20,535,686" or "USD 20,000" -- exactly as
                        submitted on the original FR, no USD conversion.
                        MDF has no total_fund_requested; it persists
                        approval_summary.claim_amount + claim_currency, which is
                        the meaningful MDF figure (the amount being claimed).
                        MMP/VMP is the same case as Rubicon and shares its
                        branch: its total_fund_requested is the NATIVE amount off
                        cashRequest.cashFundingAmount with no USD conversion, and
                        its rows carry cash_currency for this reason -- the
                        fixtures include JPY requests, and this figure printed
                        bare reads as dollars.
                        WMP/IW keep the existing pre-formatted display. */}
                    {effectiveProgram === 'MDF' || effectiveProgram === 'PIFCC'
                      ? (request.approval_summary?.claim_amount != null
                        ? `${request.approval_summary?.claim_currency || 'USD'} ${Number(request.approval_summary.claim_amount).toLocaleString('en-US')}`
                        : '-')
                      : (effectiveProgram === 'RUBICON' || effectiveProgram === 'MMP_VMP')
                      ? (request.approval_summary?.total_fund_requested != null
                        ? `${request.approval_summary?.cash_currency || 'USD'} ${Number(request.approval_summary.total_fund_requested).toLocaleString('en-US')}`
                        : '-')
                      : (request.approval_summary?.total_fund_requested || '-')}
                  </Box>
                  {/* On a multi-claim MDF FR the Claim Amount above is the SUM of
                      all submitted claims. Flag that + point to the per-claim
                      section so the reviewer doesn't read it as one claim. */}
                  {effectiveProgram === 'MDF' && mdfPerClaimBreakdown.length > 1 && (
                    <Box variant="small" color="text-body-secondary">
                      Sum of {mdfPerClaimBreakdown.length} submitted claims — see
                      Per-Claim Breakdown on the Claims tab.
                    </Box>
                  )}
                  {effectiveProgram === 'WMP' && request.tranche_number != null && (
                    <>
                      <Box variant="awsui-key-label" padding={{ top: 's' }}>Tranche</Box>
                      <div>#{request.tranche_number}</div>
                    </>
                  )}
                  {/* MMP/VMP's two resolved fields, which the MAP header above
                      was showing wrong rather than not showing: the phase is
                      whatever PH-001 read out of the Activity Name (the MAP
                      header defaulted it to 'Mobilize'), and the sub-programme
                      is MMP or VMP as the rules resolved it (the MAP header
                      printed 'MAP LITE'). Both are what every funding ceiling in
                      this program keys off, so a reviewer disagreeing with a cap
                      needs them on the same screen as the amount. Rendered only
                      when the agent resolved them -- a request whose phase the
                      rules could not settle shows no phase, which is the finding
                      talking, not a blank. */}
                  {effectiveProgram === 'MMP_VMP' && request.approval_summary?.migration_phase && (
                    <>
                      <Box variant="awsui-key-label" padding={{ top: 's' }}>Migration Phase</Box>
                      <Badge color="blue">{request.approval_summary.migration_phase}</Badge>
                    </>
                  )}
                  {effectiveProgram === 'MMP_VMP' && request.approval_summary?.program && (
                    <>
                      <Box variant="awsui-key-label" padding={{ top: 's' }}>Sub-program</Box>
                      <Badge color="blue">{request.approval_summary.program}</Badge>
                    </>
                  )}
                </div>
              </ColumnLayout>

              {/* Project description — long-form text from APFP, surfaced
                  only when persisted. Falls through to nothing when the
                  underlying complete_api_data subtree is absent. */}
              {request.validation_data?.complete_api_data?.fundRequest?.project?.description && (
                <ColumnLayout columns={1} variant="text-grid">
                  <div>
                    <Box variant="awsui-key-label">Project Description</Box>
                    <div>{request.validation_data.complete_api_data.fundRequest.project.description}</div>
                  </div>
                </ColumnLayout>
              )}
            </>
          ) : (
            <>
              <ColumnLayout columns={3} variant="text-grid">
                <div>
                  <Box variant="awsui-key-label">Request ID</Box>
                  <Link
                    external
                    href={getFundingRequestUrl(request.apfp_fundrequestid || request.id)}
                  >
                    {request.apfp_fundrequestid || request.id}
                  </Link>
                  <Box variant="awsui-key-label" padding={{ top: 's' }}>Benefit Application ID</Box>
                  <div>{request.validation_data?.complete_api_data?.fundRequest?.benefitApplicationId || '-'}</div>
                  <Box variant="awsui-key-label" padding={{ top: 's' }}>Annual Run Rate (ARR)</Box>
                  <Box fontSize="heading-s" fontWeight="bold" color="text-status-success">
                    {(() => {
                      const fundRequest = request.validation_data?.complete_api_data?.fundRequest || {};
                      const pricingData = request.validation_data?.detailed_validations?.pricing_calculator || {};

                      const arr = parseAmount(
                        pricingData.annual_recurring_revenue ||
                        pricingData.calculator_total_arr ||
                        fundRequest.arrUsdAmount ||
                        fundRequest.eligibleArrUsdAmount ||
                        0
                      );

                      return formatCurrency(arr);
                    })()}
                  </Box>
                </div>
                <div>
                  <Box variant="awsui-key-label">Partner</Box>
                  <div>{request.validation_data?.complete_api_data?.fundRequest?.partner?.name || request.partner || '-'}</div>
                  <Box variant="awsui-key-label" padding={{ top: 's' }}>SPMS ID</Box>
                  <div>{(() => {
                    const fundRequest = request.validation_data?.complete_api_data?.fundRequest || {};
                    const partner = fundRequest.partner || {};
                    const owner = fundRequest.owner || {};
                    const validationData = request.validation_data?.detailed_validations?.partner_eligibility || {};
                    return validationData.spms_id || partner.spmsId || owner.partnerSpmsId || 'Not Available';
                  })()}</div>
                  <Box variant="awsui-key-label" padding={{ top: 's' }}>
                    {(() => {
                      const fundRequest = request.validation_data?.complete_api_data?.fundRequest || {};
                      const currency = fundRequest.fundingDetails?.totalFundingRequestedFromAwsLocalCurrency || 'USD';
                      return `Total Funds Requested from AWS`;
                    })()}
                  </Box>
                  <Box fontSize="heading-s" fontWeight="bold">
                    {(() => {
                      const fundRequest = request.validation_data?.complete_api_data?.fundRequest || {};
                      const amountStr = fundRequest.fundingDetails?.totalFundingRequestedFromAwsLocalAmount || '-';

                      if (amountStr === '-') return '-';

                      // Parse amount and currency from string like "9122300000.00 IDR"
                      const parts = amountStr.split(' ');
                      const amount = parseFloat(parts[0]);
                      const currency = parts[1] || 'USD';

                      // Format with commas
                      const formatted = new Intl.NumberFormat('en-US', {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2
                      }).format(amount);

                      return `${currency} ${formatted}`;
                    })()}
                  </Box>
                </div>
                <div>
                  <Box variant="awsui-key-label">Customer</Box>
                  <div>{request.approval_summary?.customer || request.customer || '-'}</div>
                  <Box variant="awsui-key-label" padding={{ top: 's' }}>Migration Phase</Box>
                  <Badge color="blue">
                    {request.validation_data?.complete_api_data?.fundRequest?.migrationPhase ||
                     request.validation_data?.detailed_validations?.technical_requirements?.migration_phase ||
                     'Mobilize'}
                  </Badge>
                  <Box variant="awsui-key-label" padding={{ top: 's' }}>Program Type</Box>
                  <Badge color="blue">
                    {(() => {
                      // Determine MAP program based on ARR amount
                      const fundRequest = request.validation_data?.complete_api_data?.fundRequest || {};
                      const arr = parseAmount(
                        fundRequest.arrUsdAmount ||
                        fundRequest.eligibleArrUsdAmount ||
                        0
                      );
                      return arr > 500000 ? 'MAP LARGE' : 'MAP LITE';
                    })()}
                  </Badge>
                  {request.validation_data?.complete_api_data?.fundRequest?.isPublicSector && (
                    <Box padding={{ top: 'xs' }}>
                      <Badge color="blue">PUBLIC SECTOR</Badge>
                    </Box>
                  )}
                </div>
              </ColumnLayout>

              <ColumnLayout columns={1} variant="text-grid">
                <div>
                  <Box variant="awsui-key-label">Project Description</Box>
                  <div>{request.validation_data?.complete_api_data?.fundRequest?.project?.description || request.description || 'Migration and Modernization Project'}</div>
                </div>
              </ColumnLayout>
            </>
          )}
        </SpaceBetween>
      </Container>

      {/* Reviewer Feedback Panel - only show when validation data exists */}
      {request.validation_data
        && getProgramFeedbackConfig(resolveProgram(request)).feedbackEnabled && (
        <ReviewerFeedbackPanel
          fundRequestId={requestId}
          program={resolveProgram(request)}
          subProgram={request.wmp_sub_program}
          validationDecision={request.approval_status || request.approval_summary?.decision}
          onBlindReviewPending={loadRequestData}
        />
      )}

      {/* Horizontal Tabs */}
      <Tabs
        activeTabId={safeActiveTab}
        onChange={({ detail }) => setActiveTab(detail.activeTabId)}
        tabs={visibleTabs}
      />
    </SpaceBetween>
  );
};

export default FundingRequestDetail;
